import type { 
  Point, 
  Wall, 
  Room, 
  FloorPlanElement, 
  BayWindow, 
  DoorElement, 
  WindowElement, 
  Compass as CompassType, 
  TextElement,
  WallWithTempId
} from '../types';
import { 
  PIXELS_PER_METER, 
  SNAP_THRESHOLD, 
  WALL_THICKNESS, 
  POINT_SNAP_THRESHOLD, 
  SNAP_DISTANCE, 
  RESIZE_HANDLE_HOVER_SIZE, 
  CM_SNAP_SIZE, 
  GRID_SIZE, 
  GRID_COLOR, 
  CANVAS_SIZE, 
  SCALE_FACTOR, 
  MEASUREMENT_FONT 
} from '../constants/floorPlanConstants';
import {
  extractExtrusionMetadata,
  preserveExtrusionMetadata,
  validateExtrusionIntegrity,
  updateWallWithExtrusionMetadata,
  repairExtrusionRelationships
} from './extrusionMetadataPreservation';

// TEMPORARY PERFORMANCE DEBUG - Disable expensive operations  
const console = { ...globalThis.console, log: (..._args: any[]) => {}, warn: (..._args: any[]) => {} };
const DISABLE_CALCULATIONS_FOR_DEBUG = false; // RE-ENABLED: Test massive optimization

// 🚀 PERFORMANCE: Configuration for room calculation optimizations
const ROOM_CALC_CONFIG = {
  MAX_WALLS_FOR_FULL_DETECTION: 50, // Skip complex detection above this threshold
  MAX_PATH_LENGTH: 6, // Further reduced from 8 to prevent exponential growth in complex connections
  MAX_ROOMS_TO_PROCESS: 20, // Significantly reduced from 100 to prevent over-detection
  ENABLE_COMPLEX_FILTERING: false, // Disable phantom merger detection for performance
  ENABLE_POINT_CACHING: true, // Cache point string conversions
  MIN_ROOM_AREA_THRESHOLD: 1.0, // Increased from 0.5 to filter out tiny phantom rooms
};

// 🚀 PERFORMANCE: Point string cache to avoid repeated string operations
const pointStringCache = new Map<string, string>();
const clearPointStringCache = () => pointStringCache.clear();

// 🚀 PERFORMANCE: Optimized point to string conversion with caching
const pointToStringCached = (p: Point): string => {
  // 🔧 COORDINATE FIX: Normalize coordinates to prevent precision issues
  const normalizedX = Math.round(p.x * 100) / 100;
  const normalizedY = Math.round(p.y * 100) / 100;
  const key = `${normalizedX},${normalizedY}`;
  
  if (!pointStringCache.has(key)) {
    pointStringCache.set(key, key);
  }
  return pointStringCache.get(key)!;
};

// 🚀 PERFORMANCE: Cached geometric calculations
const geometryCache = new Map<string, any>();
const clearGeometryCache = () => {
  geometryCache.clear();
  areaCache.clear();
};

// 🚀 PERFORMANCE: Generate cache key for wall arrays
const generateWallsCacheKey = (walls: Wall[]): string => {
  return walls.map(w => `${w.start.x.toFixed(1)},${w.start.y.toFixed(1)}-${w.end.x.toFixed(1)},${w.end.y.toFixed(1)}`).sort().join('|');
};

// --- Geometric Calculations ---

export const calculateWallLength = (start: Point, end: Point): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthInMeters = Math.sqrt(dx * dx + dy * dy) / PIXELS_PER_METER;
  // Round to 2 decimal places during calculation
  return Math.round(lengthInMeters * 100) / 100;
};

// ❌ DEPRECATED: Old bounding box method (INACCURATE for L-shaped rooms)
export const calculateAreaBoundingBox = (walls: Wall[]): number => {
  if (walls.length < 3) return 0;
  
  // ⚠️ WARNING: This uses bounding box (width × height) which is WRONG for non-rectangular rooms
  const bounds = getRoomBounds(walls);
  const widthPixels = bounds.maxX - bounds.minX;
  const heightPixels = bounds.maxY - bounds.minY;
  
  // Convert to meters (rounded to 2 decimal places for consistency)
  const widthMeters = Math.round((widthPixels / PIXELS_PER_METER) * 100) / 100;
  const heightMeters = Math.round((heightPixels / PIXELS_PER_METER) * 100) / 100;
  
  // Calculate area as length × width
  const area = widthMeters * heightMeters;
  
  console.warn(`⚠️ BOUNDING BOX AREA (INACCURATE): ${widthMeters.toFixed(2)}m × ${heightMeters.toFixed(2)}m = ${area.toFixed(2)}m²`);
  
  // Round to 2 decimal places
  return Math.round(area * 100) / 100;
};

// ✅ CORRECT POLYGON-BASED CALCULATION using shoelace formula
export const calculateArea = (walls: Wall[]): number => {
  if (walls.length < 3) return 0;
  
  const wallsForArea = walls;
  
  // console.log(`📐 POLYGON AREA CALCULATION: Total walls: ${walls.length}`);
  
  // Debug: Show which walls are extrusion connectors (commented out to reduce console noise)
  // const extrusionConnectors = walls.filter(w => (w as any).isExtrusionConnector);
  // if (extrusionConnectors.length > 0) {
  //   console.log(`📐 AREA CALCULATION: Including ${extrusionConnectors.length} extrusion connectors as boundary walls`);
  // }
  
  // Extract ordered vertices from walls to form a proper polygon
  const vertices: Point[] = [];
  
  // Start with the first wall
  let currentWall = wallsForArea[0];
  vertices.push(currentWall.start);
  vertices.push(currentWall.end);
  
  // Find the next connected wall and build the polygon in order
  const usedWalls = new Set([currentWall]);
  let currentPoint = currentWall.end;
  
  // console.log(`📐 POLYGON BUILDING: Starting with wall (${currentWall.start.x.toFixed(1)},${currentWall.start.y.toFixed(1)})->(${currentWall.end.x.toFixed(1)},${currentWall.end.y.toFixed(1)})`);
  
  while (usedWalls.size < wallsForArea.length) {
    let foundNext = false;
    let bestMatch: { wall: Wall; distance: number; useStart: boolean } | null = null;
    
    // 🔧 CRITICAL FIX: Increased tolerance to handle floating-point precision and minor endpoint drift
    // This works in conjunction with endpoint snapping in floorPlanGeometry.ts
    // MUST match or exceed CORNER_TOLERANCE (60px) used in AR scanning to prevent disconnections
    const tolerance = 70; // Increased from 15 to 70 to match AR scanning precision
    
    // console.log(`📐 POLYGON BUILDING: Looking for wall connected to (${currentPoint.x.toFixed(1)},${currentPoint.y.toFixed(1)})`);
    
    for (const wall of wallsForArea) {
      if (usedWalls.has(wall)) continue;
      
      // Check distance to wall start and end points
      const distanceToStart = Math.hypot(wall.start.x - currentPoint.x, wall.start.y - currentPoint.y);
      const distanceToEnd = Math.hypot(wall.end.x - currentPoint.x, wall.end.y - currentPoint.y);
      
      // console.log(`📐 POLYGON BUILDING: Checking wall - distToStart: ${distanceToStart.toFixed(2)}, distToEnd: ${distanceToEnd.toFixed(2)}`);
      
      // Find the closest connection within tolerance
      if (distanceToStart < tolerance) {
        if (!bestMatch || distanceToStart < bestMatch.distance) {
          bestMatch = { wall, distance: distanceToStart, useStart: true };
        }
      }
      if (distanceToEnd < tolerance) {
        if (!bestMatch || distanceToEnd < bestMatch.distance) {
          bestMatch = { wall, distance: distanceToEnd, useStart: false };
        }
      }
    }
    
    if (bestMatch) {
      const { wall, distance, useStart } = bestMatch;
      // console.log(`📐 POLYGON BUILDING: Found connected wall at distance ${distance.toFixed(2)}, using ${useStart ? 'start' : 'end'} point`);
      
      if (useStart) {
        // Wall starts where we are, add its end point
        vertices.push(wall.end);
        currentPoint = wall.end;
      } else {
        // Wall ends where we are, add its start point
        vertices.push(wall.start);
        currentPoint = wall.start;
      }
      
      usedWalls.add(wall);
      foundNext = true;
    }
    
    if (!foundNext) {
      // If we can't find a connected wall, break to avoid infinite loop
      console.warn(`📐 AREA CALCULATION: Could not find next connected wall at point (${currentPoint.x.toFixed(1)}, ${currentPoint.y.toFixed(1)}), used ${usedWalls.size}/${wallsForArea.length} walls`);
      console.warn(`📐 REMAINING WALLS:`, Array.from(wallsForArea).filter(w => !usedWalls.has(w)).map(w => 
        `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`
      ));
      break;
    }
  }
  
  // console.log(`📐 POLYGON BUILDING: Built ${vertices.length} vertices from ${usedWalls.size}/${wallsForArea.length} walls`);
  // console.log(`📐 VERTICES:`, vertices.map(v => `(${v.x.toFixed(1)},${v.y.toFixed(1)})`));
  
  // Remove the last vertex if it's the same as the first (closed polygon)
  if (vertices.length > 2) {
    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    const closingDistance = Math.hypot(first.x - last.x, first.y - last.y);
    if (closingDistance < 3) { // Increased tolerance for closing the polygon
      vertices.pop();
      // console.log(`📐 POLYGON BUILDING: Removed duplicate closing vertex (distance: ${closingDistance.toFixed(2)})`);
    }
  }
  
  // 🔧 IMPROVED FALLBACK: If we couldn't build a complete polygon, try to use available vertices
  if (vertices.length < 3) {
    // If we have some vertices but not enough, try to extract unique points from all walls
    console.warn('📐 Area calculation: Could not build complete polygon, attempting vertex extraction from all walls');
    
    const allPoints: Point[] = [];
    const pointSet = new Set<string>();
    
    for (const wall of wallsForArea) {
      const startKey = `${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)}`;
      const endKey = `${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}`;
      
      if (!pointSet.has(startKey)) {
        allPoints.push(wall.start);
        pointSet.add(startKey);
      }
      if (!pointSet.has(endKey)) {
        allPoints.push(wall.end);
        pointSet.add(endKey);
      }
    }
    
    // console.log(` FALLBACK: Extracted ${allPoints.length} unique points from walls`);
    
    if (allPoints.length >= 3) {
      // WARNING: Angle-sorting doesn't work for concave polygons (L-shapes, etc.)
      // Instead, try to build a proper ordered polygon by attempting partial wall tracing
      console.warn(' FALLBACK: Attempting to build ordered polygon from unconnected walls');
      
      // Try to order points by tracing from leftmost point clockwise
      const leftmost = allPoints.reduce((min, p) => p.x < min.x ? p : min, allPoints[0]);
      const orderedPoints: Point[] = [leftmost];
      const remainingPoints = allPoints.filter(p => p !== leftmost);
      
      let current = leftmost;
      while (remainingPoints.length > 0 && orderedPoints.length < allPoints.length) {
        // Find the closest unused point
        let closestDist = Infinity;
        let closestIdx = -1;
        
        for (let i = 0; i < remainingPoints.length; i++) {
          const dist = Math.hypot(remainingPoints[i].x - current.x, remainingPoints[i].y - current.y);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        }
        
        if (closestIdx >= 0) {
          current = remainingPoints[closestIdx];
          orderedPoints.push(current);
          remainingPoints.splice(closestIdx, 1);
        } else {
          break;
        }
      }
      
      // console.log(`📐 FALLBACK: Ordered ${orderedPoints.length} points by proximity`);
      // console.log(`📐 ORDERED VERTICES:`, orderedPoints.map(v => `(${v.x.toFixed(1)},${v.y.toFixed(1)})`));
      
      // Calculate area using ordered vertices
      let area = 0;
      for (let i = 0; i < orderedPoints.length; i++) {
        const j = (i + 1) % orderedPoints.length;
        area += orderedPoints[i].x * orderedPoints[j].y;
        area -= orderedPoints[j].x * orderedPoints[i].y;
      }
      
      const areaInSquareMeters = Math.abs(area / 2) / (PIXELS_PER_METER * PIXELS_PER_METER);
      // console.log(`📐 FALLBACK SHOELACE: ${orderedPoints.length} vertices → ${areaInSquareMeters.toFixed(2)}m²`);
      // console.warn(`⚠️ AREA MAY BE INACCURATE for L-shaped or complex rooms - check wall connections`);
      return Math.round(areaInSquareMeters * 100) / 100;
    }
    
    // Final fallback: bounding box method (but warn that this is inaccurate)
    console.error('📐 ❌ CRITICAL: Using bounding box method - AREA WILL BE WRONG for L-shaped rooms!');
    console.error('📐 ❌ This means walls are not properly connected at corners');
    console.error('📐 ❌ Room has', wallsForArea.length, 'walls but couldn\'t trace polygon');
    
    const bounds = getRoomBounds(wallsForArea);
    const widthPixels = bounds.maxX - bounds.minX;
    const heightPixels = bounds.maxY - bounds.minY;
    
    // Convert to meters and calculate area
    const widthMeters = widthPixels / PIXELS_PER_METER;
    const heightMeters = heightPixels / PIXELS_PER_METER;
    const area = widthMeters * heightMeters;
    
    console.error(`📐 ❌ BOUNDING BOX FALLBACK: ${widthMeters.toFixed(2)}m × ${heightMeters.toFixed(2)}m = ${area.toFixed(2)}m² (INACCURATE FOR NON-RECTANGULAR ROOMS)`);
    return Math.round(area * 100) / 100; // Round to 2 decimal places
  }
  
  // 🔧 For rectangular rooms (4 walls), use wall lengths for consistent area calculation
  // This ensures the area matches the displayed dimensions (which are rounded)
  if (wallsForArea.length === 4) {
    const wallLengths = wallsForArea.map(wall => calculateWallLength(wall.start, wall.end));
    wallLengths.sort((a, b) => b - a);
    
    // Check if approximately rectangular (opposite walls similar)
    const isRectangular = (
      Math.abs(wallLengths[0] - wallLengths[1]) < 0.02 && 
      Math.abs(wallLengths[2] - wallLengths[3]) < 0.02
    );
    
    if (isRectangular) {
      // Use rounded wall lengths to calculate area
      const length = wallLengths[0];
      const width = wallLengths[2];
      const areaFromDimensions = length * width;
      // console.log(`📐 RECTANGULAR: ${length}m × ${width}m = ${areaFromDimensions.toFixed(2)}m²`);
      return Math.round(areaFromDimensions * 100) / 100;
    }
  }
  
  // Apply shoelace formula for non-rectangular rooms
  let area = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }
  
  // Convert from square pixels to square meters and take absolute value
  const areaInSquareMeters = Math.abs(area / 2) / (PIXELS_PER_METER * PIXELS_PER_METER);
  
  // console.log(`📐 SHOELACE CALCULATION SUCCESS: ${vertices.length} vertices → ${areaInSquareMeters.toFixed(2)}m²`);
  
  // Round to 2 decimal places
  return Math.round(areaInSquareMeters * 100) / 100;
};

// 🚀 PERFORMANCE: Memoized area calculation
const areaCache = new Map<string, number>();

const memoizedCalculateArea = (walls: Wall[]): number => {
  const cacheKey = `area_${generateWallsCacheKey(walls)}`;
  if (areaCache.has(cacheKey)) {
    return areaCache.get(cacheKey)!;
  }
  
  const area = calculateArea(walls);
  areaCache.set(cacheKey, area);
  return area;
};

// Use optimized area calculation for better performance
export const calculateAreaFast = (walls: Wall[]): number => {
  return memoizedCalculateArea(walls);
};

/**
 * Build ordered polygon points from walls by tracing connections
 */
const buildOrderedPolygonPoints = (walls: Wall[]): Point[] => {
  if (walls.length === 0) return [];
  if (walls.length === 1) return [walls[0].start, walls[0].end];
  
  const vertices: Point[] = [];
  const wallsForArea = walls;
  
  // Start with the first wall
  let currentWall = wallsForArea[0];
  vertices.push(currentWall.start);
  vertices.push(currentWall.end);
  
  // Find the next connected wall and build the polygon in order
  const usedWalls = new Set([currentWall]);
  let currentPoint = currentWall.end;
  
  const tolerance = 15; // Tolerance for endpoint matching
  
  while (usedWalls.size < wallsForArea.length) {
    let foundNext = false;
    let bestMatch: { wall: Wall; distance: number; useStart: boolean } | null = null;
    
    for (const wall of wallsForArea) {
      if (usedWalls.has(wall)) continue;
      
      // Check distance to wall start and end points
      const distanceToStart = Math.hypot(wall.start.x - currentPoint.x, wall.start.y - currentPoint.y);
      const distanceToEnd = Math.hypot(wall.end.x - currentPoint.x, wall.end.y - currentPoint.y);
      
      // Find the closest connection within tolerance
      if (distanceToStart < tolerance) {
        if (!bestMatch || distanceToStart < bestMatch.distance) {
          bestMatch = { wall, distance: distanceToStart, useStart: true };
        }
      }
      if (distanceToEnd < tolerance) {
        if (!bestMatch || distanceToEnd < bestMatch.distance) {
          bestMatch = { wall, distance: distanceToEnd, useStart: false };
        }
      }
    }
    
    if (bestMatch) {
      const { wall, useStart } = bestMatch;
      
      if (useStart) {
        // Wall starts where we are, add its end point
        vertices.push(wall.end);
        currentPoint = wall.end;
      } else {
        // Wall ends where we are, add its start point
        vertices.push(wall.start);
        currentPoint = wall.start;
      }
      
      usedWalls.add(wall);
      foundNext = true;
    }
    
    if (!foundNext) {
      // If we can't find a connected wall, break to avoid infinite loop
      break;
    }
  }
  
  // Remove the last vertex if it's the same as the first (closed polygon)
  if (vertices.length > 2) {
    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    const closingDistance = Math.hypot(first.x - last.x, first.y - last.y);
    if (closingDistance < 3) {
      vertices.pop();
    }
  }
  
  // Fallback: If we couldn't build a complete polygon, extract unique points
  if (vertices.length < 3) {
    const allPoints: Point[] = [];
    const pointSet = new Set<string>();
    
    for (const wall of wallsForArea) {
      const startKey = `${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)}`;
      const endKey = `${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}`;
      
      if (!pointSet.has(startKey)) {
        allPoints.push(wall.start);
        pointSet.add(startKey);
      }
      if (!pointSet.has(endKey)) {
        allPoints.push(wall.end);
        pointSet.add(endKey);
      }
    }
    
    if (allPoints.length >= 3) {
      // Order points by proximity starting from leftmost point
      const leftmost = allPoints.reduce((min, p) => p.x < min.x ? p : min, allPoints[0]);
      const orderedPoints: Point[] = [leftmost];
      const remainingPoints = allPoints.filter(p => p !== leftmost);
      
      let current = leftmost;
      while (remainingPoints.length > 0 && orderedPoints.length < allPoints.length) {
        let closestDist = Infinity;
        let closestIdx = -1;
        
        for (let i = 0; i < remainingPoints.length; i++) {
          const dist = Math.hypot(remainingPoints[i].x - current.x, remainingPoints[i].y - current.y);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        }
        
        if (closestIdx >= 0) {
          current = remainingPoints[closestIdx];
          orderedPoints.push(current);
          remainingPoints.splice(closestIdx, 1);
        } else {
          break;
        }
      }
      
      return orderedPoints;
    }
    
    return allPoints;
  }
  
  return vertices;
};

export const getRoomCenter = (walls: Wall[]): Point => {
  if (walls.length === 0) return { x: 0, y: 0 };

  // 🚀 PERFORMANCE: Cache room center calculations
  const cacheKey = `center_${generateWallsCacheKey(walls)}`;
  if (geometryCache.has(cacheKey)) {
    return geometryCache.get(cacheKey);
  }

  // Build ordered polygon points from walls
  const points = buildOrderedPolygonPoints(walls);
  if (points.length < 3) {
    // Fallback to simple average for invalid polygons
    let totalX = 0, totalY = 0;
    points.forEach(p => { totalX += p.x; totalY += p.y; });
    const result = { x: totalX / points.length, y: totalY / points.length };
    geometryCache.set(cacheKey, result);
    return result;
  }

  // Calculate polygon centroid using proper formula
  // This gives accurate center for irregular polygons
  let area = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;
    
    const cross = xi * yj - xj * yi;
    area += cross;
    cx += (xi + xj) * cross;
    cy += (yi + yj) * cross;
  }

  area = area / 2;
  
  // Handle degenerate case
  if (Math.abs(area) < 0.001) {
    let totalX = 0, totalY = 0;
    points.forEach(p => { totalX += p.x; totalY += p.y; });
    const result = { x: totalX / points.length, y: totalY / points.length };
    geometryCache.set(cacheKey, result);
    return result;
  }

  const result = {
    x: cx / (6 * area),
    y: cy / (6 * area)
  };
  
  geometryCache.set(cacheKey, result);
  return result;
};

export const getRoomBounds = (walls: Wall[]): { minX: number; maxX: number; minY: number; maxY: number } => {
  if (walls.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

  // 🚀 PERFORMANCE: Cache bounds calculations
  const cacheKey = `bounds_${generateWallsCacheKey(walls)}`;
  if (geometryCache.has(cacheKey)) {
    return geometryCache.get(cacheKey);
  }

  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;

  // 🚀 PERFORMANCE: Single pass through all wall points
  for (const wall of walls) {
    // Start point
    if (wall.start.x < minX) minX = wall.start.x;
    if (wall.start.x > maxX) maxX = wall.start.x;
    if (wall.start.y < minY) minY = wall.start.y;
    if (wall.start.y > maxY) maxY = wall.start.y;
    
    // End point
    if (wall.end.x < minX) minX = wall.end.x;
    if (wall.end.x > maxX) maxX = wall.end.x;
    if (wall.end.y < minY) minY = wall.end.y;
    if (wall.end.y > maxY) maxY = wall.end.y;
  }

  const result = { minX, maxX, minY, maxY };
  geometryCache.set(cacheKey, result);
  return result;
};

export const getElementBounds = (element: FloorPlanElement, exportScale?: number, interactionScale?: number): { minX: number; maxX: number; minY: number; maxY: number } => {
  switch (element.type) {
    case 'counter':
      // Handle counter elements which have start and end points with width
      const counter = element as any;
      const dx = counter.end.x - counter.start.x;
      const dy = counter.end.y - counter.start.y;
      const perpDx = -dy;
      const perpDy = dx;
      const perpLen = Math.hypot(perpDx, perpDy);
      const perpNormX = perpDx / perpLen;
      const perpNormY = perpDy / perpLen;
      
      // Calculate all four corners
      const corners = [
        { x: counter.start.x, y: counter.start.y },
        { x: counter.end.x, y: counter.end.y },
        { x: counter.end.x + perpNormX * counter.width, y: counter.end.y + perpNormY * counter.width },
        { x: counter.start.x + perpNormX * counter.width, y: counter.start.y + perpNormY * counter.width },
      ];
      
      return {
        minX: Math.min(...corners.map(c => c.x)),
        maxX: Math.max(...corners.map(c => c.x)),
        minY: Math.min(...corners.map(c => c.y)),
        maxY: Math.max(...corners.map(c => c.y)),
      };
      
    case 'stairs':
      // Handle stairs elements which have start and end points with width
      const stairs = element as any;
      const stairsDx = stairs.end.x - stairs.start.x;
      const stairsDy = stairs.end.y - stairs.start.y;
      const stairsPerpDx = -stairsDy;
      const stairsPerpDy = stairsDx;
      const stairsPerpLen = Math.hypot(stairsPerpDx, stairsPerpDy);
      const stairsPerpNormX = stairsPerpDx / stairsPerpLen;
      const stairsPerpNormY = stairsPerpDy / stairsPerpLen;
      
      // Calculate all four corners
      const stairsCorners = [
        { x: stairs.start.x, y: stairs.start.y },
        { x: stairs.end.x, y: stairs.end.y },
        { x: stairs.end.x + stairsPerpNormX * stairs.width, y: stairs.end.y + stairsPerpNormY * stairs.width },
        { x: stairs.start.x + stairsPerpNormX * stairs.width, y: stairs.start.y + stairsPerpNormY * stairs.width },
      ];
      
      return {
        minX: Math.min(...stairsCorners.map(c => c.x)),
        maxX: Math.max(...stairsCorners.map(c => c.x)),
        minY: Math.min(...stairsCorners.map(c => c.y)),
        maxY: Math.max(...stairsCorners.map(c => c.y)),
      };
      
    case 'highlight':
      // Handle highlight elements which have startPoint and endPoint properties
      const highlight = element as any; // Cast to access highlight properties
      const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
      const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
      const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
      const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);
      
      return { minX, maxX, minY, maxY };
      
    case 'bayWindow':
    case 'door':
    case 'window':
    case 'compass':
    case 'text':
    case 'radiator':
    case 'boiler':
    case 'light':
    case 'smoke-alarm':
    case 'utility':
      // All these types should have a 'position' property.
      const el = element as any; // Use 'any' carefully, or create a more specific union type
      let width = 20; // Default width
      let height = 20; // Default height
      
      // 🌳 GARDEN: Use actual dimensions if available
      if (el.utilityType === 'garden' && el.width && el.height) {
        width = el.width;
        height = el.height;
      } else if (element.type === 'compass') {
        const size = (element as CompassType).size;
        width = height = size;
      } else if (element.type === 'door') {
        // For a door, make it much more touch-friendly
        if (exportScale) {
          // For export, use minimal bounds
          width = Math.max((element as any).width, 20);
          height = 20;
        } else {
          // For interaction, use much larger bounds that scale with zoom
          const baseWidth = Math.max((element as any).width || 40, 40); // Minimum 40 for doors
          const baseHeight = 30; // Larger height for easier touch
          
          // Scale the touch area based on zoom level - smaller zoom = larger touch area
          const touchScale = interactionScale || 1;
          const minTouchArea = 40; // Minimum 40px touch area in screen coordinates
          const scaledTouchArea = Math.max(minTouchArea / touchScale, baseWidth);
          
          width = scaledTouchArea;
          height = Math.max(baseHeight, minTouchArea / touchScale);
        }
      } else if (element.type === 'window') {
        // For bay windows (canted), account for the depth projection
        const windowEl = element as any;
        if (windowEl.windowInstallationType === 'canted') {
          const bayWidth = windowEl.width || 120;
          const bayDepth = windowEl.depth || 60;
          // Bay window projects outward, so we need to account for both width and depth
          width = Math.max(bayWidth, bayDepth * 2); // Use larger dimension for bounding box
          height = Math.max(bayWidth, bayDepth * 2);
        } else {
          // For a regular window, use default window width
          const baseWidth = Math.max((element as any).width || 60, 20);
          if (exportScale) {
            width = baseWidth;
            height = 20;
          } else {
            // Make windows more touch-friendly too
            const touchScale = interactionScale || 1;
            const minTouchArea = 35; // Minimum touch area
            
            width = Math.max(baseWidth, minTouchArea / touchScale);
            height = Math.max(35, minTouchArea / touchScale);
          }
        }
      } else if (element.type === 'radiator') {
        // For radiators, calculate bounds based on text rendering dimensions
        const radiator = element as any;
        const radiatorType = radiator.radiatorType || 'with-trvs';
        
        let text: string;
        switch (radiatorType) {
          case 'with-trvs': text = 'RADT'; break;
          case 'without-trvs': text = 'RAD'; break;
          case 'panel-heater': text = 'PH'; break;
          case 'fan-heater': text = 'FH'; break;
          case 'electric-storage-heater': text = 'ESH'; break;
          case 'high-heat-retention': text = 'HHR'; break;
          default: text = 'RAD';
        }
        
        // Use the same font size calculation as the drawing code
        const baseFontSize = 14;
        const currentScale = exportScale || 1;
        const scaledFontSize = Math.max(8 / currentScale, Math.min(20 / currentScale, baseFontSize / currentScale));
        
        // Calculate text box dimensions (align with drawSingleRadiator padding)
        const paddingX = Math.max(2 / currentScale, scaledFontSize * 0.4);
        const paddingY = Math.max(1 / currentScale, scaledFontSize * 0.3);
        const textWidth = text.length * (scaledFontSize * 0.6); // Rough approximation
        const boxWidth = textWidth + paddingX * 2;
        const boxHeight = scaledFontSize + paddingY * 2;
        
        if (exportScale) {
          width = Math.max(boxWidth, 30);
          height = Math.max(boxHeight, 20);
        } else {
          // Make radiators more touch-friendly
          const touchScale = interactionScale || 1;
          const minTouchArea = 35;
          
          width = Math.max(boxWidth, minTouchArea / touchScale);
          height = Math.max(boxHeight, minTouchArea / touchScale);
        }
      } else if (element.type === 'boiler') {
        // For boilers, use fixed hit detection bounds
        if (exportScale) {
          width = 80 / exportScale;
          height = 40 / exportScale;
        } else {
          const touchScale = interactionScale || 1;
          const minTouchArea = 40;
          
          width = Math.max(80, minTouchArea / touchScale);
          height = Math.max(40, minTouchArea / touchScale);
        }
      } else if (element.type === 'light') {
        // Light fixtures have specific size based on their rendering
        if (exportScale) {
          // Use the same calculation as in export rendering
          const size = 0.15 * PIXELS_PER_METER; // 15cm diameter, same as main view
          width = height = size;
        } else {
          // Use the same calculation as in the light placement but make it touch-friendly
          const baseSize = Math.max(20, 0.15 * PIXELS_PER_METER); // 15cm diameter, minimum 20px for interaction
          const touchScale = interactionScale || 1;
          const minTouchArea = 30;
          
          const size = Math.max(baseSize, minTouchArea / touchScale);
          width = height = size;
        }
      }
      
      // For other element types, make them more touch-friendly if not exporting
      if (!exportScale && element.type !== 'door' && element.type !== 'window' && element.type !== 'radiator' && element.type !== 'boiler' && element.type !== 'light') {
        const touchScale = interactionScale || 1;
        const minTouchArea = 30;
        
        width = Math.max(width, minTouchArea / touchScale);
        height = Math.max(height, minTouchArea / touchScale);
      }
      
      return {
        minX: el.position.x - width / 2,
        maxX: el.position.x + width / 2,
        minY: el.position.y - height / 2,
        maxY: el.position.y + height / 2,
      };
    default:
      // Fallback for any unhandled element types or if a plain Wall object is passed.
      // This attempts to use 'position' if available, otherwise, assumes it's a Wall-like structure.
      const unknownEl = element as any;
      if (unknownEl.start && unknownEl.end) { // Check for Wall-like structure
          const wall = element as Wall;
          const padding = 5; 
          return {
            minX: Math.min(wall.start.x, wall.end.x) - padding,
            maxX: Math.max(wall.start.x, wall.end.x) + padding,
            minY: Math.min(wall.start.y, wall.end.y) - padding,
            maxY: Math.max(wall.start.y, wall.end.y) + padding,
          };
      } else if (unknownEl.position) {
        const defaultSize = 20;
        const touchScale = interactionScale || 1;
        const minTouchArea = 30;
        
        const size = exportScale ? defaultSize : Math.max(defaultSize, minTouchArea / touchScale);
        
        return {
          minX: unknownEl.position.x - size / 2,
          maxX: unknownEl.position.x + size / 2,
          minY: unknownEl.position.y - size / 2,
          maxY: unknownEl.position.y + size / 2,
        };
      }
      console.warn(`[getElementBounds] Unhandled or unpositionable element type: ${(element as any).type}`);
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
};

// --- Point and Line Interactions ---

export const isNearPoint = (p1: Point | null, p2: Point, threshold = SNAP_THRESHOLD): boolean => {
  if (!p1) return false;
  return Math.hypot(p1.x - p2.x, p1.y - p2.y) < threshold;
};

export const distanceToLine = (point: Point, lineStart: Point, lineEnd: Point): number => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  if (lenSq === 0) return Math.hypot(A, B); // Handle zero-length line

  let param = dot / lenSq;
  param = Math.max(0, Math.min(1, param)); // Clamp parameter to segment

  const xx = lineStart.x + param * C;
  const yy = lineStart.y + param * D;

  return Math.hypot(point.x - xx, point.y - yy);
};

export const pointToLineDistance = (point: Point, lineStart: Point, lineEnd: Point): number => {
    // If start and end are the same point, just return distance to that point
    if (lineStart.x === lineEnd.x && lineStart.y === lineEnd.y) {
        return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
    }

    const lineLength2 = Math.pow(lineEnd.x - lineStart.x, 2) + Math.pow(lineEnd.y - lineStart.y, 2);
    const t = Math.max(0, Math.min(1, (
        (point.x - lineStart.x) * (lineEnd.x - lineStart.x) +
        (point.y - lineStart.y) * (lineEnd.y - lineStart.y)
    ) / lineLength2));

    const closestX = lineStart.x + t * (lineEnd.x - lineStart.x);
    const closestY = lineStart.y + t * (lineEnd.y - lineStart.y);

    return Math.hypot(point.x - closestX, point.y - closestY);
};


export const doLinesIntersect = (line1Start: Point, line1End: Point, line2Start: Point, line2End: Point): boolean => {
  const x1 = line1Start.x, y1 = line1Start.y;
  const x2 = line1End.x, y2 = line1End.y;
  const x3 = line2Start.x, y3 = line2Start.y;
  const x4 = line2End.x, y4 = line2End.y;

  const denominator = ((x2 - x1) * (y4 - y3)) - ((y2 - y1) * (x4 - x3));
  if (denominator === 0) return false; // Lines are parallel

  const t = (((x3 - x1) * (y4 - y3)) - ((y3 - y1) * (x4 - x3))) / denominator;
  const u = -(((x1 - x2) * (y1 - y3)) - ((y1 - y2) * (x1 - x3))) / denominator; // Corrected formula for u

  // Check if intersection point is within both line segments
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
};

export const getLineIntersectionPoint = (line1Start: Point, line1End: Point, line2Start: Point, line2End: Point): Point | null => {
  const x1 = line1Start.x, y1 = line1Start.y;
  const x2 = line1End.x, y2 = line1End.y;
  const x3 = line2Start.x, y3 = line2Start.y;
  const x4 = line2End.x, y4 = line2End.y;

  const denominator = ((x2 - x1) * (y4 - y3)) - ((y2 - y1) * (x4 - x3));
  if (denominator === 0) return null; // Lines are parallel or collinear

  const t = (((x3 - x1) * (y4 - y3)) - ((y3 - y1) * (x4 - x3))) / denominator;
  const u = -(((x1 - x2) * (y1 - y3)) - ((y1 - y2) * (x1 - x3))) / denominator;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    // Intersection point is within both line segments
    const intersectX = x1 + t * (x2 - x1);
    const intersectY = y1 + t * (y2 - y1);
    return { x: intersectX, y: intersectY };
  }
  return null; // Segments do not intersect
};

export const getCollinearNeighbors = (
  targetWall: Wall,
  allWalls: Wall[],
  tolerance: number = 0.1 // Tolerance for floating point comparisons for collinearity and point equality
): { startNeighbor: Wall | null; endNeighbor: Wall | null } => {
  let startNeighbor: Wall | null = null;
  let endNeighbor: Wall | null = null;

  const { start: targetStart, end: targetEnd } = targetWall;

  for (const currentWall of allWalls) {
    // Skip the targetWall itself or if it's the same instance
    if (currentWall === targetWall) continue;
    if (currentWall.start === targetStart && currentWall.end === targetEnd) continue;


    const { start: currentStart, end: currentEnd } = currentWall;

    // Check for shared endpoint and collinearity
    if (pointsAreEqual(targetStart, currentStart, tolerance)) {
      // Potential neighbor at targetWall.start, sharing currentWall.start
      // Check collinearity: targetEnd, targetStart (shared), currentEnd
      if (orientation(targetEnd, targetStart, currentEnd) === 0) {
        startNeighbor = currentWall;
      }
    } else if (pointsAreEqual(targetStart, currentEnd, tolerance)) {
      // Potential neighbor at targetWall.start, sharing currentWall.end
      // Check collinearity: targetEnd, targetStart (shared), currentStart
      if (orientation(targetEnd, targetStart, currentStart) === 0) {
        startNeighbor = currentWall;
      }
    } else if (pointsAreEqual(targetEnd, currentStart, tolerance)) {
      // Potential neighbor at targetWall.end, sharing currentWall.start
      // Check collinearity: targetStart, targetEnd (shared), currentEnd
      if (orientation(targetStart, targetEnd, currentEnd) === 0) {
        endNeighbor = currentWall;
      }
    } else if (pointsAreEqual(targetEnd, currentEnd, tolerance)) {
      // Potential neighbor at targetWall.end, sharing currentWall.end
      // Check collinearity: targetStart, targetEnd (shared), currentStart
      if (orientation(targetStart, targetEnd, currentStart) === 0) {
        endNeighbor = currentWall;
      }
    }
  }

  return { startNeighbor, endNeighbor };
};

export const isPointOnWall = (point: Point, wall: Wall, threshold: number = 5): Point | null => {
    const { start, end } = wall;
    const d1 = Math.hypot(point.x - start.x, point.y - start.y);
    const d2 = Math.hypot(point.x - end.x, point.y - end.y);
    const wallLength = Math.hypot(end.x - start.x, end.y - start.y);

    // Use distanceToLine for more robust check
    const distToLineSegment = pointToLineDistance(point, start, end);

    if (distToLineSegment < threshold && d1 <= wallLength && d2 <= wallLength) {
      // Project point onto the line segment
       if (wallLength === 0) return { ...start }; // Handle zero-length wall
       const t = ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / (wallLength * wallLength);
       const clampedT = Math.max(0, Math.min(1, t));
       return {
         x: start.x + clampedT * (end.x - start.x),
         y: start.y + clampedT * (end.y - start.y)
       };
    }
    return null;
};

export const isPointOnAnyWall = (point: Point, walls: Wall[], threshold: number = 0.1): boolean => {
  for (const wall of walls) {
    if (pointToLineDistance(point, wall.start, wall.end) < threshold) {
      // pointToLineDistance clamps the projection to the segment, so this check is sufficient.
      return true;
    }
  }
  return false;
};

// --- Snapping and Alignment ---

export const findAlignedCorners = (point: Point, walls: Wall[]): { vertical: Point[]; horizontal: Point[] } => {
  const threshold = SNAP_THRESHOLD;
  const vertical: Point[] = [];
  const horizontal: Point[] = [];
  const corners = new Set<string>();

  walls.forEach(wall => {
    corners.add(`${wall.start.x},${wall.start.y}`);
    corners.add(`${wall.end.x},${wall.end.y}`);
  });

  corners.forEach(cornerStr => {
    const [x, y] = cornerStr.split(',').map(Number);
    if (Math.abs(point.x - x) < threshold) vertical.push({ x, y });
    if (Math.abs(point.y - y) < threshold) horizontal.push({ x, y });
  });

  return { vertical, horizontal };
};

export const findWallEndpoint = (point: Point, walls: Wall[]): { point: Point; wall: Wall } | null => {
    for (const wall of walls) {
      for (const endpoint of ['start', 'end'] as const) {
        const wallPoint = wall[endpoint];
        const distance = Math.hypot(point.x - wallPoint.x, point.y - wallPoint.y);

        if (distance < POINT_SNAP_THRESHOLD) {
           // Calculate safe connection point slightly offset from the actual endpoint
           const safePoint = { ...wallPoint };
           const dx = wall.end.x - wall.start.x;
           const dy = wall.end.y - wall.start.y;
           const length = Math.hypot(dx, dy);
           if (length === 0) continue; // Skip zero-length walls

           const dirX = dx / length;
           const dirY = dy / length;
           const perpOffset = WALL_THICKNESS * 0.75;
           const perpX = -dirY; // Perpendicular vector
           const perpY = dirX;

           // Offset slightly perpendicular to the wall
           safePoint.x += perpX * perpOffset * (endpoint === 'start' ? 1 : -1); // Adjust offset direction
           safePoint.y += perpY * perpOffset * (endpoint === 'start' ? 1 : -1);

           return { point: safePoint, wall };
        }
      }
    }
    return null;
};

export const shouldSnapToEndpoint = (point: Point, walls: Wall[]): { snapped: boolean, snappedPoint: Point } => {
    for (const wall of walls) {
        const distToStart = Math.hypot(point.x - wall.start.x, point.y - wall.start.y);
        if (distToStart < SNAP_DISTANCE) return { snapped: true, snappedPoint: { ...wall.start } };
        const distToEnd = Math.hypot(point.x - wall.end.x, point.y - wall.end.y);
        if (distToEnd < SNAP_DISTANCE) return { snapped: true, snappedPoint: { ...wall.end } };
    }
    return { snapped: false, snappedPoint: point };
};

export const shouldSnapToWall = (newWallStart: Point, newWallEnd: Point, existingWalls: Wall[]): {
  startSnapped: boolean,
  endSnapped: boolean,
  snappedStart: Point,
  snappedEnd: Point
} => {
    const result = {
        startSnapped: false,
        endSnapped: false,
        snappedStart: { ...newWallStart },
        snappedEnd: { ...newWallEnd }
    };

    const startEndpointSnap = shouldSnapToEndpoint(newWallStart, existingWalls);
    if (startEndpointSnap.snapped) {
        result.startSnapped = true;
        result.snappedStart = startEndpointSnap.snappedPoint;
    }

    const endEndpointSnap = shouldSnapToEndpoint(newWallEnd, existingWalls);
    if (endEndpointSnap.snapped) {
        result.endSnapped = true;
        result.snappedEnd = endEndpointSnap.snappedPoint;
        return result; // Endpoint snap takes priority
    }

    for (const wall of existingWalls) {
        // Skip if the wall being checked is effectively the one being created (e.g., during drag)
         if ((isNearPoint(wall.start, newWallStart) && isNearPoint(wall.end, newWallEnd)) ||
            (isNearPoint(wall.start, newWallEnd) && isNearPoint(wall.end, newWallStart)) ) {
             continue;
         }

        const distanceToWall = pointToLineDistance(newWallEnd, wall.start, wall.end);
        if (distanceToWall < SNAP_THRESHOLD) {
            const lineLength2 = Math.pow(wall.end.x - wall.start.x, 2) + Math.pow(wall.end.y - wall.start.y, 2);
            if (lineLength2 === 0) continue; // Skip zero-length walls

            const t = Math.max(0, Math.min(1, (
                (newWallEnd.x - wall.start.x) * (wall.end.x - wall.start.x) +
                (newWallEnd.y - wall.start.y) * (wall.end.y - wall.start.y)
            ) / lineLength2));

            const snappedX = wall.start.x + t * (wall.end.x - wall.start.x);
            const snappedY = wall.start.y + t * (wall.end.y - wall.start.y);

            result.endSnapped = true;
            result.snappedEnd = { x: snappedX, y: snappedY };
            break;
        }
    }
    return result;
};

// Generic function to snap a point to a grid
export const snapPointToGrid = (point: Point, gridSize: number): Point => {
  const gridX = Math.round(point.x / gridSize) * gridSize;
  const gridY = Math.round(point.y / gridSize) * gridSize;
  return { x: gridX, y: gridY };
};

// Snapping function for finer room creation or general point snapping
export const snapToFineGridOrEndpoints = (point: Point, wallsToSnapTo: Wall[], endpointSnapThreshold: number, cmSnapSize: number): Point => {
  for (const wall of wallsToSnapTo) {
    if (Math.hypot(point.x - wall.start.x, point.y - wall.start.y) < endpointSnapThreshold) {
      return { ...wall.start }; // Return a copy
    }
    if (Math.hypot(point.x - wall.end.x, point.y - wall.end.y) < endpointSnapThreshold) {
      return { ...wall.end }; // Return a copy
    }
  }
  // If not snapping to an endpoint, snap to a finer grid
  const fineGridX = Math.round(point.x / cmSnapSize) * cmSnapSize;
  const fineGridY = Math.round(point.y / cmSnapSize) * cmSnapSize;
  return { x: fineGridX, y: fineGridY };
};

// --- Intersection and Containment ---

export const doesWallIntersect = (newWall: Wall, existingWalls: Wall[]): boolean => {
    return existingWalls.some(wall =>
      doLinesIntersect(newWall.start, newWall.end, wall.start, wall.end) &&
      // Exclude endpoint connections by checking if points are *not* near
      !isNearPoint(newWall.start, wall.start, 1) &&
      !isNearPoint(newWall.start, wall.end, 1) &&
      !isNearPoint(newWall.end, wall.start, 1) &&
      !isNearPoint(newWall.end, wall.end, 1)
    );
};

export const isPointInsideRoom = (point: Point, walls: Wall[]): boolean => {
  if (walls.length < 3) return false;

  // 🚀 PERFORMANCE: Quick bounds check first (much faster than ray casting)
  const bounds = getRoomBounds(walls); // This is now cached
  if (point.x < bounds.minX || point.x > bounds.maxX || 
      point.y < bounds.minY || point.y > bounds.maxY) {
    return false;
  }

  // 🚀 PERFORMANCE: Cache point-in-room calculations for frequently checked points
  const pointKey = `${point.x.toFixed(1)},${point.y.toFixed(1)}`;
  const wallsKey = generateWallsCacheKey(walls);
  const cacheKey = `inside_${pointKey}_${wallsKey}`;
  
  if (geometryCache.has(cacheKey)) {
    return geometryCache.get(cacheKey);
  }

  // 🔧 ENHANCED: Tolerance-based point-in-polygon for complex room geometries
  const result = isPointInsideRoomWithTolerance(point, walls, bounds);
  
  geometryCache.set(cacheKey, result);
  return result;
};

/**
 * 🔧 TOLERANCE-BASED: Enhanced point-in-polygon detection for complex room geometries
 * Handles rooms with split walls, extrusions, and coordinate gaps from repeated operations
 */
const isPointInsideRoomWithTolerance = (point: Point, walls: Wall[], bounds: any): boolean => {
  const tolerance = 5; // 5 pixel tolerance for coordinate gaps
  
  // 🔧 WALL SPLIT FIX: Check if this room has split walls that might break geometry
  const hasSplitWalls = walls.some(w => (w as any).isSplitOrigin || (w as any).parentWallId);
  
  if (hasSplitWalls) {
    // Use enhanced geometry repair for rooms with split walls
    return isPointInsideRoomWithSplitWallRepair(point, walls, bounds, tolerance);
  }
  
  // First, try standard ray casting
  let inside = false;
  for (let i = 0, j = walls.length - 1; i < walls.length; j = i++) {
    const xi = walls[i].start.x, yi = walls[i].start.y;
    const xj = walls[j].start.x, yj = walls[j].start.y;
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  // If standard ray casting succeeded or point is clearly outside bounds, return result
  if (inside || 
      point.x < bounds.minX - tolerance || point.x > bounds.maxX + tolerance ||
      point.y < bounds.minY - tolerance || point.y > bounds.maxY + tolerance) {
    return inside;
  }
  
  // Fallback for edge cases near room boundaries
  return isPointNearRoomBoundary(point, walls, tolerance);
};

/**
 * 🔧 WALL SPLIT FIX: Enhanced point-in-polygon for rooms with split walls
 * Repairs coordinate gaps and wall ordering issues that break room geometry
 */
const isPointInsideRoomWithSplitWallRepair = (point: Point, walls: Wall[], bounds: any, tolerance: number): boolean => {
  // Step 1: Repair wall continuity by connecting gaps
  const repairedWalls = repairWallContinuity(walls, tolerance);
  
  // Step 2: Try ray casting with repaired walls
  let inside = false;
  for (let i = 0, j = repairedWalls.length - 1; i < repairedWalls.length; j = i++) {
    const xi = repairedWalls[i].start.x, yi = repairedWalls[i].start.y;
    const xj = repairedWalls[j].start.x, yj = repairedWalls[j].start.y;
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  // Step 3: If still failed, use enhanced boundary detection
  if (!inside && isPointNearRoomBoundary(point, repairedWalls, tolerance * 2)) {
    // For points very close to repaired room boundary, consider them inside if within expanded tolerance
    const expandedTolerance = tolerance * 3;
    return point.x >= bounds.minX - expandedTolerance &&
           point.x <= bounds.maxX + expandedTolerance &&
           point.y >= bounds.minY - expandedTolerance &&
           point.y <= bounds.maxY + expandedTolerance;
  }
  
  return inside;
};

/**
 * 🔧 WALL SPLIT FIX: Repair wall continuity by connecting small gaps
 */
const repairWallContinuity = (walls: Wall[], tolerance: number): Wall[] => {
  if (walls.length < 3) return walls;
  
  const repairedWalls = [...walls];
  
  for (let i = 0; i < repairedWalls.length; i++) {
    const currentWall = repairedWalls[i];
    const nextIndex = (i + 1) % repairedWalls.length;
    const nextWall = repairedWalls[nextIndex];
    
    // Check for gap between current wall end and next wall start
    const gap = Math.hypot(
      currentWall.end.x - nextWall.start.x,
      currentWall.end.y - nextWall.start.y
    );
    
    // If gap is small (coordinate drift from splits), connect the walls
    if (gap > 0.1 && gap <= tolerance) {
      // Adjust next wall start to connect with current wall end
      repairedWalls[nextIndex] = {
        ...nextWall,
        start: { x: currentWall.end.x, y: currentWall.end.y }
      };
    }
  }
  
  return repairedWalls;
};

/**
 * Check if point is near room boundary within tolerance
 */
const isPointNearRoomBoundary = (point: Point, walls: Wall[], tolerance: number): boolean => {
  // 🔧 FALLBACK: For complex geometries, use tolerance-based boundary detection
  // Check if point is close to room boundary (within tolerance)
  const isNearBoundary = walls.some(wall => {
    const distanceToWall = pointToLineDistance(point, wall.start, wall.end);
    if (distanceToWall > tolerance) return false;
    
    // Check if point projects onto wall segment (not just extended line)
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
    
    if (wallLength === 0) return false;
    
    const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
    const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength ** 2);
    
    return projection >= -0.1 && projection <= 1.1; // Small tolerance for projection bounds
  });
  
  // If point is near boundary but ray casting failed, use centroid-based approach
  if (isNearBoundary) {
    const roomCenter = getRoomCenter(walls);
    const distanceToCenter = Math.hypot(point.x - roomCenter.x, point.y - roomCenter.y);
    
    // Calculate average distance from center to walls as room "radius"
    const avgWallDistance = walls.reduce((sum, wall) => {
      const wallMid = { x: (wall.start.x + wall.end.x) / 2, y: (wall.start.y + wall.end.y) / 2 };
      return sum + Math.hypot(wallMid.x - roomCenter.x, wallMid.y - roomCenter.y);
    }, 0) / walls.length;
    
    // Point is inside if it's closer to center than average wall distance (with tolerance)
    return distanceToCenter <= avgWallDistance * 1.2; // 20% tolerance
  }
  
  return false;
};

// --- Wall Type Specific Calculations ---

export const calculateSWLength = (walls: Wall[]): number => {
  let totalLength = 0;
  
  walls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // 🆕 ENHANCED SECTION-BASED CALCULATION: Calculate SW length from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach(section => {
        if (section.type === 'SW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / PIXELS_PER_METER;
          totalLength += sectionLengthMeters;
        }
      });
    } else if (wall.type === 'SW') {
      // Standard calculation for walls without sections
      totalLength += calculateWallLength(wall.start, wall.end);
    }
  });
  
  // Round final total to 2 decimal places
  return Math.round(totalLength * 100) / 100;
};

export const calculateHLPLength = (walls: Wall[]): number => {
  let totalLength = 0;
  
  walls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // 🆕 ENHANCED SECTION-BASED CALCULATION: Calculate HLP+SW length from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach(section => {
        if (section.type === 'HLP' || section.type === 'SW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / PIXELS_PER_METER;
          totalLength += sectionLengthMeters;
        }
      });
    } else if (wall.type === 'HLP' || wall.type === 'SW') {
      // Standard calculation for walls without sections
      totalLength += calculateWallLength(wall.start, wall.end);
    }
  });
  
  // Round final total to 2 decimal places
  return Math.round(totalLength * 100) / 100;
};

export const calculatePWLength = (walls: Wall[]): number => {
  let totalLength = 0;
  
  walls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // 🆕 ENHANCED SECTION-BASED CALCULATION: Calculate PW length from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach(section => {
        if (section.type === 'PW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / PIXELS_PER_METER;
          totalLength += sectionLengthMeters;
        }
      });
    } else if (wall.type === 'PW') {
      // Standard calculation for walls without sections
      totalLength += calculateWallLength(wall.start, wall.end);
    }
  });
  
  // Round final total to 2 decimal places
  return Math.round(totalLength * 100) / 100;
};

// --- Professional CAD-Inspired Room Creation System ---

/**
 * Professional CAD approach: Boundary Integrity Preservation
 * Key principles:
 * 1. Preserve existing room boundaries when adding new connections
 * 2. Use wall following instead of greedy cycle detection
 * 3. Apply spatial reasoning to distinguish rooms from connecting passages
 * 4. Maintain boundary integrity during partial connections
 */

/**
 * Create rooms using professional CAD boundary integrity principles
 * This prevents unwanted merging in partially connected scenarios
 */
function createRoomsWithBoundaryIntegrity(
  walls: Wall[], 
  roomCustomLabels: Record<string, string> = {},
  currentFloor?: number
): Room[] {
  console.log('🏗️ PROFESSIONAL CAD APPROACH: Boundary Integrity Preservation', { wallCount: walls.length });
  
  // Step 1: Identify potential room seed points using spatial analysis
  const roomSeeds = identifyRoomSeedPoints(walls);
  console.log(`🌱 IDENTIFIED ${roomSeeds.length} ROOM SEED POINTS`);
  
  // Step 2: For each seed, grow a room using boundary following
  const rooms: Room[] = [];
  const processedAreas = new Set<string>();
  
  for (const seed of roomSeeds) {
    const seedKey = `${seed.x.toFixed(1)},${seed.y.toFixed(1)}`;
    if (processedAreas.has(seedKey)) continue;
    
    // Use boundary following to trace the room boundary
    const roomBoundary = traceRoomBoundaryFromSeed(seed, walls);
    
    if (roomBoundary && roomBoundary.length >= 3) {
      // Validate this is a legitimate room (not a connecting passage)
      if (validateRoomBoundary(roomBoundary, rooms)) {
        const center = getRoomCenter(roomBoundary);
        
        // 🛡️ USE PERSISTENT ROOM ID: Get or create a stable ID that never changes
        const persistentId = getPersistentRoomId(roomBoundary, currentFloor);
        
        // 🔄 CHECK EXISTING LABELS: First check with persistent ID, then coordinate-based ID for migration
        const coordinateBasedId = getPersistentRoomId(roomBoundary, currentFloor);
        const customLabel = roomCustomLabels[persistentId] || roomCustomLabels[coordinateBasedId];
        const roomName = customLabel || `Room ${rooms.length + 1}`;
        
        const room: Room = {
          id: persistentId,
          stableId: persistentId,
          name: roomName,
          walls: [...roomBoundary],
          area: Math.abs(calculateAreaFast(roomBoundary)),
          center,
          elements: [],
          isExtension: false
        };
        
        rooms.push(room);
        
        // Mark this area as processed to prevent overlapping rooms
        const roomBounds = getRoomBounds(roomBoundary);
        for (let x = roomBounds.minX; x <= roomBounds.maxX; x += 10) {
          for (let y = roomBounds.minY; y <= roomBounds.maxY; y += 10) {
            if (isPointInsideRoom({ x, y }, roomBoundary)) {
              processedAreas.add(`${x.toFixed(1)},${y.toFixed(1)}`);
            }
          }
        }
        
        console.log(`🏠 TRACED ROOM: ${roomName} (${roomBoundary.length} walls, ${room.area.toFixed(2)} m²)`);
      }
    }
  }
  
  console.log(`✅ BOUNDARY INTEGRITY PRESERVED: Found ${rooms.length} distinct rooms`);
  return rooms;
}

/**
 * Identify room seed points using spatial analysis
 * Professional CAD principle: Start from logical room centers, not wall endpoints
 */
function identifyRoomSeedPoints(walls: Wall[]): Point[] {
  const seeds: Point[] = [];
  const bounds = getRoomBounds(walls);
  const gridSpacing = 20; // Professional CAD uses regular grid sampling
  
  // Sample points in a grid pattern to find interior spaces
  for (let x = bounds.minX + gridSpacing; x < bounds.maxX; x += gridSpacing) {
    for (let y = bounds.minY + gridSpacing; y < bounds.maxY; y += gridSpacing) {
      const testPoint = { x, y };
      
      // Check if this point is inside a potential room (not on walls)
      if (!isPointOnAnyWall(testPoint, walls, 5)) {
        // Ensure it's actually enclosed by checking ray casting
        if (isPointEnclosedByWalls(testPoint, walls)) {
          seeds.push(testPoint);
        }
      }
    }
  }
  
  return seeds;
}

/**
 * Check if a point is enclosed by walls using ray casting
 * Professional CAD principle: Proper inside/outside testing
 */
function isPointEnclosedByWalls(point: Point, walls: Wall[]): boolean {
  let crossings = 0;
  const rayEnd = { x: point.x + 1000, y: point.y }; // Cast ray to the right
  
  for (const wall of walls) {
    if (doLinesIntersect(point, rayEnd, wall.start, wall.end)) {
      crossings++;
    }
  }
  
  return crossings % 2 === 1; // Odd number of crossings = inside
}

/**
 * Trace room boundary from a seed point using wall following
 * Professional CAD principle: Systematic boundary tracing instead of greedy search
 */
function traceRoomBoundaryFromSeed(seed: Point, walls: Wall[]): Wall[] | null {
  // Find the closest wall to start boundary tracing
  let closestWall = null;
  let closestDistance = Infinity;
  
  for (const wall of walls) {
    const distance = distanceToLine(seed, wall.start, wall.end);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestWall = wall;
    }
  }
  
  if (!closestWall || closestDistance > 50) {
    return null; // Too far from any wall
  }
  
  // Trace boundary using wall following algorithm
  return followWallBoundary(closestWall, walls, seed);
}

/**
 * Follow wall boundary to trace complete room perimeter
 * Professional CAD principle: Consistent directional traversal
 */
function followWallBoundary(startWall: Wall, allWalls: Wall[], seedPoint: Point): Wall[] | null {
  const boundary: Wall[] = [];
  const visited = new Set<string>();
  const tolerance = 0.1;
  
  let currentWall = startWall;
  let currentEndPoint = getCorrectWallDirection(currentWall, seedPoint);
  const startPoint = currentWall.start;
  
  // Follow walls in consistent direction
  for (let step = 0; step < 20; step++) { // Limit to prevent infinite loops
    const wallKey = `${currentWall.start.x},${currentWall.start.y}-${currentWall.end.x},${currentWall.end.y}`;
    
    if (visited.has(wallKey)) {
      break; // Already processed this wall
    }
    
    visited.add(wallKey);
    boundary.push(currentWall);
    
    // Find next wall that connects and turns in the correct direction
    const nextWall = findNextBoundaryWall(currentEndPoint, allWalls, visited, seedPoint);
    
    if (!nextWall) {
      break; // No more connecting walls
    }
    
    // Check if we've completed the boundary
    const nextEndPoint = getConnectedEndpoint(nextWall, currentEndPoint);
    if (Math.abs(nextEndPoint.x - startPoint.x) < tolerance && 
        Math.abs(nextEndPoint.y - startPoint.y) < tolerance) {
      boundary.push(nextWall);
      return boundary; // Completed boundary
    }
    
    currentWall = nextWall;
    currentEndPoint = nextEndPoint;
  }
  
  return boundary.length >= 3 ? boundary : null;
}

/**
 * Get the correct wall direction based on seed point position
 * Professional CAD principle: Interior-aware directional selection
 */
function getCorrectWallDirection(wall: Wall, seedPoint: Point): Point {
  // Choose wall direction that keeps the seed point on the "inside"
  const midPoint = {
    x: (wall.start.x + wall.end.x) / 2,
    y: (wall.start.y + wall.end.y) / 2
  };
  
  // Use cross product to determine which direction keeps seed on inside
  const toSeed = { x: seedPoint.x - midPoint.x, y: seedPoint.y - midPoint.y };
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const cross = wallVector.x * toSeed.y - wallVector.y * toSeed.x;
  
  return cross > 0 ? wall.end : wall.start;
}

/**
 * Find the next wall in boundary traversal
 * Professional CAD principle: Interior-aware next wall selection
 */
function findNextBoundaryWall(
  currentEndPoint: Point, 
  allWalls: Wall[], 
  visited: Set<string>, 
  seedPoint: Point
): Wall | null {
  const tolerance = 0.1;
  const candidates: { wall: Wall; angle: number }[] = [];
  
  for (const wall of allWalls) {
    const wallKey = `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`;
    if (visited.has(wallKey)) continue;
    
    // Check if wall connects to current endpoint
    const connectsAtStart = Math.abs(wall.start.x - currentEndPoint.x) < tolerance && 
                           Math.abs(wall.start.y - currentEndPoint.y) < tolerance;
    const connectsAtEnd = Math.abs(wall.end.x - currentEndPoint.x) < tolerance && 
                         Math.abs(wall.end.y - currentEndPoint.y) < tolerance;
    
    if (connectsAtStart || connectsAtEnd) {
      // Calculate angle to prioritize walls that turn toward the interior
      const nextPoint = connectsAtStart ? wall.end : wall.start;
      const angle = calculateInteriorAngle(currentEndPoint, nextPoint, seedPoint);
      candidates.push({ wall, angle });
    }
  }
  
  if (candidates.length === 0) return null;
  
  // Choose wall that best follows the interior boundary
  candidates.sort((a, b) => a.angle - b.angle);
  return candidates[0].wall;
}

/**
 * Calculate angle that prioritizes interior-facing turns
 * Professional CAD principle: Consistent interior boundary following
 */
function calculateInteriorAngle(current: Point, next: Point, seed: Point): number {
  const toNext = { x: next.x - current.x, y: next.y - current.y };
  const toSeed = { x: seed.x - current.x, y: seed.y - current.y };
  
  const angle = Math.atan2(toNext.y, toNext.x) - Math.atan2(toSeed.y, toSeed.x);
  return angle < 0 ? angle + 2 * Math.PI : angle;
}

/**
 * Get the endpoint of a wall that connects to the given point
 */
function getConnectedEndpoint(wall: Wall, connectPoint: Point): Point {
  const tolerance = 0.1;
  
  if (Math.abs(wall.start.x - connectPoint.x) < tolerance && 
      Math.abs(wall.start.y - connectPoint.y) < tolerance) {
    return wall.end;
  }
  return wall.start;
}

/**
 * Validate that a traced boundary represents a legitimate room
 * Professional CAD principle: Room validation using geometric and spatial criteria
 */
function validateRoomBoundary(boundary: Wall[], existingRooms: Room[]): boolean {
  const area = Math.abs(calculateAreaFast(boundary));
  
  // Must have minimum area
  if (area < 1.0) {
    console.log(`🚫 REJECTING SMALL BOUNDARY: Area ${area.toFixed(2)} < 1.0 m²`);
    return false;
  }
  
  // Must not be a duplicate of existing room
  const center = getRoomCenter(boundary);
  for (const existing of existingRooms) {
    const distance = Math.sqrt(
      Math.pow(center.x - existing.center.x, 2) + 
      Math.pow(center.y - existing.center.y, 2)
    );
    
    if (distance < 10 && Math.abs(area - existing.area) < 1.0) {
      console.log(`🚫 REJECTING DUPLICATE BOUNDARY: Too close to existing room`);
      return false;
    }
  }
  
  return true;
}

// --- Room-Wall Association System ---

/**
 * Assigns walls to rooms based on spatial containment and adjacency.
 * Each wall can belong to multiple rooms (shared walls).
 * This replaces complex cycle detection with explicit relationships.
 * 
 * 🛡️ ENHANCED: Preserves existing wall ownership during resize operations
 */
function assignWallsToRooms(walls: Wall[], rooms: Room[]): Wall[] {
  console.log('🔗 ASSIGNING WALLS TO ROOMS:', { wallCount: walls.length, roomCount: rooms.length });
  
  const updatedWalls = walls.map(wall => {
    // 🛡️ PRESERVE EXISTING OWNERSHIP: Check if wall already has ownership metadata
    const hasExistingOwnership = !!(wall as any).originatingRoomId || 
                                !!(wall as any).assignedRoomIds?.length || 
                                !!(wall as any).parentRoomIds?.length;
    
    if (hasExistingOwnership) {
      // 🛡️ PRESERVE: Don't overwrite existing ownership during resize operations
      console.log('🛡️ PRESERVING_EXISTING_OWNERSHIP:', {
        wallId: (wall as any).tempId,
        coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
        originatingRoomId: (wall as any).originatingRoomId,
        assignedRoomIds: (wall as any).assignedRoomIds?.length || 0,
        parentRoomIds: (wall as any).parentRoomIds?.length || 0
      });
      
      // Return wall with existing ownership intact
      return {
        ...wall,
        // Ensure all ownership fields are preserved
        ...(((wall as any).originatingRoomId) && { originatingRoomId: (wall as any).originatingRoomId }),
        ...(((wall as any).assignedRoomIds) && { assignedRoomIds: [...(wall as any).assignedRoomIds] }),
        ...(((wall as any).parentRoomIds) && { parentRoomIds: [...(wall as any).parentRoomIds] })
      };
    }
    
    // 🆕 CREATE NEW OWNERSHIP: Only for walls without existing ownership
    console.log('🆕 CREATING_NEW_OWNERSHIP:', {
      wallId: (wall as any).tempId,
      coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
      reason: 'No existing ownership found'
    });
    
    const roomIds: string[] = [];
    
    // Find rooms that contain or are bounded by this wall
    for (const room of rooms) {
      if (room.stableId && isWallPartOfRoom(wall, room)) {
        roomIds.push(room.stableId);
      }
    }
    
    return {
      ...wall,
      parentRoomIds: roomIds.length > 0 ? roomIds : undefined
    };
  });
  
  const preservedWalls = updatedWalls.filter(w => 
    (w as any).originatingRoomId || (w as any).assignedRoomIds?.length || (w as any).parentRoomIds?.length
  ).length;
  const newlyAssignedWalls = updatedWalls.filter(w => 
    !(w as any).originatingRoomId && !(w as any).assignedRoomIds?.length && (w as any).parentRoomIds?.length
  ).length;
  
  console.log('🔗 WALL ASSIGNMENT COMPLETE:', {
    totalWalls: updatedWalls.length,
    preservedOwnership: preservedWalls - newlyAssignedWalls,
    newlyAssigned: newlyAssignedWalls,
    wallsWithoutRooms: updatedWalls.filter(w => !(w as any).parentRoomIds?.length).length
  });
  
  return updatedWalls;
}

/**
 * Checks if a wall is part of a room's boundary
 * FIXED: Only assign walls that are actually part of the room's defining walls
 */
function isWallPartOfRoom(wall: Wall, room: Room): boolean {
  // Only check if wall is one of the room's defining walls
  // Remove the overly permissive boundary detection that was causing over-assignment
  for (const roomWall of room.walls) {
    if (areWallsEqual(wall, roomWall)) {
      return true;
    }
  }
  
  // DO NOT use boundary detection - it was causing over-assignment
  // The room's walls array already contains all walls that should belong to the room
  return false;
}

/**
 * Checks if two walls are essentially the same (within tolerance)
 * 🔧 CRITICAL FIX: Increased tolerance from 0.1 to 1.0 to handle coordinate precision drift
 * during resize operations, especially for split wall segments
 */
function areWallsEqual(wall1: Wall, wall2: Wall, tolerance: number = 1.0): boolean {
  return (isNearPoint(wall1.start, wall2.start, tolerance) && isNearPoint(wall1.end, wall2.end, tolerance)) ||
         (isNearPoint(wall1.start, wall2.end, tolerance) && isNearPoint(wall1.end, wall2.start, tolerance));
}

/**
 * Calculate room area using only walls assigned to that room
 */
function calculateRoomAreaByAssignment(roomId: string, allWalls: Wall[]): number {
  const roomWalls = allWalls.filter(wall => {
    // 🔧 CRITICAL FIX: Check for room membership using BOTH old and new ownership formats
    const belongsToRoomOld = (wall as any).parentRoomIds?.includes(roomId);
    const belongsToRoomNew = (
      (wall as any).originatingRoomId === roomId ||
      (wall as any).assignedRoomIds?.includes(roomId)
    );
    return belongsToRoomOld || belongsToRoomNew;
  });
  
  if (roomWalls.length < 3) {
    console.warn(`🚫 Room ${roomId} has insufficient walls for area calculation:`, roomWalls.length);
    return 0;
  }
  
  const area = calculateAreaFast(roomWalls);
  console.log(`📐 Room ${roomId} area calculation:`, {
    assignedWalls: roomWalls.length,
    area: area.toFixed(2),
    walls: roomWalls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)}) -> (${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
  });
  
  return area;
}

/**
 * Create rooms with explicit wall ownership to prevent merging
 * Each wall remembers which room it was created from
 */
function createRoomsWithWallAssignment(
  walls: Wall[], 
  roomCustomLabels: Record<string, string> = {},
  currentFloor?: number,
  context: RoomCalculationContext = 'geometryUpdate'
): Room[] {
  console.log('🏠 CREATING ROOMS WITH WALL OWNERSHIP:', { wallCount: walls.length });
  
  // 🛡️ DEFENSIVE: Validate walls input
  if (!walls || !Array.isArray(walls) || walls.length === 0) {
    console.warn('⚠️ ROOM_CREATION: Invalid or empty walls array');
    return [];
  }
  
  // 🛡️ DEFENSIVE: Filter out invalid walls before processing
  const validWalls = walls.filter(w => {
    if (!w || !w.start || !w.end) return false;
    if (typeof w.start.x !== 'number' || typeof w.start.y !== 'number') return false;
    if (typeof w.end.x !== 'number' || typeof w.end.y !== 'number') return false;
    
    // Check for degenerate walls (zero length or near-zero)
    const dx = w.end.x - w.start.x;
    const dy = w.end.y - w.start.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 0.01) {
      console.warn('⚠️ DEGENERATE_WALL: Excluding zero-length wall', {
        coords: `(${w.start.x},${w.start.y})->(${w.end.x},${w.end.y})`,
        length: length.toFixed(4)
      });
      return false;
    }
    
    return true;
  });
  
  if (validWalls.length !== walls.length) {
    console.warn(`⚠️ WALL_VALIDATION: Filtered out ${walls.length - validWalls.length} invalid walls`);
  }
  
  // Step 1: Separate walls by their ownership if they have it
  const ownedWalls = validWalls.filter(w => w.originatingRoomId || w.assignedRoomIds?.length);
  const unownedWalls = validWalls.filter(w => !w.originatingRoomId && !w.assignedRoomIds?.length);
  
  console.log(`🔗 WALL OWNERSHIP: ${ownedWalls.length} owned, ${unownedWalls.length} unowned`);
  
  // Step 2: Create rooms from owned walls first (prevents merging)
  const rooms: Room[] = [];
  const processedWalls = new Set<string>();
  
  if (ownedWalls.length > 0) {
    // Group owned walls by their originating room
    const wallsByRoom = new Map<string, Wall[]>();
    
    console.log(`🔍 AR MULTI-ROOM DEBUG: Grouping ${ownedWalls.length} owned walls by originatingRoomId`);
    
    ownedWalls.forEach((wall, idx) => {
      const roomId = wall.originatingRoomId || wall.assignedRoomIds?.[0];
      console.log(`  Wall[${idx}]: (${wall.start.x.toFixed(0)},${wall.start.y.toFixed(0)})->(${wall.end.x.toFixed(0)},${wall.end.y.toFixed(0)}) → roomId: ${roomId}`);
      
      if (roomId) {
        if (!wallsByRoom.has(roomId)) {
          wallsByRoom.set(roomId, []);
        }
        wallsByRoom.get(roomId)!.push(wall);
      }
    });
    
    console.log(`🔍 AR MULTI-ROOM DEBUG: Wall groups created:`, Array.from(wallsByRoom.entries()).map(([roomId, walls]) => 
      `${roomId}: ${walls.length} walls`
    ));
    
    // Create rooms from owned wall groups
    for (const [roomId, roomWalls] of wallsByRoom) {
      if (roomWalls.length >= 3) {
        // 🔍 DEBUG: Log ALL walls for this room to debug identical area bug
        console.log(`🏠 Creating room from owned walls:`, {
          roomId,
          wallCount: roomWalls.length,
          allWalls: roomWalls.map((w, i) => `[${i}] (${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`),
          ownerships: roomWalls.map((w: any, i) => `[${i}] ${w.originatingRoomId || 'none'}`)
        });
        
        // Calculate area and center
        let area: number;
        let center: Point;
        
        // 🛡️ DEFENSIVE: Validate room walls form a valid polygon
        try {
          area = Math.abs(calculateAreaFast(roomWalls));
          center = getRoomCenter(roomWalls);
          
          console.log(`📐 Room ${roomId} calculated:`, {
            area: area.toFixed(2),
            center: `(${center.x.toFixed(1)}, ${center.y.toFixed(1)})`,
            wallCount: roomWalls.length
          });
          
          if (area <= 0 || !isFinite(area) || isNaN(area)) {
            console.warn('⚠️ INVALID_ROOM_AREA:', {
              roomId,
              area,
              wallCount: roomWalls.length,
              walls: roomWalls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
            });
            continue; // Skip this invalid room
          }
          
          // Sanity check: area should be reasonable (between 0.1m² and 10000m²)
          if (area < 0.1 || area > 10000) {
            console.warn('⚠️ SUSPICIOUS_ROOM_AREA:', {
              roomId,
              area: area.toFixed(2),
              wallCount: roomWalls.length,
              message: area < 0.1 ? 'Area too small - likely degenerate polygon' : 'Area too large - likely polygon calculation error'
            });
            // Continue anyway but log the warning
          }
        } catch (error) {
          console.error('❌ AREA_CALCULATION_ERROR:', {
            roomId,
            error,
            wallCount: roomWalls.length
          });
          continue; // Skip this room if area calculation fails
        }
        
        // Center was already calculated above for debugging
        
        // 🛡️ USE PERSISTENT ROOM ID: Get or create a stable ID that never changes
        let stableId = getPersistentRoomId(roomWalls, currentFloor, roomId);
        let preservedRoomName = roomId; // Default to originating room ID
        
        // 🔄 CHECK EXISTING LABELS: First check with persistent ID, then coordinate-based ID for migration
        const coordinateBasedId = getPersistentRoomId(roomWalls, currentFloor);
        
        // During geometry updates (resize), try to preserve existing room identity
        if (context === 'geometryUpdate' || context === 'labelPreservation') {
          // Check if any existing label matches this room
          const existingLabelWithPersistentId = roomCustomLabels[stableId];
          const existingLabelWithCoordinateId = roomCustomLabels[coordinateBasedId];
          
          if (existingLabelWithPersistentId || existingLabelWithCoordinateId) {
            preservedRoomName = existingLabelWithPersistentId || existingLabelWithCoordinateId;
            
            console.log(`🛡️ PERSISTENT_ID_LABEL_PRESERVED`, {
              persistentId: stableId,
              coordinateBasedId: coordinateBasedId.substring(0, 50) + '...',
              preservedLabel: preservedRoomName,
              context
            });
          }
        }
        
        // 🔧 MULTI-FLOOR FIX: Check floor-aware keys first to prevent cross-floor label collisions
        const floorAwareStableId = currentFloor !== undefined ? `${currentFloor}_${stableId}` : null;
        const floorAwareCoordinateId = currentFloor !== undefined ? `${currentFloor}_${coordinateBasedId}` : null;
        
        const customLabel = (floorAwareStableId && roomCustomLabels[floorAwareStableId]) ||
                            (floorAwareCoordinateId && roomCustomLabels[floorAwareCoordinateId]) ||
                            roomCustomLabels[stableId] || 
                            roomCustomLabels[coordinateBasedId];
        const roomName = customLabel || preservedRoomName;
        
        const room: Room = {
          id: stableId,
          stableId,
          name: roomName,
          walls: [...roomWalls],
          area, // Use already calculated area
          center, // Use already calculated center
          elements: [],
          isExtension: false
        };
        
        rooms.push(room);
        
        // 🔄 MIGRATE WALL OWNERSHIP: Update walls to reference the new persistent room ID
        if (stableId !== coordinateBasedId) {
          console.log('🔄 MIGRATING_WALL_OWNERSHIP', {
            oldId: coordinateBasedId.substring(0, 50) + '...',
            newId: stableId,
            roomName,
            wallCount: roomWalls.length
          });
          
          // Update wall ownership in the global walls array if it exists
          // This ensures walls reference the new persistent room ID
          roomWalls.forEach(roomWall => {
            // Update originatingRoomId if it matches the old coordinate-based ID
            if ((roomWall as any).originatingRoomId === coordinateBasedId) {
              (roomWall as any).originatingRoomId = stableId;
              console.log('🔄 MIGRATED_ORIGINATING_ROOM_ID', {
                wallId: (roomWall as any).tempId,
                newId: stableId
              });
            }
            
            // Update assignedRoomIds array
            if ((roomWall as any).assignedRoomIds?.includes(coordinateBasedId)) {
              (roomWall as any).assignedRoomIds = (roomWall as any).assignedRoomIds.map((id: string) => 
                id === coordinateBasedId ? stableId : id
              );
              console.log('🔄 MIGRATED_ASSIGNED_ROOM_IDS', {
                wallId: (roomWall as any).tempId,
                newId: stableId
              });
            }
            
            // Update parentRoomIds array
            if ((roomWall as any).parentRoomIds?.includes(coordinateBasedId)) {
              (roomWall as any).parentRoomIds = (roomWall as any).parentRoomIds.map((id: string) => 
                id === coordinateBasedId ? stableId : id
              );
              console.log('🔄 MIGRATED_PARENT_ROOM_IDS', {
                wallId: (roomWall as any).tempId,
                newId: stableId
              });
            }
          });
        }
        
        // Mark these walls as processed
        roomWalls.forEach(w => {
          const key = `${w.start.x},${w.start.y}-${w.end.x},${w.end.y}`;
          processedWalls.add(key);
        });
        
        console.log(`🏠 OWNED ROOM: ${roomName} (${roomWalls.length} walls, ${room.area.toFixed(2)} m²)`);
      }
    }
  }
  
  // Step 3: Handle unowned walls (old files) - assign ownership to prevent merging
  if (unownedWalls.length > 0) {
    console.log(`🔧 OLD FILE DETECTED: Pre-assigning ownership to ${unownedWalls.length} unowned walls`);
    
    // 🔧 CRITICAL FIX: Check if we already have existing labeled rooms
    // If so, only assign ownership to walls but don't create additional rooms
    const hasExistingLabels = Object.keys(roomCustomLabels).length > 0;
    const existingRoomCount = rooms.length;
    
          // 🔧 CONTEXT-AWARE OLD FILE DETECTION: Be much more conservative during wall operations
      const isWallOperation = context === 'geometryUpdate';
      const isInitialLoading = context === 'explicitRoomCreation';
      
      let shouldCreateAdditionalRooms = true;
      
      if (isWallOperation && hasExistingLabels && existingRoomCount > 0) {
        // During wall operations, only prevent additional room creation if there are BOTH labels AND existing rooms
        // This prevents inappropriate room merging while still allowing room reconstruction when needed
        shouldCreateAdditionalRooms = false;
        console.log(`🔧 OLD FILE: Wall operation with existing rooms (${existingRoomCount}) and labels (${Object.keys(roomCustomLabels).length}) - preventing additional room creation`);
      } else if (isInitialLoading) {
        // During initial loading, check if rooms already exist for the custom labels
        const existingLabelKeys = Object.keys(roomCustomLabels);
        const labeledRoomsAlreadyExist = existingLabelKeys.length > 0 && existingRoomCount > 0;
        
        // Also check if any of the existing room names match the custom labels
        const existingRoomNames = rooms.map(r => r.name);
        const hasMatchingLabeledRooms = existingLabelKeys.some(labelKey => {
          const labelValue = roomCustomLabels[labelKey];
          return existingRoomNames.includes(labelValue);
        });
        
        shouldCreateAdditionalRooms = !hasExistingLabels || (!labeledRoomsAlreadyExist && !hasMatchingLabeledRooms);
        
        if (!shouldCreateAdditionalRooms) {
          console.log(`🔧 OLD FILE: Initial loading - labeled rooms already exist (${existingRoomCount} rooms, ${existingLabelKeys.length} labels) - preventing duplicate creation`);
        }
      }
      
      if (!shouldCreateAdditionalRooms) {
        
        // 🔧 TARGETED APPROACH: Allow original room reconstruction but apply filtering afterward
        // Create rooms normally but then filter the results based on context
        console.log(`🔧 OLD FILE: Context-aware filtering will be applied - creating rooms normally then filtering`);
        
        // Create rooms from unowned walls normally
        const tempRooms: Room[] = [];
        const unprocessedWalls = [...unownedWalls];
        
        for (const wall of unprocessedWalls) {
          const wallKey = `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`;
          if (processedWalls.has(wallKey)) continue;
          
          // Use safe cycle detection with limited scope to prevent merging
          const polygonWalls = findMinimalCycleFromWall(wall, unprocessedWalls.filter(w => {
            // Only consider walls within reasonable distance to prevent cross-room cycles
            const maxDistance = 500; // Adjust based on typical room sizes
            const wallCenter = { x: (wall.start.x + wall.end.x) / 2, y: (wall.start.y + wall.end.y) / 2 };
            const wCenter = { x: (w.start.x + w.end.x) / 2, y: (w.start.y + w.end.y) / 2 };
            const distance = Math.sqrt(Math.pow(wallCenter.x - wCenter.x, 2) + Math.pow(wallCenter.y - wCenter.y, 2));
            return distance <= maxDistance;
          })) || [];
          
          if (polygonWalls.length >= 3) {
            // 🔧 FIX: Generate canonical room ID first, before assigning ownership
            const center = getRoomCenter(polygonWalls);
            const stableId = getPersistentRoomId(polygonWalls, currentFloor);
            const humanReadableId = `Room ${rooms.length + tempRooms.length + 1}`;
            
            // 🏠 ASSIGN OWNERSHIP: Use canonical stableId for ownership, not human-readable name
            const wallsWithOwnership = polygonWalls.map(w => ({
              ...w,
              originatingRoomId: stableId,     // ✅ Use canonical ID that matches room.stableId
              assignedRoomIds: [stableId]      // ✅ Use canonical ID that matches room.stableId
            }));
            
            // Mark as processed
            polygonWalls.forEach((w: Wall) => {
              const key = `${w.start.x},${w.start.y}-${w.end.x},${w.end.y}`;
              processedWalls.add(key);
            });
            const customLabel = roomCustomLabels[stableId];
            const roomName = customLabel || humanReadableId;
            
            const room: Room = {
              id: stableId,
              stableId,
              name: roomName,
              walls: wallsWithOwnership,
              area: Math.abs(calculateAreaFast(wallsWithOwnership)),
              center,
              elements: [],
              isExtension: false
            };
            
            tempRooms.push(room);
            
            console.log(`🔄 OLD FILE ROOM: ${roomName} (${wallsWithOwnership.length} walls, ${room.area.toFixed(2)} m²) - Created for filtering`);
          }
        }
        
        // 🔧 CONTEXT-AWARE FILTERING: Apply intelligent filtering based on context
        if (isWallOperation && tempRooms.length > 0) {
                     // During wall operations, prefer rooms that have existing labels
           const labeledRooms = tempRooms.filter(room => {
             const hasCustomLabel = room.stableId && roomCustomLabels[room.stableId];
             return hasCustomLabel;
           });
          
          console.log(`🔧 WALL OPERATION FILTERING: Found ${tempRooms.length} total rooms, ${labeledRooms.length} labeled rooms`);
          
          // If we have labeled rooms, prefer them over unlabeled ones
          const roomsToAdd = labeledRooms.length > 0 ? labeledRooms : tempRooms;
          rooms.push(...roomsToAdd);
          
          console.log(`🔧 WALL OPERATION: Added ${roomsToAdd.length} rooms after intelligent filtering`);
        } else {
          // For other contexts, add all rooms
          rooms.push(...tempRooms);
          console.log(`🔧 CONTEXT FILTERING: Added all ${tempRooms.length} rooms (context: ${context})`);
        }
      } else {
      // Original logic for files without existing rooms/labels or when context allows room creation
      console.log(`🔧 OLD FILE: Creating rooms from unowned walls (context: ${context}, labels: ${Object.keys(roomCustomLabels).length})`);
      
      const tempRooms: Room[] = [];
      const unprocessedWalls = [...unownedWalls];
      
      for (const wall of unprocessedWalls) {
        const wallKey = `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`;
        if (processedWalls.has(wallKey)) continue;
        
        // Use safe cycle detection with limited scope to prevent merging
        const polygonWalls = findMinimalCycleFromWall(wall, unprocessedWalls.filter(w => {
          // Only consider walls within reasonable distance to prevent cross-room cycles
          const maxDistance = 500; // Adjust based on typical room sizes
          const wallCenter = { x: (wall.start.x + wall.end.x) / 2, y: (wall.start.y + wall.end.y) / 2 };
          const wCenter = { x: (w.start.x + w.end.x) / 2, y: (w.start.y + w.end.y) / 2 };
          const distance = Math.sqrt(Math.pow(wallCenter.x - wCenter.x, 2) + Math.pow(wallCenter.y - wCenter.y, 2));
          return distance <= maxDistance;
        })) || [];
        
        if (polygonWalls.length >= 3) {
          // 🛡️ PERSISTENT ROOM ID: Generate persistent ID for legacy unowned walls
          const center = getRoomCenter(polygonWalls);
          const stableId = getPersistentRoomId(polygonWalls, currentFloor);
          const humanReadableId = `Room ${rooms.length + tempRooms.length + 1}`;
          
          // 🏠 ASSIGN OWNERSHIP: Use canonical stableId for ownership, not human-readable name
          const wallsWithOwnership = polygonWalls.map(w => ({
            ...w,
            originatingRoomId: stableId,     // ✅ Use canonical ID that matches room.stableId
            assignedRoomIds: [stableId]      // ✅ Use canonical ID that matches room.stableId
          }));
          
          // Mark as processed
          polygonWalls.forEach((w: Wall) => {
            const key = `${w.start.x},${w.start.y}-${w.end.x},${w.end.y}`;
            processedWalls.add(key);
          });
          const customLabel = roomCustomLabels[stableId];
          const roomName = customLabel || humanReadableId;
          
          const room: Room = {
            id: stableId,
            stableId,
            name: roomName,
            walls: wallsWithOwnership,
            area: Math.abs(calculateAreaFast(wallsWithOwnership)),
            center,
            elements: [],
            isExtension: false
          };
          
          tempRooms.push(room);
          
          console.log(`🔄 OLD FILE ROOM: ${roomName} (${wallsWithOwnership.length} walls, ${room.area.toFixed(2)} m²) - Ownership assigned`);
        }
      }
      
      rooms.push(...tempRooms);
    }
  }
  
  console.log(`✅ WALL OWNERSHIP: Created ${rooms.length} rooms with explicit ownership`);
  return rooms;
}

// --- Room and Element Finding ---

// Note: calculateRooms and findRoomAtPoint depend on each other.
// We might need to pass calculateRooms as an argument or keep them together.
// Let's try moving findRoomAtPoint here for now.

export const findRoomAtPoint = (point: Point, rooms: Room[]): Room | null => {
   // Defensive check: ensure point parameter is valid
   if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
     console.warn('findRoomAtPoint called with invalid point:', point);
     return null;
   }

   // Defensive check: ensure rooms array is valid
   if (!rooms || !Array.isArray(rooms)) {
     console.warn('findRoomAtPoint called with invalid rooms array:', rooms);
     return null;
   }

   // First check bounding boxes for quick exclusion
   const potentiallyContainingRooms = rooms.filter(room => {
     // Defensive check: ensure room has valid walls array
     if (!room || !room.walls || !Array.isArray(room.walls) || room.walls.length === 0) {
       return false;
     }
     
     const bounds = getRoomBounds(room.walls);
     // Defensive check: ensure bounds is valid
     if (!bounds || typeof bounds.minX !== 'number' || typeof bounds.maxX !== 'number' || 
         typeof bounds.minY !== 'number' || typeof bounds.maxY !== 'number') {
       return false;
     }
     
     return point.x >= bounds.minX && point.x <= bounds.maxX &&
            point.y >= bounds.minY && point.y <= bounds.maxY;
   });

   // First try: precise point-in-polygon test for points strictly inside
   const roomStrictlyContaining = potentiallyContainingRooms.find(room => {
     // Additional defensive check for walls validity
     if (!room.walls || !Array.isArray(room.walls)) return false;
     return isPointInsideRoom(point, room.walls);
   });
   if (roomStrictlyContaining) return roomStrictlyContaining;

   // Second try: check if point is near any wall boundary (for elements placed on walls)
   const WALL_BOUNDARY_TOLERANCE = 15; // Tolerance for wall-mounted elements like windows/radiators
   
   return potentiallyContainingRooms.find(room => {
     // Additional defensive check for walls validity
     if (!room.walls || !Array.isArray(room.walls)) return false;
     
     return room.walls.some(wall => {
       // Defensive check: ensure wall has valid start/end points
       if (!wall || !wall.start || !wall.end || 
           typeof wall.start.x !== 'number' || typeof wall.start.y !== 'number' ||
           typeof wall.end.x !== 'number' || typeof wall.end.y !== 'number') {
         return false;
       }
       
       const distanceToWall = distanceToLine(point, wall.start, wall.end);
       
       // Check if point is close to this wall
       if (distanceToWall <= WALL_BOUNDARY_TOLERANCE) {
         // Also check if the point projection falls on the wall segment (not extending beyond it)
         const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
         if (wallLength === 0) return false;
         
         const t = ((point.x - wall.start.x) * (wall.end.x - wall.start.x) + 
                   (point.y - wall.start.y) * (wall.end.y - wall.start.y)) / (wallLength * wallLength);
         
         return t >= 0 && t <= 1; // Point projection is on the wall segment
       }
       return false;
     });
   }) || null;
};

export const findElementAtPoint = (point: Point, elements: FloorPlanElement[], currentFloor: number, interactionScale?: number): FloorPlanElement | null => {
  // Filter elements to current floor first
  const elementsOnCurrentFloor = elements.filter(element => {
    const el = element as any;
    return el.floor === currentFloor || el.floor === undefined; // Include elements without floor specified
  });

  // Check each element to see if the point is within its bounds
  // Use touch-friendly bounds by passing the interaction scale
  for (const element of elementsOnCurrentFloor) {
    const bounds = getElementBounds(element, undefined, interactionScale);
    
    if (point.x >= bounds.minX && point.x <= bounds.maxX &&
        point.y >= bounds.minY && point.y <= bounds.maxY) {
      return element;
    }
  }
  
  return null;
};

// --- Resize Handle Logic ---

export const getDistanceToHandleCenter = (point: Point, handleCenter: Point): number => {
    return Math.hypot(point.x - handleCenter.x, point.y - handleCenter.y);
};

export const getResizeHandle = (point: Point, bounds: { minX: number; maxX: number; minY: number; maxY: number }): string | null => {
    const handleSize = RESIZE_HANDLE_HOVER_SIZE; // This will now be found
    const { minX, maxX, minY, maxY } = bounds;
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;

    const handles = [
      { id: 'nw', center: { x: minX, y: minY } }, { id: 'n', center: { x: midX, y: minY } }, { id: 'ne', center: { x: maxX, y: minY } },
      { id: 'w', center: { x: minX, y: midY } }, { id: 'e', center: { x: maxX, y: midY } },
      { id: 'sw', center: { x: minX, y: maxY } }, { id: 's', center: { x: midX, y: maxY } }, { id: 'se', center: { x: maxX, y: maxY } }
    ];

    let closestHandle = null;
    let minDistance = handleSize;

    handles.forEach(handle => {
      const distance = getDistanceToHandleCenter(point, handle.center);
      if (distance < minDistance) {
        minDistance = distance;
        closestHandle = handle.id;
      }
    });

    return closestHandle;
};

// Helper to check if two points are close (already in your utils or similar)
export const pointsAreEqual = (p1: Point, p2: Point, tolerance = 0.1): boolean => {
  // 🔧 COORDINATE FIX: Use normalized coordinates for consistent comparison
  const normalizedP1X = Math.round(p1.x * 100) / 100;
  const normalizedP1Y = Math.round(p1.y * 100) / 100;
  const normalizedP2X = Math.round(p2.x * 100) / 100;
  const normalizedP2Y = Math.round(p2.y * 100) / 100;
  
  return Math.abs(normalizedP1X - normalizedP2X) <= tolerance && 
         Math.abs(normalizedP1Y - normalizedP2Y) <= tolerance;
};

// 🛡️ PERSISTENT ROOM ID SYSTEM: Global room counter and ID registry for truly stable IDs
let globalRoomCounter = 1;
const roomIdRegistry = new Map<string, string>(); // coordinateSignature -> persistentId

/**
 * 🔄 RESET ROOM ID SYSTEM: Call this when creating/loading a new file
 * Resets counter to 1 and clears registry so new files start with room_1
 */
export const resetRoomIdSystem = (): void => {
  globalRoomCounter = 1;
  roomIdRegistry.clear();
  console.log('🔄 ROOM_ID_SYSTEM_RESET', {
    message: 'Counter reset to 1, registry cleared for new file',
    newCounterValue: globalRoomCounter,
    registrySize: roomIdRegistry.size
  });
};

/**
 * 🛡️ COORDINATE SIGNATURE: Generate a signature based on room geometry for matching
 */
function generateRoomCoordinateSignature(walls: Wall[], floor?: number): string {
  if (!walls || walls.length === 0) return `empty_${Date.now()}`;
  
  // Extract floor number from walls if not provided
  let effectiveFloor = floor;
  if (effectiveFloor === undefined) {
    const wallWithFloor = walls.find(w => w.floor !== undefined);
    if (wallWithFloor) {
      effectiveFloor = wallWithFloor.floor;
    }
  }
  
  const normalizedWallStrings = walls.map(w => {
    const p1 = w.start;
    const p2 = w.end;
    const s1 = `${p1.x.toFixed(2)},${p1.y.toFixed(2)}`;
    const s2 = `${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
    return s1 < s2 ? `${s1}-${s2}` : `${s2}-${s1}`;
  });
  const wallsId = normalizedWallStrings.sort().join(';');
  
  const floorPrefix = effectiveFloor !== undefined ? `floor_${effectiveFloor}_` : 'floor_unknown_';
  return `${floorPrefix}${wallsId}`;
}

/**
 * 🛡️ PERSISTENT ROOM ID: Generate or retrieve a persistent ID that never changes
 */
export const getPersistentRoomId = (walls: Wall[], floor?: number, existingId?: string): string => {
  // If we already have a persistent ID, use it
  if (existingId && existingId.startsWith('room_')) {
    console.log('🛡️ PERSISTENT_ID_REUSED', { existingId, wallCount: walls.length });
    return existingId;
  }
  
  // Generate coordinate signature for this room geometry
  const coordinateSignature = generateRoomCoordinateSignature(walls, floor);
  
  // Check if we already have a persistent ID for this coordinate signature
  if (roomIdRegistry.has(coordinateSignature)) {
    const persistentId = roomIdRegistry.get(coordinateSignature)!;
    console.log('🛡️ PERSISTENT_ID_FOUND', { persistentId, coordinateSignature: coordinateSignature.substring(0, 50) + '...' });
    return persistentId;
  }
  
  // Create a new persistent ID
  const newPersistentId = `room_${globalRoomCounter++}`;
  roomIdRegistry.set(coordinateSignature, newPersistentId);
  
  console.log('🛡️ PERSISTENT_ID_CREATED', { 
    newPersistentId, 
    coordinateSignature: coordinateSignature.substring(0, 50) + '...', 
    globalCounter: globalRoomCounter - 1 
  });
  
  return newPersistentId;
};


/**
 * 🛡️ ROOM ID MIGRATION: Update wall ownership to use new persistent room IDs
 */
export const migrateWallOwnershipToPersistentIds = (
  walls: Wall[], 
  oldId: string, 
  newId: string
): Wall[] => {
  return walls.map(wall => {
    const hasUpdates = {
      originatingRoomId: false,
      assignedRoomIds: false,
      parentRoomIds: false
    };

    const updatedWall = { ...wall };

    // Update originatingRoomId
    if ((wall as any).originatingRoomId === oldId) {
      (updatedWall as any).originatingRoomId = newId;
      hasUpdates.originatingRoomId = true;
    }

    // Update assignedRoomIds array
    if ((wall as any).assignedRoomIds?.includes(oldId)) {
      (updatedWall as any).assignedRoomIds = (wall as any).assignedRoomIds.map((id: string) => 
        id === oldId ? newId : id
      );
      hasUpdates.assignedRoomIds = true;
    }

    // Update parentRoomIds array
    if ((wall as any).parentRoomIds?.includes(oldId)) {
      (updatedWall as any).parentRoomIds = (wall as any).parentRoomIds.map((id: string) => 
        id === oldId ? newId : id
      );
      hasUpdates.parentRoomIds = true;
    }

    // Log if any updates were made
    if (hasUpdates.originatingRoomId || hasUpdates.assignedRoomIds || hasUpdates.parentRoomIds) {
      console.log('🛡️ WALL_OWNERSHIP_MIGRATED', {
        wallId: (wall as any).tempId,
        oldId: oldId.substring(0, 30) + '...',
        newId,
        updates: hasUpdates
      });
    }

    return updatedWall;
  });
};

export type RoomCalculationContext = 
  | 'explicitRoomCreation' 
  | 'geometryUpdate' 
  | 'labelPreservation'
  | 'wallTypeModification' // Added new context type
  | 'positioningRoomAdjustment'; // Prevent room merging during AR room positioning

// Helper function to detect and fix missing parent wall IDs for legacy extrusion connectors
const fixLegacyExtrusionConnectors = (walls: Wall[]): Wall[] => {
  const extrusionConnectors = walls.filter(wall => (wall as any).isExtrusionConnector);
  const splitWalls = walls.filter(wall => (wall as any).isSplitOrigin);
  const mainSegments = walls.filter(wall => (wall as any).isMainExtrusionSegment);
  
  console.log(`🔧 LEGACY FIX: Analyzing ${walls.length} walls - found ${extrusionConnectors.length} extrusion connectors, ${splitWalls.length} split walls, and ${mainSegments.length} main segments`);
  
  // 🔧 ENHANCED: Skip legacy fix if extrusion connectors already have proper parentRoomIds
  const connectorsWithParentRoomIds = extrusionConnectors.filter(connector => (connector as any).parentRoomIds && (connector as any).parentRoomIds.length > 0);
  
  if (connectorsWithParentRoomIds.length > 0) {
    console.log(`🔧 LEGACY FIX: SKIPPING - Found ${connectorsWithParentRoomIds.length} extrusion connectors with proper parentRoomIds, legacy fix not needed`);
    return walls; // Skip legacy fix entirely when proper parent room IDs exist
  }
  
  // If no extrusion connectors or all already have parent wall IDs, no need to fix
  const connectorsNeedingFix = extrusionConnectors.filter(connector => !(connector as any).parentWallId);
  if (connectorsNeedingFix.length === 0) {
    if (extrusionConnectors.length > 0) {
      console.log(`🔧 LEGACY FIX: All ${extrusionConnectors.length} extrusion connectors already have parent wall IDs`);
    }
    return walls;
  }
  
  console.log(`🔧 LEGACY FIX: Found ${connectorsNeedingFix.length} extrusion connectors without parent wall IDs`);
  
  // First pass: Fix extrusion connectors
  const wallsWithFixedConnectors = walls.map(wall => {
    if (!(wall as any).isExtrusionConnector || (wall as any).parentWallId) {
      return wall; // Not an extrusion connector or already has parent wall ID
    }
    
    console.log(`🔧 LEGACY FIX: Processing extrusion connector`, {
      start: wall.start,
      end: wall.end,
      type: wall.type
    });
    
    // Try to find the split wall this connector is connected to
    const connectedSplitWall = splitWalls.find(splitWall => {
      // Check if the connector is connected to either endpoint of the split wall
      return pointsAreEqual(wall.start, splitWall.start, 0.1) || 
             pointsAreEqual(wall.start, splitWall.end, 0.1) ||
             pointsAreEqual(wall.end, splitWall.start, 0.1) || 
             pointsAreEqual(wall.end, splitWall.end, 0.1);
    });
    
    if (connectedSplitWall && (connectedSplitWall as any).parentWallId) {
      // Inherit the parent wall ID from the connected split wall
      console.log(`🔧 LEGACY FIX: Assigning parent wall ID to extrusion connector`, {
        connectorStart: wall.start,
        connectorEnd: wall.end,
        parentWallId: (connectedSplitWall as any).parentWallId
      });
      
      return {
        ...wall,
        parentWallId: (connectedSplitWall as any).parentWallId
      };
    }
    
    // If we can't find a connected split wall with parent ID, try to infer from geometry
    // Look for walls that could be the "main segment" this connector extends from
    const potentialMainSegments = walls.filter(w => {
      if ((w as any).isExtrusionConnector || (w as any).isSplitOrigin) return false;
      
      // Check if this wall is collinear and connected to the connector
      const isConnected = pointsAreEqual(wall.start, w.start, 0.1) || 
                         pointsAreEqual(wall.start, w.end, 0.1) ||
                         pointsAreEqual(wall.end, w.start, 0.1) || 
                         pointsAreEqual(wall.end, w.end, 0.1);
      
      if (!isConnected) return false;
      
      // Check if they're roughly collinear (same direction)
      const connectorVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
      const wallVector = { x: w.end.x - w.start.x, y: w.end.y - w.start.y };
      
      // Normalize vectors
      const connectorLength = Math.hypot(connectorVector.x, connectorVector.y);
      const wallLength = Math.hypot(wallVector.x, wallVector.y);
      
      if (connectorLength === 0 || wallLength === 0) return false;
      
      const connectorNorm = { x: connectorVector.x / connectorLength, y: connectorVector.y / connectorLength };
      const wallNorm = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
      
      // Check if vectors are parallel (dot product close to 1 or -1)
      const dotProduct = Math.abs(connectorNorm.x * wallNorm.x + connectorNorm.y * wallNorm.y);
      return dotProduct > 0.9; // Roughly parallel
    });
    
    if (potentialMainSegments.length > 0) {
      // Use the first potential main segment to generate a parent wall ID
      const mainSegment = potentialMainSegments[0];
      const inferredParentWallId = `legacy-${mainSegment.start.x.toFixed(1)}-${mainSegment.start.y.toFixed(1)}-${mainSegment.end.x.toFixed(1)}-${mainSegment.end.y.toFixed(1)}`;
      
      console.log(`🔧 LEGACY FIX: Inferring parent wall ID for extrusion connector`, {
        connectorStart: wall.start,
        connectorEnd: wall.end,
        inferredParentWallId,
        mainSegmentStart: mainSegment.start,
        mainSegmentEnd: mainSegment.end
      });
      
      return {
        ...wall,
        parentWallId: inferredParentWallId
      };
    }
    
    // If all else fails, generate a unique parent wall ID based on the connector's position
    const fallbackParentWallId = `legacy-connector-${wall.start.x.toFixed(1)}-${wall.start.y.toFixed(1)}-${wall.end.x.toFixed(1)}-${wall.end.y.toFixed(1)}`;
    
    console.log(`🔧 LEGACY FIX: Using fallback parent wall ID for extrusion connector`, {
      connectorStart: wall.start,
      connectorEnd: wall.end,
      fallbackParentWallId
    });
    
    return {
      ...wall,
      parentWallId: fallbackParentWallId
    };
  });
  
  // Second pass: Propagate parent wall IDs to connected split walls and main segments
  const fixedConnectors = wallsWithFixedConnectors.filter(wall => (wall as any).isExtrusionConnector && (wall as any).parentWallId);
  console.log(`🔧 LEGACY FIX: Successfully fixed ${fixedConnectors.length} extrusion connectors with parent wall IDs`);
  
  // Now propagate parent wall IDs to connected split walls and main segments
  const finalWalls = wallsWithFixedConnectors.map(wall => {
    // Skip if already has parent wall ID or is an extrusion connector
    if ((wall as any).parentWallId || (wall as any).isExtrusionConnector) {
      return wall;
    }
    
    // Find connected extrusion connectors that have parent wall IDs
    const connectedConnectors = fixedConnectors.filter(connector => {
      return pointsAreEqual(wall.start, connector.start, 0.1) || 
             pointsAreEqual(wall.start, connector.end, 0.1) ||
             pointsAreEqual(wall.end, connector.start, 0.1) || 
             pointsAreEqual(wall.end, connector.end, 0.1);
    });
    
    if (connectedConnectors.length > 0) {
      // Use the parent wall ID from the first connected connector
      const parentWallId = (connectedConnectors[0] as any).parentWallId;
      console.log(`🔧 LEGACY FIX: Propagating parent wall ID to ${(wall as any).isSplitOrigin ? 'split wall' : (wall as any).isMainExtrusionSegment ? 'main segment' : 'wall'}`, {
        wallStart: wall.start,
        wallEnd: wall.end,
        wallType: wall.type,
        parentWallId
      });
      
      return {
        ...wall,
        parentWallId
      };
    }
    
    return wall;
  });
  
  const totalFixed = finalWalls.filter(wall => (wall as any).parentWallId).length;
  console.log(`🔧 LEGACY FIX: Total walls with parent wall IDs: ${totalFixed}`);
  
  return finalWalls;
};

export const calculateRooms = (
  walls: Wall[], 
  roomCustomLabels: Record<string, string> = {},
  context: RoomCalculationContext = 'geometryUpdate',
  currentFloor?: number,
  creationContext?: {
    isFromEndpoint?: boolean;
    lastRoomCount?: number;
    expectedNewRooms?: number;
    originalRoomCount?: number;
    newWalls?: Wall[];
  }
): Room[] => {
  console.log('🔍 CALCULATE_ROOMS ENTRY:', {
    wallCount: walls?.length || 0,
    context,
    currentFloor,
    hasRoomCustomLabels: Object.keys(roomCustomLabels).length > 0,
    roomLabelKeys: Object.keys(roomCustomLabels)
  });
  
  if (!walls || walls.length === 0) {
    console.log('❌ CALCULATE_ROOMS: No walls, returning empty');
    return [];
  }

  console.log('🚀 CALCULATE_ROOMS: Calling calculateRoomsInternal...');
  
  // Always use legacy room calculation to preserve existing rooms and prevent merging
  const result = calculateRoomsInternal(walls, roomCustomLabels, context, currentFloor, creationContext);
  
  console.log('📊 CALCULATE_ROOMS RESULT:', {
    roomCount: result.length,
    roomNames: result.map(r => r.name)
  });
  
  return result;
};



/**
 * Create a simple room from a set of walls without complex cycle detection
 */
function createSimpleRoom(walls: Wall[], roomCustomLabels: Record<string, string>, currentFloor?: number): Room | null {
  if (walls.length < 3) return null;
  
  try {
    const area = calculateAreaFast(walls);
    if (area <= 0) return null;
    
    const center = getRoomCenter(walls);
    const stableId = getPersistentRoomId(walls, currentFloor);
    
    // Check if there's a custom label for this room
    const customLabel = roomCustomLabels[stableId];
    const roomName = customLabel || `Room ${Math.floor(Math.random() * 1000) + 1}`;
    
    return {
      id: stableId,
      stableId,
      name: roomName,
      walls: [...walls], // Create a copy
      area,
      center,
      elements: [],
      isExtension: false
    };
  } catch (error) {
    console.warn('🔧 SIMPLE ROOM CREATION: Error creating room', error);
    return null;
  }
}

/**
 * Find minimal new rooms conservatively without merging existing rooms
 */
function findMinimalNewRoomsConservatively(
  newWalls: Wall[],
  existingWalls: Wall[],
  roomCustomLabels: Record<string, string>,
  currentFloor?: number
): Room[] {
  console.log('🔧 CONSERVATIVE ROOM FINDING: Attempting to find minimal new rooms', {
    newWallCount: newWalls.length,
    existingWallCount: existingWalls.length
  });
  
  if (newWalls.length === 0) return [];
  
  const potentialRooms: Room[] = [];
  
  try {
    // Strategy: Try to find the smallest possible cycles that include at least one new wall
    // but don't merge existing room boundaries
    
    // For each new wall, try to find a minimal cycle that includes it
    for (const newWall of newWalls) {
      const minimalCycle = findMinimalCycleFromWall(newWall, [...newWalls, ...existingWalls]);
      
      if (minimalCycle && minimalCycle.length >= 3) {
        // Ensure this cycle includes at least one new wall
        const includesNewWall = minimalCycle.some(cycleWall => 
          newWalls.some(nw => 
            nw.start.x === cycleWall.start.x && nw.start.y === cycleWall.start.y &&
            nw.end.x === cycleWall.end.x && nw.end.y === cycleWall.end.y
          )
        );
        
        if (includesNewWall) {
          const room = createSimpleRoom(minimalCycle, roomCustomLabels, currentFloor);
          if (room && room.area > ROOM_CALC_CONFIG.MIN_ROOM_AREA_THRESHOLD) {
            // Check if we already have this room (avoid duplicates)
            const isDuplicate = potentialRooms.some(existing => existing.stableId === room.stableId);
            if (!isDuplicate) {
              potentialRooms.push(room);
            }
          }
        }
      }
    }
  } catch (error) {
    console.warn('🔧 CONSERVATIVE ROOM FINDING: Error in minimal room detection', error);
  }
  
  console.log('🔧 CONSERVATIVE ROOM FINDING: Found potential rooms', {
    roomCount: potentialRooms.length,
    rooms: potentialRooms.map(r => ({
      name: r.name,
      area: r.area.toFixed(2),
      wallCount: r.walls.length
    }))
  });
  
  return potentialRooms;
}

/**
 * Find a minimal cycle starting from a specific wall
 */
function findMinimalCycleFromWall(startWall: Wall, allWalls: Wall[]): Wall[] | null {
  // This is a simplified cycle finding that tries to find the smallest possible cycle
  // starting from the given wall without doing complex graph traversal
  
  const visited = new Set<string>();
  const cycle: Wall[] = [startWall];
  
  const wallKey = (wall: Wall) => `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`;
  visited.add(wallKey(startWall));
  
  let currentEndPoint = startWall.end;
  const targetStartPoint = startWall.start;
  
  // Try to find a path back to the start point
  for (let depth = 0; depth < 10; depth++) { // Limit depth to prevent infinite loops
    let foundConnection = false;
    
    for (const wall of allWalls) {
      const key = wallKey(wall);
      if (visited.has(key)) continue;
      
      // Check if this wall connects to our current end point
      const tolerance = 0.1;
      let connectsAtStart = Math.abs(wall.start.x - currentEndPoint.x) < tolerance && 
                           Math.abs(wall.start.y - currentEndPoint.y) < tolerance;
      let connectsAtEnd = Math.abs(wall.end.x - currentEndPoint.x) < tolerance && 
                         Math.abs(wall.end.y - currentEndPoint.y) < tolerance;
      
      if (connectsAtStart) {
        cycle.push(wall);
        visited.add(key);
        currentEndPoint = wall.end;
        foundConnection = true;
        
        // Check if we've completed the cycle
        if (Math.abs(currentEndPoint.x - targetStartPoint.x) < tolerance && 
            Math.abs(currentEndPoint.y - targetStartPoint.y) < tolerance) {
          return cycle;
        }
        break;
      } else if (connectsAtEnd) {
        // Create a reversed wall to maintain direction
        const reversedWall: Wall = {
          start: wall.end,
          end: wall.start,
          type: wall.type,
          thickness: wall.thickness,
          floor: wall.floor
        };
        
        cycle.push(reversedWall);
        visited.add(key);
        currentEndPoint = wall.start;
        foundConnection = true;
        
        // Check if we've completed the cycle
        if (Math.abs(currentEndPoint.x - targetStartPoint.x) < tolerance && 
            Math.abs(currentEndPoint.y - targetStartPoint.y) < tolerance) {
          return cycle;
        }
        break;
      }
    }
    
    if (!foundConnection) {
      break; // No more connections found
    }
  }
  
  return null; // No cycle found
}



// Helper function to check if one room is entirely contained within another
const isRoomContainedInAnother = (innerWalls: Wall[], outerWalls: Wall[]): boolean => {
  try {
    // Get center points of all walls in the inner room
    const innerPoints = innerWalls.flatMap(wall => [wall.start, wall.end]);
    const uniqueInnerPoints = innerPoints.filter((point, index, array) => 
      array.findIndex(p => pointsAreEqual(p, point, 0.1)) === index
    );
    
    // Check if all inner room points are inside the outer room
    const allPointsInside = uniqueInnerPoints.every(point => {
      try {
        return isPointInsideRoom(point, outerWalls);
      } catch (error) {
        // If point-in-polygon fails, use bounding box check as fallback
        const outerBounds = getRoomBounds(outerWalls);
        return point.x >= outerBounds.minX && point.x <= outerBounds.maxX && 
               point.y >= outerBounds.minY && point.y <= outerBounds.maxY;
      }
    });
    
    return allPointsInside;
  } catch (error) {
    // If containment check fails, return false
    return false;
  }
};

// Helper function to calculate boundary overlap between two rooms
const calculateBoundaryOverlap = (walls1: Wall[], walls2: Wall[]): number => {
  try {
    let totalOverlapLength = 0;
    let totalBoundaryLength = 0;
    
    // Calculate total boundary length of the smaller room
    const room1Length = walls1.reduce((sum, wall) => sum + calculateWallLength(wall.start, wall.end), 0);
    const room2Length = walls2.reduce((sum, wall) => sum + calculateWallLength(wall.start, wall.end), 0);
    totalBoundaryLength = Math.min(room1Length, room2Length);
    
    // Check for overlapping wall segments
    for (const wall1 of walls1) {
      for (const wall2 of walls2) {
        const overlap = calculateWallOverlap(wall1, wall2);
        totalOverlapLength += overlap;
      }
    }
    
    return totalBoundaryLength > 0 ? totalOverlapLength / totalBoundaryLength : 0;
  } catch (error) {
    return 0;
  }
};

// Helper function to calculate overlap between two wall segments
const calculateWallOverlap = (wall1: Wall, wall2: Wall): number => {
  try {
    // Check if walls are collinear
    if (!areWallsCollinear(wall1, wall2)) {
      return 0;
    }
    
    // Project walls onto a line to find overlap
    const wall1Start = wall1.start;
    const wall1End = wall1.end;
    const wall2Start = wall2.start;
    const wall2End = wall2.end;
    
    // Use X-axis projection for near-vertical walls, Y-axis for near-horizontal
    const wall1IsVertical = Math.abs(wall1End.x - wall1Start.x) < Math.abs(wall1End.y - wall1Start.y);
    
    let wall1Min, wall1Max, wall2Min, wall2Max;
    
    if (wall1IsVertical) {
      wall1Min = Math.min(wall1Start.y, wall1End.y);
      wall1Max = Math.max(wall1Start.y, wall1End.y);
      wall2Min = Math.min(wall2Start.y, wall2End.y);
      wall2Max = Math.max(wall2Start.y, wall2End.y);
    } else {
      wall1Min = Math.min(wall1Start.x, wall1End.x);
      wall1Max = Math.max(wall1Start.x, wall1End.x);
      wall2Min = Math.min(wall2Start.x, wall2End.x);
      wall2Max = Math.max(wall2Start.x, wall2End.x);
    }
    
    // Calculate overlap
    const overlapMin = Math.max(wall1Min, wall2Min);
    const overlapMax = Math.min(wall1Max, wall2Max);
    
    return overlapMax > overlapMin ? overlapMax - overlapMin : 0;
  } catch (error) {
    return 0;
  }
};

// Helper function to check if two walls are collinear
const areWallsCollinear = (wall1: Wall, wall2: Wall, tolerance: number = 0.1): boolean => {
  try {
    // Calculate direction vectors
    const dir1 = { x: wall1.end.x - wall1.start.x, y: wall1.end.y - wall1.start.y };
    const dir2 = { x: wall2.end.x - wall2.start.x, y: wall2.end.y - wall2.start.y };
    
    // Check if directions are parallel (cross product near zero)
    const cross = Math.abs(dir1.x * dir2.y - dir1.y * dir2.x);
    
    // Normalize by the lengths to handle different wall lengths
    const len1 = Math.sqrt(dir1.x * dir1.x + dir1.y * dir1.y);
    const len2 = Math.sqrt(dir2.x * dir2.x + dir2.y * dir2.y);
    
    if (len1 < tolerance || len2 < tolerance) return false;
    
    const normalizedCross = cross / (len1 * len2);
    
    // Check if walls are parallel
    if (normalizedCross > tolerance) return false;
    
    // Check if one wall's point lies on the line defined by the other wall
    const pointDistance = distanceToLine(wall1.start, wall2.start, wall2.end);
    
    return pointDistance < tolerance;
  } catch (error) {
    return false;
  }
};

// Internal function that does the actual room calculation
const calculateRoomsInternal = (
  walls: Wall[], 
  roomCustomLabels: Record<string, string> = {},
  context: RoomCalculationContext = 'geometryUpdate',
  currentFloor?: number,
  // 🔧 NEW: Room creation context for better filtering
  creationContext?: {
    isFromEndpoint?: boolean;
    lastRoomCount?: number;
    expectedNewRooms?: number;
    originalRoomCount?: number; // Track original room count for endpoint creation
    newWalls?: Wall[]; // Track newly added walls for endpoint creation
  }
): Room[] => {
  
  // 🔧 SIMPLE ROOM DETECTION: Back to working approach
  // Focus on fixing the specific partial connection issue without breaking everything
  console.log('🔧 SIMPLE ROOM DETECTION: Using proven wall assignment approach');
  
  try {
    const rooms = createRoomsWithWallAssignment(walls, roomCustomLabels, currentFloor, context);
    console.log(`✅ SIMPLE DETECTION: Found ${rooms.length} rooms`);
    return rooms;
  } catch (error) {
    console.warn('❌ SIMPLE DETECTION: Error, returning empty rooms', error);
    return [];
  }
  
  // 🔧 LEGACY FIX: DISABLED to prevent stable ID corruption during file loading
  // The legacy fix modifies wall coordinates which breaks room label mapping
  const wallsWithFixedParentIds = walls; // Skip legacy fix to preserve stable IDs

  // 🔧 CONDITIONAL LEGACY FIX: Only apply minimal fixes when absolutely necessary
  // Check if we have extrusion connectors without parent room IDs that would break room calculation
  const extrusionConnectors = walls.filter(w => (w as any).isExtrusionConnector);
  const connectorsWithoutParentRoomIds = extrusionConnectors.filter(w => 
    !(w as any).parentRoomIds || !Array.isArray((w as any).parentRoomIds) || (w as any).parentRoomIds.length === 0
  );
  
  let wallsToUse = walls;
  if (connectorsWithoutParentRoomIds.length > 0 && context !== 'geometryUpdate') {
    console.log(`🔧 CONDITIONAL LEGACY FIX: Found ${connectorsWithoutParentRoomIds.length} extrusion connectors without parent room IDs - applying minimal fix`);
    
    // Apply minimal fix that preserves coordinates but adds missing parent room IDs
    wallsToUse = walls.map(wall => {
      if (!(wall as any).isExtrusionConnector || (wall as any).parentRoomIds) {
        return wall; // Don't modify non-extrusion walls or walls that already have parent room IDs
      }
      
      // Find connected split walls to inherit parent room IDs from
      const splitWalls = walls.filter(w => (w as any).isSplitOrigin);
      const connectedSplitWall = splitWalls.find(splitWall => {
        return pointsAreEqual(wall.start, splitWall.start, 0.1) || 
               pointsAreEqual(wall.start, splitWall.end, 0.1) ||
               pointsAreEqual(wall.end, splitWall.start, 0.1) || 
               pointsAreEqual(wall.end, splitWall.end, 0.1);
      });
      
      if (connectedSplitWall && (connectedSplitWall as any).parentRoomIds) {
        console.log(`🔧 CONDITIONAL LEGACY FIX: Inheriting parent room IDs for extrusion connector`, {
          connectorCoords: `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`,
          inheritedParentRoomIds: (connectedSplitWall as any).parentRoomIds
        });
        return {
          ...wall,
          parentRoomIds: (connectedSplitWall as any).parentRoomIds
        };
      }
      
      return wall; // Return unchanged if we can't find parent room IDs to inherit
    });
  }

  // 🚀 PERFORMANCE: Early exit for overly complex floor plans
  if (wallsToUse.length > ROOM_CALC_CONFIG.MAX_WALLS_FOR_FULL_DETECTION) {
    console.warn(`⚠️ PERFORMANCE: Skipping room detection for ${wallsToUse.length} walls (>${ROOM_CALC_CONFIG.MAX_WALLS_FOR_FULL_DETECTION}). Consider simplifying floor plan.`);
    return [];
  }

  // 🚀 PERFORMANCE: Clear point cache at start of calculation
  if (ROOM_CALC_CONFIG.ENABLE_POINT_CACHING) {
    clearPointStringCache();
  }

  // 🔧 ENHANCED EXCURSION FIX: Include ALL walls in room boundary detection
  // Extrusion connectors are ESSENTIAL for forming the complete room boundary
  const roomBoundaryWalls = wallsToUse.filter(wall => {
    // Include all walls - extrusion connectors are needed to form complete room boundaries
    return true;
  });

  // 🔧 ENHANCED EXCURSION FIX: All walls are now included for proper room boundary detection
  console.log(`🏠 ENHANCED EXCURSION: Using ${roomBoundaryWalls.length} walls for room detection (including extrusion connectors for complete boundaries)`);

  // Build adjacency list for room boundary walls
  const adj = new Map<string, { point: Point, walls: Wall[] }>();
  const pointToString = pointToStringCached; // Use cached version
  
  roomBoundaryWalls.forEach(wall => {
    const startStr = pointToString(wall.start);
    const endStr = pointToString(wall.end);
    
    // Add start point
    if (!adj.has(startStr)) {
      adj.set(startStr, { point: wall.start, walls: [] });
    }
    adj.get(startStr)!.walls.push(wall);
    
    // Add end point
    if (!adj.has(endStr)) {
      adj.set(endStr, { point: wall.end, walls: [] });
    }
    adj.get(endStr)!.walls.push(wall);
  });

  // 🔧 DEBUG: Log adjacency information for wall split debugging
  if (context === 'labelPreservation') {
    console.log('🔧 WALL SPLIT DEBUG: Adjacency list analysis', {
      totalWalls: roomBoundaryWalls.length,
      totalNodes: adj.size,
      splitWallsDetected: roomBoundaryWalls.filter(w => (w as any).isSplitOrigin).length
    });
    
    // Log detailed node connections (DISABLED - too verbose)
    // console.log('🔧 WALL SPLIT DEBUG: Detailed node connections:');
    // Array.from(adj.entries()).forEach(([nodeStr, nodeData]) => {
    //   console.log(`  Node ${nodeStr} (${nodeData.point.x.toFixed(1)}, ${nodeData.point.y.toFixed(1)}):`, {
    //     connectionCount: nodeData.walls.length,
    //     connectedWalls: nodeData.walls.map(w => ({
    //       wall: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
    //       isSplit: (w as any).isSplitOrigin || false,
    //       type: w.type
    //     }))
    //   });
    // });
    
    // Check for disconnected nodes (nodes with only 1 connection)
    const disconnectedNodes = Array.from(adj.entries()).filter(([nodeStr, nodeData]) => nodeData.walls.length === 1);
    if (disconnectedNodes.length > 0) {
      console.log('🔧 WALL SPLIT DEBUG: Found disconnected nodes (dead ends)', {
        disconnectedNodesCount: disconnectedNodes.length,
        disconnectedNodes: disconnectedNodes.map(([nodeStr, nodeData]) => ({
          node: nodeStr,
          point: nodeData.point,
          connectedWall: `(${nodeData.walls[0].start.x},${nodeData.walls[0].start.y})->(${nodeData.walls[0].end.x},${nodeData.walls[0].end.y})`
        }))
      });
    }
    
    // Check for nodes with unusual connection counts
    const unusualNodes = Array.from(adj.entries()).filter(([nodeStr, nodeData]) => nodeData.walls.length > 4);
    if (unusualNodes.length > 0) {
      console.log('🔧 WALL SPLIT DEBUG: Found nodes with >4 connections', {
        unusualNodesCount: unusualNodes.length,
        unusualNodes: unusualNodes.map(([nodeStr, nodeData]) => ({
          node: nodeStr,
          point: nodeData.point,
          connectionCount: nodeData.walls.length
        }))
      });
    }
  }

  const foundRoomWallSets: { walls: Wall[]; area: number }[] = [];
  const globalVisitedEdges = new Set<string>(); // Global set to prevent re-exploring the same cycles

  const generateEdgeId = (p1: Point, p2: Point) => {
    const s1 = pointToString(p1);
    const s2 = pointToString(p2);
    return s1 < s2 ? `${s1}_${s2}` : `${s2}_${s1}`;
  };
  
  /**
   * Intelligent room merging logic that distinguishes between true excursions and adjacent rooms
   */
  const shouldMergeRoomsBasedOnGeometry = (
    roomData1: { walls: Wall[]; area: number },
    roomData2: { walls: Wall[]; area: number },
    context: RoomCalculationContext,
    creationContext?: {
      isFromEndpoint?: boolean;
      lastRoomCount?: number;
      expectedNewRooms?: number;
    }
  ): boolean => {
    const room1Center = getRoomCenter(roomData1.walls);
    const room2Center = getRoomCenter(roomData2.walls);
    const centerDistance = Math.hypot(room1Center.x - room2Center.x, room1Center.y - room2Center.y);
    
    // Calculate area ratio
    const areaRatio = Math.min(roomData1.area, roomData2.area) / Math.max(roomData1.area, roomData2.area);
    
    // Calculate wall overlap
    const room1EdgeIds = new Set(roomData1.walls.map(w => generateEdgeId(w.start, w.end)));
    const room2EdgeIds = new Set(roomData2.walls.map(w => generateEdgeId(w.start, w.end)));
    const sharedEdges = [...room1EdgeIds].filter(edgeId => room2EdgeIds.has(edgeId));
    const overlapRatio = sharedEdges.length / Math.min(room1EdgeIds.size, room2EdgeIds.size);
    
    // 🔧 SMART MERGING CRITERIA:
    // 1. Don't merge during explicit room creation (user is creating separate rooms)
    // 2. NEVER merge during label preservation (resize operations) to maintain room structure
    if (context === 'explicitRoomCreation' || context === 'labelPreservation' || creationContext?.isFromEndpoint) {
      console.log(`🏠 SMART MERGE: Skipping merge - preserving room separation`, {
        context,
        isFromEndpoint: creationContext?.isFromEndpoint,
        room1Area: roomData1.area.toFixed(2),
        room2Area: roomData2.area.toFixed(2),
        centerDistance: centerDistance.toFixed(1),
        overlapRatio: (overlapRatio * 100).toFixed(1) + '%',
        reason: context === 'labelPreservation' 
          ? 'label_preservation_maintains_existing_room_structure'
          : 'preserving_separate_room_identities_for_user_creation'
      });
      return false;
    }
    
    // 🔧 LARGE RESIZE ADDITIONAL PROTECTION: Extra safety for complex resize scenarios
    // Even if context is not labelPreservation, check for signs of a resize operation
    const hasSignsOfResize = (centerDistance > 5 && centerDistance < 200) && // Moderate displacement
                            (overlapRatio > 0.1 && overlapRatio < 0.7) && // Some overlap but not complete
                            (areaRatio > 0.3 && areaRatio < 3); // Area changed but not dramatically
    
    if (hasSignsOfResize) {
      console.log(`🔧 RESIZE PROTECTION: Detected potential resize operation - preventing merge`, {
        centerDistance: centerDistance.toFixed(1),
        overlapRatio: (overlapRatio * 100).toFixed(1) + '%',
        areaRatio: areaRatio.toFixed(2),
        room1Area: roomData1.area.toFixed(2),
        room2Area: roomData2.area.toFixed(2),
        reason: 'potential_resize_operation_detected'
      });
      return false;
    }
    
    // 🔧 ENDPOINT CONNECTION FIX: Additional check for rooms that share endpoints but should remain separate
    // When rooms connect at endpoints, they may share walls but are intentionally separate
    if (creationContext?.isFromEndpoint && overlapRatio > 0.2 && overlapRatio < 0.8) {
      console.log(`🏠 ENDPOINT CONNECTION: Preserving separate rooms - partial wall sharing indicates adjacent rooms`, {
        overlapRatio: (overlapRatio * 100).toFixed(1) + '%',
        centerDistance: centerDistance.toFixed(1),
        areaRatio: areaRatio.toFixed(2),
        reason: 'adjacent_rooms_sharing_endpoints'
      });
      return false;
    }
    
    // 2. Only merge if rooms are very small relative to each other (likely excursions)
    const isTrueExcursion = areaRatio < 0.3 && centerDistance < 50;
    
    // 3. Only merge if one room is significantly smaller and very close (alcove/nook pattern)
    const isAlcovePattern = (roomData1.area < 5 || roomData2.area < 5) && centerDistance < 30;
    
    // 4. Only merge if rooms have very high overlap (> 80% of walls shared)
    const isHighOverlap = overlapRatio > 0.8;
    
    const shouldMerge = isTrueExcursion || (isAlcovePattern && isHighOverlap);
    
    console.log(`🏠 SMART MERGE ANALYSIS:`, {
      centerDistance: centerDistance.toFixed(1),
      areaRatio: areaRatio.toFixed(2),
      overlapRatio: (overlapRatio * 100).toFixed(1) + '%',
      isTrueExcursion,
      isAlcovePattern,
      isHighOverlap,
      shouldMerge,
      context,
      creationContext,
      areas: [roomData1.area.toFixed(2), roomData2.area.toFixed(2)]
    });
    
    return shouldMerge;
  };
  
  // 🚀 PERFORMANCE OPTIMIZATION: Only start cycle detection from walls not yet part of any found cycle
  // This prevents the massive over-generation of duplicate cycles
  roomBoundaryWalls.forEach(startWall => {
    // 🚀 PERFORMANCE: Smart early exit based on room-to-wall ratio
    // For connected rooms, we typically expect 1 room per 3-4 walls maximum
    const maxExpectedRooms = Math.ceil(roomBoundaryWalls.length / 3);
    const conservativeLimit = Math.min(ROOM_CALC_CONFIG.MAX_ROOMS_TO_PROCESS, maxExpectedRooms);
    
    if (foundRoomWallSets.length >= conservativeLimit) {
      return;
    }

    // 🚀 PERFORMANCE OPTIMIZATION: Skip walls that are already part of a found room
    const startWallEdgeId = generateEdgeId(startWall.start, startWall.end);
    if (globalVisitedEdges.has(startWallEdgeId)) {
      return; // This wall is already part of a detected room, skip to avoid duplicates
    }

    const startNodeStr = pointToString(startWall.start);
    const endNodeOfStartWallStr = pointToString(startWall.end);

    const q: [[string, Wall[], Set<string>]] = [[endNodeOfStartWallStr, [startWall], new Set([startWallEdgeId])]];
    
    while (q.length > 0) {
      const [currentNodeStr, currentPathWalls, currentPathVisitedEdges] = q.shift()!;
      const currentNodePoint = adj.get(currentNodeStr)?.point;

      if (!currentNodePoint) continue;

      const connectedWallInfos = adj.get(currentNodeStr)?.walls || [];

      for (const connectedWall of connectedWallInfos) {
        const edgeId = generateEdgeId(connectedWall.start, connectedWall.end);
        if (currentPathVisitedEdges.has(edgeId)) {
          continue; 
        }
        
        let orientedConnectedWall = connectedWall;
        let nextNodePoint: Point;

        if (pointsAreEqual(connectedWall.start, currentNodePoint)) {
            nextNodePoint = connectedWall.end;
            // orientedConnectedWall is already correct: currentNodePoint -> nextNodePoint
        } else if (pointsAreEqual(connectedWall.end, currentNodePoint)) {
            nextNodePoint = connectedWall.start;
            // Wall is reversed in storage, orient it for path: currentNodePoint -> nextNodePoint
            orientedConnectedWall = { 
                ...connectedWall, // Spread to keep ALL properties including extrusion flags
                start: currentNodePoint, 
                end: nextNodePoint 
            };
        } else {
            // This wall does not connect to currentNodePoint, should not happen if adj list is correct
            console.error("[calculateRooms] Wall in adjacency list does not connect to current node.", connectedWall, currentNodePoint);
            continue;
        }
        
        const nextNodeStr = pointToString(nextNodePoint);
        
        if (nextNodeStr === startNodeStr) { // Cycle detected
            const cycleWalls = [...currentPathWalls, orientedConnectedWall]; // Use the correctly oriented wall
            if (cycleWalls.length >= 3) { // Basic check for a valid room
              const originalCycleWallsForStableId = [...currentPathWalls.map(cw => {
                return cw; 
              }), connectedWall]; // Use original connectedWall for stable ID consistency if it matters.
                                  // For area, use cycleWalls with orientedConnectedWall.

              const sortedCycleEdgeIds = cycleWalls.map(w => generateEdgeId(w.start, w.end)).sort().join('|');
              // Check if this room (by sorted edge IDs) is already found to avoid duplicates
              if (!foundRoomWallSets.some(existingSet => 
                  existingSet.walls.map(w => generateEdgeId(w.start, w.end)).sort().join('|') === sortedCycleEdgeIds
              )) {
                 // Further validation: Shoelace formula for area to check winding order / non-self-intersection
                 const cycleArea = calculateArea(cycleWalls);
                 
                 // 🚀 PERFORMANCE: Increased area threshold to filter out tiny rooms earlier
                 if (cycleArea > ROOM_CALC_CONFIG.MIN_ROOM_AREA_THRESHOLD) {
                    let addThisNewCycle = true;
                    const indexesToRemove: number[] = [];

                    // 🚀 PERFORMANCE: Simplified room filtering - skip complex phantom merger detection
                    if (ROOM_CALC_CONFIG.ENABLE_COMPLEX_FILTERING) {
                      const newCycleHasNonStandardWalls = cycleWalls.some(w => w.type === 'HLP' || w.type === 'PW' || w.type === 'SW');
                      const newCycleCenter = getRoomCenter(cycleWalls);

                      for (let i = 0; i < foundRoomWallSets.length; i++) {
                          const existingSet = foundRoomWallSets[i];
                          const existingSetWalls = existingSet.walls;
                          const existingSetEdgeIds = new Set(existingSetWalls.map(w => generateEdgeId(w.start, w.end)));
                          const cycleWallsEdgeIds = new Set(cycleWalls.map(w => generateEdgeId(w.start, w.end)));
                          const existingSetArea = existingSet.area; // 🚀 PERFORMANCE: Use pre-calculated area
                          const existingSetHasNonStandardWalls = existingSetWalls.some(w => w.type === 'HLP' || w.type === 'PW' || w.type === 'SW');
                          const existingSetCenter = getRoomCenter(existingSetWalls);

                          // Rule 1: New cycle is a superset of an existing cycle (discard new)
                          if (cycleWalls.length > existingSetWalls.length && [...existingSetEdgeIds].every(edgeId => cycleWallsEdgeIds.has(edgeId))) {
                              addThisNewCycle = false;
                              break;
                          }

                          // Rule 2: Existing cycle is a superset of the new cycle (discard existing)
                          if (existingSetWalls.length > cycleWalls.length && [...cycleWallsEdgeIds].every(edgeId => existingSetEdgeIds.has(edgeId))) {
                              indexesToRemove.push(i);
                              continue; // Continue checking other existing rooms, new cycle is better than this one
                          }
                          
                          // Rule 3: New cycle is a potential phantom merger (discard new)
                          // A new, larger, non-standard cycle that contains a simpler, smaller, existing cycle
                          if (newCycleHasNonStandardWalls && !existingSetHasNonStandardWalls && 
                              cycleArea > existingSetArea && 
                              isPointInsideRoom(existingSetCenter, cycleWalls) &&
                              existingSetWalls.every(wall => isPointOnAnyWall(getRoomCenter([wall]), cycleWalls, 1) || 
                                                       isPointInsideRoom(wall.start, cycleWalls) && isPointInsideRoom(wall.end, cycleWalls)) // Check if existing walls are contained
                             ) {
                              addThisNewCycle = false;
                              break;
                          }

                          // Rule 4: Existing cycle is a potential phantom merger (discard existing)
                          // An existing, larger, non-standard cycle that contains a simpler, smaller, new cycle
                          if (existingSetHasNonStandardWalls && !newCycleHasNonStandardWalls && 
                              existingSetArea > cycleArea && 
                              isPointInsideRoom(newCycleCenter, existingSetWalls) &&
                              cycleWalls.every(wall => isPointOnAnyWall(getRoomCenter([wall]), existingSetWalls, 1) ||
                                                     isPointInsideRoom(wall.start, existingSetWalls) && isPointInsideRoom(wall.end, existingSetWalls)) // Check if new walls are contained
                             ) {
                              indexesToRemove.push(i);
                          }
                      }
                    } else {
                      // 🚀 PERFORMANCE: Simple duplicate detection only
                      for (let i = 0; i < foundRoomWallSets.length; i++) {
                          const existingSet = foundRoomWallSets[i];
                          const existingSetWalls = existingSet.walls;
                          const existingSetEdgeIds = new Set(existingSetWalls.map(w => generateEdgeId(w.start, w.end)));
                          const cycleWallsEdgeIds = new Set(cycleWalls.map(w => generateEdgeId(w.start, w.end)));

                          // Only check for exact supersets to avoid duplicates
                          if (cycleWalls.length > existingSetWalls.length && [...existingSetEdgeIds].every(edgeId => cycleWallsEdgeIds.has(edgeId))) {
                              addThisNewCycle = false;
                              break;
                          }
                          if (existingSetWalls.length > cycleWalls.length && [...cycleWallsEdgeIds].every(edgeId => existingSetEdgeIds.has(edgeId))) {
                              indexesToRemove.push(i);
                          }
                      }
                    }

                    if (addThisNewCycle) {
                        // Remove marked rooms (iterate backwards to avoid index shifting issues)
                        for (let i = indexesToRemove.length - 1; i >= 0; i--) {
                            foundRoomWallSets.splice(indexesToRemove[i], 1);
                        }
                        foundRoomWallSets.push({ walls: cycleWalls, area: cycleArea });
                        
                        // 🚀 PERFORMANCE OPTIMIZATION: Mark all edges of this room as globally visited
                        // This prevents future cycle detection from re-exploring these edges
                        cycleWalls.forEach(wall => {
                          const wallEdgeId = generateEdgeId(wall.start, wall.end);
                          globalVisitedEdges.add(wallEdgeId);
                        });
                    }
                 }
            }
          }
        } else {
          // 🚀 PERFORMANCE: Reduced max path length from 20 to 8 to prevent exponential growth
          if (currentPathWalls.length < ROOM_CALC_CONFIG.MAX_PATH_LENGTH) {
            const newPathVisitedEdges = new Set(currentPathVisitedEdges);
            newPathVisitedEdges.add(edgeId);
            q.push([nextNodeStr, [...currentPathWalls, orientedConnectedWall], newPathVisitedEdges]); // Use oriented wall for path
          }
        }
      }
    }
  });

  // 🚀 PERFORMANCE: Clear caches after calculation to prevent memory leaks
  if (ROOM_CALC_CONFIG.ENABLE_POINT_CACHING) {
    clearPointStringCache();
  }
  clearGeometryCache();
  
  // 🔧 DEBUG: Log cycle detection results to help identify multiplication issues
  console.log('🔧 CYCLE DETECTION COMPLETE (OPTIMIZED):', {
    totalWallsAnalyzed: roomBoundaryWalls.length,
    cyclesDetected: foundRoomWallSets.length,
    wallsSkippedByGlobalVisited: globalVisitedEdges.size,
    context,
    roomAreas: foundRoomWallSets.map(r => r.area.toFixed(2)),
    roomWallCounts: foundRoomWallSets.map(r => r.walls.length),
    performanceNote: `Reduced from typical ${roomBoundaryWalls.length}x over-detection to ${foundRoomWallSets.length} rooms`
  });

  // 🔧 EXCURSION FIX: Merge rooms that share walls with the same parent wall ID
  const mergedRoomWallSets: { walls: Wall[]; area: number }[] = [];
  const processedRoomIndices = new Set<number>();
  
  // Logging disabled to prevent console spam
  // console.log(`🏠 EXCURSION: Starting room merging process with ${foundRoomWallSets.length} detected rooms`);
  
  foundRoomWallSets.forEach((roomData, index) => {
    if (processedRoomIndices.has(index)) return;
    
    // Find all rooms that share parent wall IDs with this room
    const roomParentWallIds = new Set<string>();
    roomData.walls.forEach(wall => {
      const parentWallId = (wall as any).parentWallId;
      if (parentWallId) {
        roomParentWallIds.add(parentWallId);
      }
    });
    
    // Logging disabled to prevent console spam
    // console.log(`🏠 EXCURSION: Room ${index + 1} has ${roomParentWallIds.size} unique parent wall IDs: [${Array.from(roomParentWallIds).join(', ')}]`);
    
    if (roomParentWallIds.size === 0) {
      // No parent wall IDs, treat as normal room
              // Logging disabled to prevent console spam
        // console.log(`🏠 EXCURSION: Room ${index + 1} has no parent wall IDs - treating as normal room`);
      mergedRoomWallSets.push(roomData);
      processedRoomIndices.add(index);
      return;
    }
    
    // Find other rooms that share any parent wall IDs
    const roomsToMerge = [roomData];
    const indicesToMerge = [index];
    
    foundRoomWallSets.forEach((otherRoomData, otherIndex) => {
      if (otherIndex === index || processedRoomIndices.has(otherIndex)) return;
      
      const otherRoomParentWallIds = new Set<string>();
      otherRoomData.walls.forEach(wall => {
        const parentWallId = (wall as any).parentWallId;
        if (parentWallId) {
          otherRoomParentWallIds.add(parentWallId);
        }
      });
      
      // Check if there's any overlap in parent wall IDs
      const hasSharedParentWallId = [...roomParentWallIds].some(id => otherRoomParentWallIds.has(id));
      
      if (hasSharedParentWallId) {
        // 🚫 PRESERVE ROOM IDENTITIES: Never merge rooms with shared walls
        // Each room maintains its own identity and walls for proper ID preservation
        // Visual representation of shared walls will be handled in the rendering layer
        console.log(`🏠 SHARED WALLS PRESERVED: Keeping separate - rooms ${index + 1} and ${otherIndex + 1} share walls but maintain distinct identities`);
        console.log(`🏠 Shared wall IDs: ${[...roomParentWallIds].filter(id => otherRoomParentWallIds.has(id)).join(', ')}`);
        // Note: No merging occurs - rooms keep their individual identities
      }
    });
    
    // Mark all merged room indices as processed
    indicesToMerge.forEach(idx => processedRoomIndices.add(idx));
    
    if (roomsToMerge.length > 1) {
      // Merge the rooms by combining their walls and recalculating area
      const allWalls = roomsToMerge.flatMap(room => room.walls);
      const uniqueWalls = allWalls.filter((wall, index, array) => {
        return array.findIndex(w => 
          pointsAreEqual(w.start, wall.start, 0.1) && pointsAreEqual(w.end, wall.end, 0.1)
        ) === index;
      });
      
      const mergedArea = calculateArea(uniqueWalls);
      mergedRoomWallSets.push({ walls: uniqueWalls, area: mergedArea });
      // console.log(`🏠 EXCURSION: Merged ${roomsToMerge.length} rooms into one room with ${uniqueWalls.length} walls and area ${mergedArea.toFixed(2)}m²`);
    } else {
              // console.log(`🏠 EXCURSION: Room ${index + 1} has no rooms to merge with - keeping as single room`);
      mergedRoomWallSets.push(roomData);
    }
  });

  // 🔧 ENHANCED DUPLICATE DETECTION: More aggressive filtering for connection-related duplicates
  const filteredRoomWallSets: { walls: Wall[]; area: number }[] = [];
  
  mergedRoomWallSets.forEach((roomData, index) => {
    const roomCenter = getRoomCenter(roomData.walls);
    const roomArea = roomData.area;
    const roomBounds = getRoomBounds(roomData.walls);
    
    // Check if this room is too similar to an already added room
    let isDuplicate = false;
    let duplicateReason = '';
    
    for (let i = 0; i < filteredRoomWallSets.length; i++) {
      const existingRoomData = filteredRoomWallSets[i];
      const existingCenter = getRoomCenter(existingRoomData.walls);
      const existingArea = existingRoomData.area;
      const existingBounds = getRoomBounds(existingRoomData.walls);
      
      // 1. Check center proximity (more aggressive - within 30 pixels)
      const centerDistance = Math.hypot(roomCenter.x - existingCenter.x, roomCenter.y - existingCenter.y);
      const isCloseCenter = centerDistance < 30;
      
      // 2. Check area similarity (more lenient for merged rooms - within 15%)
      const areaDifference = Math.abs(roomArea - existingArea) / Math.max(roomArea, existingArea);
      const isSimilarArea = areaDifference < 0.15;
      
      // 3. Check wall overlap (improved algorithm with 60% threshold)
      const roomEdgeIds = new Set(roomData.walls.map(w => generateEdgeId(w.start, w.end)));
      const existingEdgeIds = new Set(existingRoomData.walls.map(w => generateEdgeId(w.start, w.end)));
      const sharedEdges = [...roomEdgeIds].filter(edgeId => existingEdgeIds.has(edgeId));
      const overlapRatio = sharedEdges.length / Math.min(roomEdgeIds.size, existingEdgeIds.size);
      const hasSignificantOverlap = overlapRatio > 0.6;
      
      // 4. Check if one room is entirely contained within another (geometric containment)
      const roomContainedInExisting = isRoomContainedInAnother(roomData.walls, existingRoomData.walls);
      const existingContainedInRoom = isRoomContainedInAnother(existingRoomData.walls, roomData.walls);
      
      // 5. Check for superset relationships (one room has all walls of another plus more)
      const roomIsSupersetOfExisting = [...existingEdgeIds].every(edgeId => roomEdgeIds.has(edgeId)) && roomEdgeIds.size > existingEdgeIds.size;
      const existingIsSupersetOfRoom = [...roomEdgeIds].every(edgeId => existingEdgeIds.has(edgeId)) && existingEdgeIds.size > roomEdgeIds.size;
      
      // 6. Check for boundary overlap (rooms sharing significant boundary length)
      const boundaryOverlap = calculateBoundaryOverlap(roomData.walls, existingRoomData.walls);
      const hasBoundaryOverlap = boundaryOverlap > 0.5; // 50% of boundary shared
      
      // 🔧 ENHANCED DUPLICATE DETECTION: More intelligent filtering for adjacent rooms
      // When creating rooms from existing endpoints, we need to distinguish between:
      // 1. True duplicates (same room detected multiple times)
      // 2. Adjacent rooms (legitimate separate rooms that share walls)
      
      // 🔧 CONTEXT-AWARE FILTERING: Adjust thresholds based on room creation context
      const isFromEndpoint = creationContext?.isFromEndpoint === true;
      const isExplicitRoomCreation = context === 'explicitRoomCreation';
      const isLabelPreservation = context === 'labelPreservation';
      
      // 🔧 RESIZE FIX: During label preservation (resize), use extremely strict thresholds
      // This prevents adjacent rooms from being detected as duplicates during resize
      const centerThreshold = isLabelPreservation ? 1 : // Almost identical centers only during resize
                             (isFromEndpoint || isExplicitRoomCreation) ? 5 : 15;
      const overlapThreshold = isLabelPreservation ? 0.99 : // 99% overlap required during resize
                              (isFromEndpoint || isExplicitRoomCreation) ? 0.95 : 0.8;
      const areaThreshold = isLabelPreservation ? 0.001 : // 0.1% area difference during resize
                           (isFromEndpoint || isExplicitRoomCreation) ? 0.01 : 0.05;
      
      // CRITICAL FIX: Only flag as duplicate if rooms are truly the same, not just adjacent
      const isVeryCloseCenter = centerDistance < centerThreshold;
      const isVeryHighOverlap = overlapRatio > overlapThreshold;
      const isNearlySameArea = areaDifference < areaThreshold;
      
      // 🔧 ENDPOINT CONNECTION FIX: For endpoint creation, require ALL criteria to be met for duplicate detection
      // This prevents adjacent rooms from being considered duplicates just because they share walls
      const areTrulyTheSame = isFromEndpoint ? 
        (isVeryCloseCenter && isVeryHighOverlap && isNearlySameArea) : // All criteria must be met for endpoint creation
        (isVeryCloseCenter && isVeryHighOverlap && isNearlySameArea); // Same for other contexts
      
      // Determine if this is a duplicate based on multiple criteria
      if (areTrulyTheSame) {
        // 🔧 ENDPOINT CONNECTION FIX: Extra validation for endpoint-created rooms
        // Even if they appear identical, check if they have significantly different wall counts or arrangements
        if (isFromEndpoint) {
          const roomWallCount = roomData.walls.length;
          const existingWallCount = existingRoomData.walls.length;
          const wallCountDifference = Math.abs(roomWallCount - existingWallCount);
          const wallCountRatio = Math.min(roomWallCount, existingWallCount) / Math.max(roomWallCount, existingWallCount);
          
          // If wall counts are significantly different, these are likely separate adjacent rooms
          if (wallCountDifference > 1 || wallCountRatio < 0.8) {
            console.log(`🔧 ENDPOINT CONNECTION: Preserving rooms despite similarity - different wall structures`, {
              newRoomWalls: roomWallCount,
              existingRoomWalls: existingWallCount,
              wallCountDifference,
              wallCountRatio: wallCountRatio.toFixed(2),
              centerDistance: centerDistance.toFixed(1),
              overlapRatio: (overlapRatio * 100).toFixed(1) + '%'
            });
            // Don't mark as duplicate - these are separate rooms with different structures
          } else {
            isDuplicate = true;
            duplicateReason = `identical endpoint-created room (center: ${centerDistance.toFixed(1)}px, overlap: ${(overlapRatio * 100).toFixed(1)}%, area diff: ${(areaDifference * 100).toFixed(1)}%, walls: ${roomWallCount}/${existingWallCount})`;
            break;
          }
        } else {
          isDuplicate = true;
          duplicateReason = `identical room (center: ${centerDistance.toFixed(1)}px, overlap: ${(overlapRatio * 100).toFixed(1)}%, area diff: ${(areaDifference * 100).toFixed(1)}%)`;
          break;
        }
      } else if (roomContainedInExisting) {
        isDuplicate = true;
        duplicateReason = `room geometrically contained in existing room`;
        break;
      } else if (existingContainedInRoom) {
        // Remove the existing room and add this one instead
        filteredRoomWallSets.splice(i, 1);
        duplicateReason = `existing room contained in new room - replacing`;
        console.log(`🔍 Replacing contained room at center (${existingCenter.x.toFixed(1)}, ${existingCenter.y.toFixed(1)}) with larger room`);
        break;
      } else if (roomIsSupersetOfExisting) {
        // Remove the existing room and add this one instead
        filteredRoomWallSets.splice(i, 1);
        duplicateReason = `room is superset of existing - replacing`;
        console.log(`🔍 Replacing subset room (${existingRoomData.walls.length} walls) with superset room (${roomData.walls.length} walls)`);
        break;
      } else if (existingIsSupersetOfRoom) {
        isDuplicate = true;
        duplicateReason = `existing room is superset of this room`;
        break;
      }
      
      // 🔧 ADJACENT ROOM ALLOWANCE: Don't flag adjacent rooms as duplicates
      // Adjacent rooms naturally share walls and may have similar areas but different centers
      const isAdjacentRoom = hasSignificantOverlap && centerDistance > 15 && centerDistance < 200;
      
      // 🔧 ENDPOINT CREATION DETECTION: Check if this room was likely created from an endpoint
      const isLikelyFromEndpoint = isExplicitRoomCreation && isAdjacentRoom && overlapRatio > 0.3 && overlapRatio < 0.8;
      
      if (isAdjacentRoom || isLikelyFromEndpoint) {
        console.log(`🏠 Allowing ${isLikelyFromEndpoint ? 'endpoint-created' : 'adjacent'} room at center (${roomCenter.x.toFixed(1)}, ${roomCenter.y.toFixed(1)}) - wall overlap: ${(overlapRatio * 100).toFixed(1)}%, center distance: ${centerDistance.toFixed(1)}px`);
        // Continue processing - this is a valid adjacent room, not a duplicate
      }
    }
    
    if (!isDuplicate) {
      filteredRoomWallSets.push(roomData);
    } else {
      console.log(`🔍 Filtered out duplicate room at center (${roomCenter.x.toFixed(1)}, ${roomCenter.y.toFixed(1)}) with area ${roomArea.toFixed(2)}m² - reason: ${duplicateReason}`);
    }
  });

  // 🔧 ENHANCED DEBUG: Log duplicate filtering results with adjacency context
  console.log('🔧 DUPLICATE FILTERING COMPLETE:', {
    context,
    roomsBeforeFiltering: mergedRoomWallSets.length,
    roomsAfterFiltering: filteredRoomWallSets.length,
    roomsFiltered: mergedRoomWallSets.length - filteredRoomWallSets.length,
    remainingRoomAreas: filteredRoomWallSets.map(r => r.area.toFixed(2)),
    // 🔧 NEW: Enhanced adjacency information
    adjacencyInfo: {
      roomsWithSharedWalls: filteredRoomWallSets.filter((room, i) => {
        return filteredRoomWallSets.some((otherRoom, j) => {
          if (i === j) return false;
          const roomEdges = new Set(room.walls.map(w => generateEdgeId(w.start, w.end)));
          const otherEdges = new Set(otherRoom.walls.map(w => generateEdgeId(w.start, w.end)));
          const sharedEdges = [...roomEdges].filter(edgeId => otherEdges.has(edgeId));
          return sharedEdges.length > 0;
        });
      }).length,
      roomCenters: filteredRoomWallSets.map(r => {
        const center = getRoomCenter(r.walls);
        return `(${center.x.toFixed(1)}, ${center.y.toFixed(1)})`;
      })
    }
  });

  const finalRooms = filteredRoomWallSets.map((roomData, index) => {
    let roomWalls = roomData.walls;
    
    // 🔧 ENHANCED EXCURSION FIX: Add back all walls that belong to this room based on parent wall relationships and parent room IDs
    const extrusionWalls = wallsToUse.filter(wall => (wall as any).isExtrusionConnector);
    const mainExtrusionSegments = wallsToUse.filter(wall => (wall as any).isMainExtrusionSegment);
    const splitWalls = wallsToUse.filter(wall => (wall as any).isSplitOrigin);
    
    // Calculate the stable room ID for this room to match against parent room IDs
    const currentRoomStableId = getPersistentRoomId(roomWalls, currentFloor);
    
    // Find all walls that share parent wall IDs with walls in this room
    const roomWallParentIds = new Set<string>();
    roomWalls.forEach(roomWall => {
      const parentWallId = (roomWall as any).parentWallId;
      if (parentWallId) {
        roomWallParentIds.add(parentWallId);
      }
    });
    
    // 🔧 CRITICAL FIX: Find walls that reference this room in BOTH old and new ownership formats
    const wallsWithMatchingParentRoomIds = wallsToUse.filter(wall => {
      // Old format check
      const parentRoomIds = (wall as any).parentRoomIds;
      const belongsToRoomOld = Array.isArray(parentRoomIds) && 
        parentRoomIds.some(roomId => roomId === currentRoomStableId);
      
      // New format check
      const belongsToRoomNew = (
        (wall as any).originatingRoomId === currentRoomStableId ||
        ((wall as any).assignedRoomIds && Array.isArray((wall as any).assignedRoomIds) && 
         (wall as any).assignedRoomIds.some((roomId: string) => roomId === currentRoomStableId))
      );
      
      return belongsToRoomOld || belongsToRoomNew;
    });
    
    // Add all split segments that share the same parent wall IDs or parent room IDs
    const additionalSplitWalls = wallsToUse.filter(wall => {
      const parentWallId = (wall as any).parentWallId;
      const parentRoomIds = (wall as any).parentRoomIds;
      
      // Include if it shares a parent wall ID with this room and isn't already included
      const sharesByParentWallId = parentWallId && roomWallParentIds.has(parentWallId);
      
      // 🔧 CRITICAL FIX: Include if it references this room in either old or new ownership format
      const sharesByParentRoomIdOld = Array.isArray(parentRoomIds) && 
        parentRoomIds.some(roomId => roomId === currentRoomStableId);
      const sharesByParentRoomIdNew = (
        (wall as any).originatingRoomId === currentRoomStableId ||
        ((wall as any).assignedRoomIds && Array.isArray((wall as any).assignedRoomIds) && 
         (wall as any).assignedRoomIds.some((roomId: string) => roomId === currentRoomStableId))
      );
      const sharesByParentRoomId = sharesByParentRoomIdOld || sharesByParentRoomIdNew;
      
      if (sharesByParentWallId || sharesByParentRoomId) {
        const alreadyIncluded = roomWalls.some(rw => 
          pointsAreEqual(wall.start, rw.start, 0.1) && pointsAreEqual(wall.end, rw.end, 0.1)
        );
        return !alreadyIncluded;
      }
      
      return false;
    });
    
    // Find extrusion walls that connect to this room's boundary (including split segments)
    const allRoomWalls = [...roomWalls, ...additionalSplitWalls];
    const belongingExtrusionWalls = extrusionWalls.filter(extWall => {
      // 🔧 CRITICAL FIX: Check room membership using BOTH old and new ownership formats
      const extrusionParentRoomIds = (extWall as any).parentRoomIds;
      const belongsToRoomOld = Array.isArray(extrusionParentRoomIds) && 
          extrusionParentRoomIds.some(roomId => roomId === currentRoomStableId);
      const belongsToRoomNew = (
        (extWall as any).originatingRoomId === currentRoomStableId ||
        ((extWall as any).assignedRoomIds && Array.isArray((extWall as any).assignedRoomIds) && 
         (extWall as any).assignedRoomIds.some((roomId: string) => roomId === currentRoomStableId))
      );
      
      if (belongsToRoomOld || belongsToRoomNew) {
        return true;
      }
      
      // Fallback to geometric connection check
      return allRoomWalls.some(roomWall => {
        return pointsAreEqual(extWall.start, roomWall.start, 1) ||
               pointsAreEqual(extWall.start, roomWall.end, 1) ||
               pointsAreEqual(extWall.end, roomWall.start, 1) ||
               pointsAreEqual(extWall.end, roomWall.end, 1);
      });
    });
    
    // Find main extrusion segments that connect to this room's extrusion connectors
    const belongingMainSegments = mainExtrusionSegments.filter(mainSeg => {
      // 🔧 CRITICAL FIX: Check room membership using BOTH old and new ownership formats
      const mainSegParentRoomIds = (mainSeg as any).parentRoomIds;
      const belongsToRoomOld = Array.isArray(mainSegParentRoomIds) && 
          mainSegParentRoomIds.some(roomId => roomId === currentRoomStableId);
      const belongsToRoomNew = (
        (mainSeg as any).originatingRoomId === currentRoomStableId ||
        ((mainSeg as any).assignedRoomIds && Array.isArray((mainSeg as any).assignedRoomIds) && 
         (mainSeg as any).assignedRoomIds.some((roomId: string) => roomId === currentRoomStableId))
      );
      
      if (belongsToRoomOld || belongsToRoomNew) {
        return true;
      }
      
      // Fallback to connection check with extrusion connectors
      return belongingExtrusionWalls.some(extWall => {
        return pointsAreEqual(mainSeg.start, extWall.start, 1) ||
               pointsAreEqual(mainSeg.start, extWall.end, 1) ||
               pointsAreEqual(mainSeg.end, extWall.start, 1) ||
               pointsAreEqual(mainSeg.end, extWall.end, 1);
      });
    });
    
    // 🔧 FIX: Deduplicate extrusion walls to prevent "extrusions within extrusions"
    const allExtrusionWallsRaw = [...additionalSplitWalls, ...belongingExtrusionWalls, ...belongingMainSegments];
    
    // 🔧 CRITICAL FIX: First check against existing room walls to prevent adding walls that are already in the room
    const existingRoomWallKeys = new Set<string>();
    roomWalls.forEach(wall => {
      const coordKey = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}-${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
      const reverseCoordKey = `${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}-${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}`;
      const tempIdKey = (wall as any).tempId ? `tempId:${(wall as any).tempId}` : '';
      
      existingRoomWallKeys.add(coordKey);
      existingRoomWallKeys.add(reverseCoordKey);
      if (tempIdKey) existingRoomWallKeys.add(tempIdKey);
    });
    
    // Deduplicate by coordinates and tempId to prevent the same wall being added multiple times
    const seenWalls = new Set<string>();
    let wallsSkippedAlreadyInRoom = 0;
    let wallsSkippedDuplicates = 0;
    
    const allExtrusionWalls = allExtrusionWallsRaw.filter(wall => {
      // Create a unique identifier using coordinates and tempId
      const coordKey = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}-${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
      const reverseCoordKey = `${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}-${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}`;
      const tempIdKey = (wall as any).tempId ? `tempId:${(wall as any).tempId}` : '';
      
      // 🔧 CRITICAL FIX: Skip walls that are already in the room
      const alreadyInRoom = existingRoomWallKeys.has(coordKey) || 
                           existingRoomWallKeys.has(reverseCoordKey) || 
                           (tempIdKey && existingRoomWallKeys.has(tempIdKey));
      
      if (alreadyInRoom) {
        wallsSkippedAlreadyInRoom++;
        return false; // Skip wall already in room
      }
      
      // Check if we've seen this wall before (either direction or by tempId)
      const hasSeenCoords = seenWalls.has(coordKey) || seenWalls.has(reverseCoordKey);
      const hasSeenTempId = tempIdKey && seenWalls.has(tempIdKey);
      
      if (hasSeenCoords || hasSeenTempId) {
        wallsSkippedDuplicates++;
        return false; // Skip duplicate
      }
      
      // Mark as seen
      seenWalls.add(coordKey);
      if (tempIdKey) seenWalls.add(tempIdKey);
      
      return true; // Include unique wall
    });
    
    if (allExtrusionWalls.length > 0) {
      console.log(`🔧 ENHANCED EXCURSION: Adding ${allExtrusionWalls.length} walls to room ${index + 1}`, {
        roomStableId: currentRoomStableId,
        additionalSplitWalls: additionalSplitWalls.length,
        belongingExtrusionWalls: belongingExtrusionWalls.length,
        belongingMainSegments: belongingMainSegments.length,
        wallsWithMatchingParentRoomIds: wallsWithMatchingParentRoomIds.length,
        rawExtrusionWalls: allExtrusionWallsRaw.length,
        finalExtrusionWalls: allExtrusionWalls.length,
        wallsSkippedAlreadyInRoom,
        wallsSkippedDuplicates,
        currentRoomWallCount: roomWalls.length,
        extrusionWallsWithParentRoomIds: allExtrusionWalls.filter(w => 
          Array.isArray((w as any).parentRoomIds)
        ).length
      });
      roomWalls = [...roomWalls, ...allExtrusionWalls];
    } else if (allExtrusionWallsRaw.length > 0) {
      console.log(`🔧 ENHANCED EXCURSION: All ${allExtrusionWallsRaw.length} extrusion walls already in room ${index + 1} - no additions needed`, {
        roomStableId: currentRoomStableId,
        currentRoomWallCount: roomWalls.length,
        wallsSkippedAlreadyInRoom,
        wallsSkippedDuplicates,
        additionalSplitWalls: additionalSplitWalls.length,
        belongingExtrusionWalls: belongingExtrusionWalls.length,
        belongingMainSegments: belongingMainSegments.length
      });
    }
    
    // Recalculate area if extrusion walls were added
    const finalArea = allExtrusionWalls.length > 0 ? calculateArea(roomWalls) : roomData.area;
    
    // 🔧 EXTRUSION FIX: Use original stable ID for rooms with extrusions to maintain label consistency
    let stableId: string;
    if (allExtrusionWalls.length > 0) {
      // For rooms with extrusions, preserve the original stable ID to maintain label mapping
      stableId = currentRoomStableId;
      console.log(`🔧 EXTRUSION ROOM STABLE ID PRESERVED:`, {
        roomIndex: index + 1,
        preservedStableId: stableId,
        wouldHaveBeenStableId: getPersistentRoomId(roomWalls, currentFloor),
        wallCount: roomWalls.length,
        extrusionWallsAdded: allExtrusionWalls.length
      });
    } else {
      // For normal rooms, calculate stable ID normally
      stableId = getPersistentRoomId(roomWalls, currentFloor);
    }
    
    const center = getRoomCenter(roomWalls);
    
    // Use custom label if available, otherwise use auto-generated name
    // But ensure we don't override existing custom labels with auto-generated ones
    let roomName = roomCustomLabels[stableId];
    
    // 🔧 EXTRUSION FIX: Enhanced fallback for rooms with extrusions
    // If no label found by stable ID, try alternative approaches for extrusion rooms
    if (!roomName && allExtrusionWalls.length > 0) {
      // Try to find label by the new calculated stable ID (in case labels were stored with the new ID)
      const newCalculatedStableId = getPersistentRoomId(roomWalls, currentFloor);
      if (newCalculatedStableId !== stableId && roomCustomLabels[newCalculatedStableId]) {
        roomName = roomCustomLabels[newCalculatedStableId];
        console.log(`🔧 EXTRUSION LABEL FOUND by new calculated ID:`, {
          originalStableId: stableId,
          newCalculatedStableId,
          foundLabel: roomName
        });
      }
      
      // 🔧 EXTRUSION FIX: Try fuzzy matching for similar stable IDs
      if (!roomName) {
        const roomCenter = getRoomCenter(roomWalls);
        const currentFloorPrefix = `floor_${currentFloor}_`;
        
        // Extract key coordinates from current stable ID for fuzzy matching
        const coordinateMatches = stableId.match(/(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/g) || [];
        const currentCoords = new Set(coordinateMatches);
        
        // Find labels with similar coordinates on the same floor
        const candidateLabels = Object.entries(roomCustomLabels).filter(([id, label]) => {
          if (!id.startsWith(currentFloorPrefix)) return false;
          
          const candidateMatches = id.match(/(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/g) || [];
          const candidateCoords = new Set(candidateMatches);
          
          // Check for coordinate overlap (at least 2 coordinates should match)
          const intersection = [...currentCoords].filter(coord => candidateCoords.has(coord));
          return intersection.length >= 2;
        });
        
        if (candidateLabels.length > 0) {
          // Pick the first candidate (could be improved with distance calculation)
          const [matchedId, matchedLabel] = candidateLabels[0];
          roomName = matchedLabel;
          console.log(`🔧 EXTRUSION LABEL FOUND by fuzzy coordinate matching:`, {
            originalStableId: stableId,
            matchedStableId: matchedId,
            foundLabel: roomName,
            coordinateOverlap: [...currentCoords].filter(coord => matchedId.includes(coord)),
            totalCandidates: candidateLabels.length
          });
        }
      }
    }
    
    // 🔧 PRE-SPLIT FIX: Enhanced fallback for rooms with pre-split walls
    // If no label found by stable ID, try fuzzy matching for rooms that may have changed due to pre-splitting
    if (!roomName) {
      // Check if this room has pre-split walls
      const hasPreSplitWalls = roomWalls.some(wall => (wall as any).isPreSplit);
      
      if (hasPreSplitWalls) {
        const currentFloorPrefix = `floor_${currentFloor}_`;
        
        // Extract key coordinates from current stable ID for fuzzy matching
        const coordinateMatches = stableId.match(/(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/g) || [];
        const currentCoords = new Set(coordinateMatches);
        
        // Find labels with similar coordinates on the same floor
        const candidateLabels = Object.entries(roomCustomLabels).filter(([id, label]) => {
          if (!id.startsWith(currentFloorPrefix)) return false;
          
          const candidateMatches = id.match(/(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/g) || [];
          const candidateCoords = new Set(candidateMatches);
          
          // Check for coordinate overlap (at least 2 coordinates should match for pre-split rooms)
          const intersection = [...currentCoords].filter(coord => candidateCoords.has(coord));
          return intersection.length >= 2;
        });
        
        if (candidateLabels.length > 0) {
          // Pick the first candidate (could be improved with distance calculation)
          const [matchedId, matchedLabel] = candidateLabels[0];
          roomName = matchedLabel;
          
          // 🔧 CRITICAL FIX: Update roomCustomLabels to map new stable ID to found label
          // This ensures future room calculations can find the label by the new stable ID
          roomCustomLabels[stableId] = matchedLabel;
          
          console.log(`🔧 PRE-SPLIT LABEL FOUND by fuzzy coordinate matching:`, {
            originalStableId: stableId,
            matchedStableId: matchedId,
            foundLabel: roomName,
            coordinateOverlap: [...currentCoords].filter(coord => matchedId.includes(coord)),
            totalCandidates: candidateLabels.length,
            hasPreSplitWalls: hasPreSplitWalls,
            updatedRoomCustomLabels: true
          });
        }
      }
    }
    
    // FALLBACK: If no label found by stableId, try to find by room position/geometry
    // 🔧 FIX: Skip proximity matching during explicit room creation to allow label prompt
    // Only use proximity matching for migrations, loading existing data, or geometry updates
    const isExplicitCreation = context === 'explicitRoomCreation';
    
    if (!roomName || roomName.trim() === '') {
      // Only try proximity matching if NOT during explicit room creation
      if (!isExplicitCreation) {
        // Try to find a matching label by room center proximity
        const roomCenter = getRoomCenter(roomWalls);
        type BestMatchType = { id: string; label: string; distance: number };
        let bestMatch: BestMatchType | null = null;
        
        Object.entries(roomCustomLabels).forEach(([labelId, label]) => {
          if (label && label.trim() !== '' && !label.startsWith('Room ')) {
            // 🏠 FLOOR FILTERING: Only consider labels from the current floor
            // Check if labelId has floor prefix and matches current floor
            const hasFloorPrefix = labelId.startsWith('floor_');
            if (hasFloorPrefix && currentFloor !== undefined) {
              const expectedFloorPrefix = `floor_${currentFloor}_`;
              if (!labelId.startsWith(expectedFloorPrefix)) {
                // This label belongs to a different floor, skip it
                return;
              }
            }
            
            // Try to extract coordinates from the labelId to compare positions
            try {
              // Parse the stable ID to get approximate center
              const coordMatches = labelId.match(/-?\d+\.\d+/g);
              if (coordMatches && coordMatches.length >= 4) {
                // Estimate center from first few coordinates
                const x1 = parseFloat(coordMatches[0]);
                const y1 = parseFloat(coordMatches[1]);
                const x2 = parseFloat(coordMatches[2]);
                const y2 = parseFloat(coordMatches[3]);
                const labelCenter = { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
                
                const distance = Math.hypot(roomCenter.x - labelCenter.x, roomCenter.y - labelCenter.y);
                
                if (!bestMatch || distance < bestMatch.distance) {
                  bestMatch = { id: labelId, label, distance };
                }
              }
            } catch (error) {
              // Ignore parsing errors
            }
          }
        });
        
        // Use the closest match if it's within a reasonable distance (200 pixels)
        if (bestMatch !== null) {
          const match = bestMatch as BestMatchType; // Type assertion to help TypeScript
          if (match.distance < 200) {
            roomName = match.label;
            console.log(`🔄 Room ${index + 1} matched by position: "${match.label}" (distance: ${match.distance.toFixed(1)}px)`);
          } else {
            roomName = `Room ${index + 1}`;
          }
        } else {
          roomName = `Room ${index + 1}`;
        }
      } else {
        // During explicit room creation, leave unlabeled so prompt can show
        roomName = `Room ${index + 1}`;
        console.log(`🏷️ Room ${index + 1} left unlabeled during explicit creation - prompt will show`);
      }
    }
    
    // DEBUG: Log room label resolution (DISABLED - causing console spam)
    // console.log(`🏷️ Room ${index + 1} label resolution:`, {
    //   stableId,
    //   availableLabels: Object.keys(roomCustomLabels),
    //   availableLabelsWithValues: roomCustomLabels,
    //   foundLabel: roomCustomLabels[stableId],
    //   finalName: roomName,
    //   hasCustomLabels: Object.keys(roomCustomLabels).length > 0,
    //   // 🔧 EXTRUSION DEBUG: Show detailed stable ID comparison for extrusion rooms
    //   ...(allExtrusionWalls.length > 0 && {
    //     isExtrusionRoom: true,
    //     extrusionWallsCount: allExtrusionWalls.length,
    //     currentRoomStableId: currentRoomStableId,
    //     preservedStableId: stableId,
    //     wouldHaveBeenStableId: getPersistentRoomId(roomWalls, currentFloor),
    //     similarStableIds: Object.keys(roomCustomLabels).filter(id => 
    //       id.includes(`floor_${currentFloor}_`) && 
    //       (id.includes('333.67') || id.includes('356.93') || id.includes('630.73') || id.includes('585.79'))
    //     )
    //   })
    // });
    
    const roomObject = {
      id: `room_${stableId}_${index}`, 
      stableId,
      walls: roomWalls,
      area: finalArea, // 🔧 EXCURSION FIX: Use recalculated area that includes extrusions
      name: roomName,
      center,
      elements: [], 
    };

    return roomObject;
  });

  if (context === 'geometryUpdate') {
    // 🔧 ROOM MERGE FIX: Smarter filtering for geometryUpdate with existing labels
    // Instead of returning ALL rooms, filter intelligently to prevent inappropriate room multiplication
    const existingLabelsCount = Object.keys(roomCustomLabels).length;
    
    if (existingLabelsCount > 0) {
      console.log('🔧 GEOMETRY UPDATE: Existing labels detected, applying intelligent filtering', {
        context,
        detectedRooms: finalRooms.length,
        existingLabelsCount,
        existingLabels: Object.keys(roomCustomLabels),
      });
      
      // Step 1: Find rooms that have existing labels (these should definitely be included)
      const labeledRooms = finalRooms.filter(room => {
        const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
        const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
        return hasStableIdLabel || hasIdLabel;
      });
      
      // Step 2: If we have significantly more detected rooms than labeled rooms, 
      // it suggests small connector spaces that should be filtered out
      const roomCountDiscrepancy = finalRooms.length - existingLabelsCount;
      const hasExcessRooms = roomCountDiscrepancy > 1; // More than 1 extra room suggests fragmentation
      
      if (hasExcessRooms && labeledRooms.length >= Math.min(existingLabelsCount, 2)) {
        // We have excess rooms and some labeled rooms found - prefer labeled rooms
        console.log('🔧 GEOMETRY UPDATE: Filtering excess rooms, returning labeled rooms', {
          detectedRooms: finalRooms.length,
          labeledRooms: labeledRooms.length,
          existingLabelsCount,
          roomCountDiscrepancy,
          reason: 'Preventing room fragmentation - too many small spaces detected'
        });
        return labeledRooms;
      } else {
        // Room count is reasonable or we couldn't find enough labeled rooms - return all
        console.log('🔧 GEOMETRY UPDATE: Room count reasonable, returning all rooms', {
          detectedRooms: finalRooms.length,
          labeledRooms: labeledRooms.length,
          existingLabelsCount,
          roomCountDiscrepancy,
          reason: 'Room count within expected range or insufficient labeled room matches'
        });
        return finalRooms;
      }
    }
    
    // 🔧 FIX: Modified filtering logic to allow unlabeled rooms to reach renderer
    // This fixes the chicken-and-egg problem where rooms can't show label UI because they're filtered out
    const knownRooms = finalRooms.filter(room => {
      const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
      const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
      const hasNonAutoGeneratedName = room.name && !room.name.startsWith('Room ');
      
      // 🔧 GEOMETRIC MATCHING: Check for geometric signature match (includes floor to prevent cross-floor collisions)
      let hasGeometricLabel = false;
      if (room.center && room.area && currentFloor !== undefined) {
        const geometricKey = `geo_${currentFloor}_${Math.round(room.center.x)}_${Math.round(room.center.y)}_${room.area.toFixed(1)}`;
        hasGeometricLabel = roomCustomLabels.hasOwnProperty(geometricKey);
        if (hasGeometricLabel) {
          console.log('🎯 GEOMETRIC MATCH FOUND:', {
            roomId: room.id,
            roomStableId: room.stableId,
            geometricKey,
            label: roomCustomLabels[geometricKey],
            floor: currentFloor
          });
        }
      }
      
      // 🔧 FIX: Allow unlabeled rooms to pass through to enable label UI rendering
      // Allow rooms that start with "Room " (auto-generated names) to show so they can get labeled
      const isAutoGeneratedRoom = room.name && room.name.startsWith('Room ');
      const shouldInclude = hasStableIdLabel || hasIdLabel || hasNonAutoGeneratedName || hasGeometricLabel || isAutoGeneratedRoom;
      
      return shouldInclude;
    });
    
    console.log('🔧 GEOMETRY UPDATE FILTERING (RE-ENABLED):', {
      context,
      detectedRooms: finalRooms.length,
      knownRooms: knownRooms.length,
      labeledRoomCount: Object.keys(roomCustomLabels).length,
      roomNames: finalRooms.map(r => r.name),
      knownRoomNames: knownRooms.map(r => r.name),
      roomStableIds: finalRooms.map(r => r.stableId),
      roomIds: finalRooms.map(r => r.id),
      labelKeys: Object.keys(roomCustomLabels),
      filteredOutCount: finalRooms.length - knownRooms.length
    });
    
    // 🔧 INTELLIGENT FIX: Smart room filtering to prevent inappropriate room merging
    const labeledRoomCount = Object.keys(roomCustomLabels).length;
    if (labeledRoomCount > 0 && knownRooms.length === 0) {
      console.log('🔧 GEOMETRY UPDATE: No known rooms found despite having labels - applying intelligent filtering');
      
      // Only return labeled rooms when there are excess detected rooms (preventing room fragmentation)
      // If we have more than 1 extra room beyond existing labels, return labeled rooms
      const excessRooms = finalRooms.length - labeledRoomCount;
      if (excessRooms > 1) {
        console.log('🔧 GEOMETRY UPDATE: Excess rooms detected, returning labeled rooms only', {
          detectedRooms: finalRooms.length,
          labeledRoomCount,
          excessRooms,
          roomLabels: Object.entries(roomCustomLabels).map(([id, label]) => ({ id: id.substring(0, 50) + '...', label }))
        });
        
        // Try to find rooms that match existing labels by position or characteristics
        const labeledRooms = finalRooms.filter(room => {
          // Check if this room has a custom label (non-auto-generated)
          const hasCustomLabel = room.name && !room.name.startsWith('Room ');
          // Check if there's a label entry for this room's ID or stableId
          const hasLabelEntry = roomCustomLabels[room.id] || roomCustomLabels[room.stableId];
          return hasCustomLabel || hasLabelEntry;
        });
        
        // If we found labeled rooms, return them; otherwise return known rooms to prevent total loss
        return labeledRooms.length > 0 ? labeledRooms : knownRooms.slice(0, labeledRoomCount);
      }
      
      console.log('🔧 GEOMETRY UPDATE: No excess rooms, returning all rooms to preserve layout', {
        detectedRooms: finalRooms.length,
        labeledRoomCount,
        excessRooms
      });
      return finalRooms;
    }
    
    return knownRooms;
  }
  
  if (context === 'labelPreservation') {
    // For label preservation during wall splitting, return ALL rooms so that
    // the label transfer logic can work with the complete set of detected rooms
    return finalRooms;
  }

  // 🔧 NEW: Handle endpoint room creation by filtering to only the newly created room
  if (context === 'explicitRoomCreation' && creationContext?.isFromEndpoint) {
    console.log('🔧 ENDPOINT ROOM CREATION DEBUG: Filtering for newly created room', {
      context,
      isFromEndpoint: creationContext?.isFromEndpoint,
      originalRoomCount: creationContext?.originalRoomCount,
      totalRoomsDetected: finalRooms.length,
      roomCustomLabelsKeys: Object.keys(roomCustomLabels),
      roomsWithNames: finalRooms.map(r => ({ name: r.name, stableId: r.stableId, area: r.area.toFixed(2) }))
    });
    
    // 🔧 ENDPOINT ROOM CREATION FIX: Use original room count to identify newly created rooms
    // If we have exactly one more room than we started with, return just that new room
    if (creationContext?.originalRoomCount !== undefined && 
        finalRooms.length === (creationContext?.originalRoomCount || 0) + 1) {
      
      // Find the room that wasn't labeled originally (most reliable approach)
      const newlyCreatedRooms = finalRooms.filter(room => {
        // Check if this room had a label before (if it's in roomCustomLabels, it's not new)
        const hadPreviousLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
        console.log('🔧 ENDPOINT ROOM CREATION DEBUG: Checking room', {
          roomName: room.name,
          roomStableId: room.stableId,
          hadPreviousLabel,
          roomArea: room.area.toFixed(2),
          isNewRoom: !hadPreviousLabel
        });
        return !hadPreviousLabel;
      });
      
      console.log('🔧 ENDPOINT ROOM CREATION DEBUG: Newly created rooms found:', {
        newlyCreatedRoomsCount: newlyCreatedRooms.length,
        newlyCreatedRooms: newlyCreatedRooms.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
      });
      
      // If we found exactly one newly created room, return ALL rooms (existing + new)
      if (newlyCreatedRooms.length === 1) {
        console.log('🔧 ENDPOINT ROOM CREATION: Returning all rooms (preserving existing + new):', {
          newRoomName: newlyCreatedRooms[0].name,
          newRoomArea: newlyCreatedRooms[0].area.toFixed(2),
          totalRoomsDetected: finalRooms.length,
          existingRoomsPreserved: finalRooms.length - newlyCreatedRooms.length,
          allRoomsReturned: true
        });
        return finalRooms; // Return ALL rooms, not just the new one
      }
      
      // 🔧 ENDPOINT ROOM CREATION FIX: If label-based detection fails but we know we have exactly one new room,
      // return the room with the largest area (assuming it's the newly created one)
      if (newlyCreatedRooms.length === 0) {
        const largestRoom = finalRooms.reduce((prev, current) => 
          (current.area > prev.area) ? current : prev
        );
        console.log('🔧 ENDPOINT ROOM CREATION: Label-based detection failed, returning all rooms with largest identified:', {
          largestRoomName: largestRoom.name,
          largestRoomArea: largestRoom.area.toFixed(2),
          totalRoomsDetected: finalRooms.length,
          allRoomsPreserved: true
        });
        return finalRooms; // Return ALL rooms, not just the largest
      }
    }
    
    // Find the newly created room using original approach (for backwards compatibility)
    const newlyCreatedRooms = finalRooms.filter(room => {
      // Check if this room had a label before (if it's in roomCustomLabels, it's not new)
      const hadPreviousLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
      console.log('🔧 ENDPOINT ROOM CREATION DEBUG: Checking room', {
        roomName: room.name,
        roomStableId: room.stableId,
        hadPreviousLabel,
        roomArea: room.area.toFixed(2),
        isNewRoom: !hadPreviousLabel
      });
      return !hadPreviousLabel;
    });
    
    console.log('🔧 ENDPOINT ROOM CREATION DEBUG: Newly created rooms found:', {
      newlyCreatedRoomsCount: newlyCreatedRooms.length,
      newlyCreatedRooms: newlyCreatedRooms.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
    });
    
    // 🔧 CRITICAL FIX: If we found exactly one newly created room, check if we should preserve existing labels
    if (newlyCreatedRooms.length === 1) {
      const hasExistingLabels = Object.keys(roomCustomLabels).length > 1; // More than just the new room
      const existingLabeledRooms = finalRooms.filter(room => {
        const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
        const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
        return hasStableIdLabel || hasIdLabel;
      });
      
      if (hasExistingLabels && existingLabeledRooms.length > 1) {
        console.log('🔧 ENDPOINT ROOM CREATION: Found new room but preserving existing labeled rooms:', {
          newRoomName: newlyCreatedRooms[0].name,
          newRoomArea: newlyCreatedRooms[0].area.toFixed(2),
          totalRoomsDetected: finalRooms.length,
          existingLabeledRooms: existingLabeledRooms.length,
          returningAllRooms: true,
          reasoning: 'Preserving existing labeled rooms along with new room'
        });
        return finalRooms; // Return all rooms to preserve existing labels
      } else {
        console.log('🔧 ENDPOINT ROOM CREATION: Returning only the newly created room:', {
          newRoomName: newlyCreatedRooms[0].name,
          newRoomArea: newlyCreatedRooms[0].area.toFixed(2),
          totalRoomsDetected: finalRooms.length,
          filteredToNewRoom: true
        });
        return newlyCreatedRooms;
      }
    }
    
    // 🔧 ENHANCED ENDPOINT ROOM CREATION FIX: More sophisticated detection for connected rooms
    // When a room connects to multiple existing rooms, we need to identify the room with the most new walls
    if (newlyCreatedRooms.length !== 1 && creationContext && creationContext!.newWalls && creationContext!.newWalls!.length > 0) {
      // Type assertion for the entire block since we know these are defined
      const context = creationContext!;
      const newWalls = creationContext!.newWalls!;
      console.log('🔧 ENDPOINT ROOM CREATION: Using new wall analysis for connected room detection');
      
      // Find the room that contains the most new walls (should be the newly created room)
      let bestCandidateRoom: Room | null = null;
      let maxNewWallCount = 0;
      
      finalRooms.forEach(room => {
        const newWallsInRoom = room.walls.filter(roomWall => 
          newWalls.some(newWall => 
            Math.abs(newWall.start.x - roomWall.start.x) < 0.1 && 
            Math.abs(newWall.start.y - roomWall.start.y) < 0.1 &&
            Math.abs(newWall.end.x - roomWall.end.x) < 0.1 && 
            Math.abs(newWall.end.y - roomWall.end.y) < 0.1
          )
        ).length;
        
        console.log('🔧 ENDPOINT ROOM CREATION: Analyzing room for new walls', {
          roomName: room.name,
          stableId: room.stableId,
          totalWalls: room.walls.length,
          newWallsCount: newWallsInRoom,
          newWallRatio: (newWallsInRoom / room.walls.length * 100).toFixed(1) + '%'
        });
        
        if (newWallsInRoom > maxNewWallCount) {
          maxNewWallCount = newWallsInRoom;
          bestCandidateRoom = room;
        }
      });
      
      // If we found a room with significant new walls, use it but preserve existing labels
      if (bestCandidateRoom && newWalls && maxNewWallCount >= Math.ceil(newWalls.length * 0.5)) {
        const candidateRoom = bestCandidateRoom as Room; // Type assertion for clarity
        const hasExistingLabels = Object.keys(roomCustomLabels).length > 0;
        const existingLabeledRooms = finalRooms.filter(room => {
          const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
          const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
          return hasStableIdLabel || hasIdLabel;
        });
        
        if (hasExistingLabels && existingLabeledRooms.length > 0) {
          console.log('🔧 ENDPOINT ROOM CREATION: Found best candidate room but preserving existing labeled rooms:', {
            roomName: candidateRoom.name,
            roomArea: candidateRoom.area.toFixed(2),
            newWallsContained: maxNewWallCount,
            totalNewWalls: newWalls.length,
            containmentRatio: (maxNewWallCount / newWalls.length * 100).toFixed(1) + '%',
            existingLabeledRooms: existingLabeledRooms.length,
            returningAllRooms: true,
            reasoning: 'Preserving existing labeled rooms along with candidate room'
          });
          return finalRooms; // Return all rooms to preserve existing labels
        } else {
          console.log('🔧 ENDPOINT ROOM CREATION: Found best candidate room using new wall analysis:', {
            roomName: candidateRoom.name,
            roomArea: candidateRoom.area.toFixed(2),
            newWallsContained: maxNewWallCount,
            totalNewWalls: newWalls.length,
            containmentRatio: (maxNewWallCount / newWalls.length * 100).toFixed(1) + '%'
          });
          return [candidateRoom];
        }
      }
    }
    
    // 🔧 CRITICAL FIX: Handle multiple candidate rooms while preserving existing labels
    if (newlyCreatedRooms.length > 1) {
      const hasExistingLabels = Object.keys(roomCustomLabels).length > newlyCreatedRooms.length;
      const existingLabeledRooms = finalRooms.filter(room => {
        const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
        const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
        return hasStableIdLabel || hasIdLabel;
      });
      
      if (hasExistingLabels && existingLabeledRooms.length > 0) {
        console.log('🔧 ENDPOINT ROOM CREATION: Multiple candidates found but preserving existing labeled rooms:', {
          totalCandidates: newlyCreatedRooms.length,
          candidateAreas: newlyCreatedRooms.map(r => r.area.toFixed(2)),
          existingLabeledRooms: existingLabeledRooms.length,
          returningAllRooms: true,
          reasoning: 'Preserving existing labeled rooms with multiple new candidates'
        });
        return finalRooms; // Return all rooms to preserve existing labels
      } else {
        // If multiple candidates, pick the largest one (most likely to be the new room)
        const largestNewRoom = newlyCreatedRooms.reduce((prev, current) => 
          (current.area > prev.area) ? current : prev
        );
        console.log('🔧 ENDPOINT ROOM CREATION: Multiple candidates found, selecting largest:', {
          selectedRoom: largestNewRoom.name,
          selectedArea: largestNewRoom.area.toFixed(2),
          totalCandidates: newlyCreatedRooms.length,
          candidateAreas: newlyCreatedRooms.map(r => r.area.toFixed(2))
        });
        return [largestNewRoom];
      }
    }
    
    // 🔧 CRITICAL FIX: If no newly created rooms found, check if we have existing labeled rooms
    // NEVER return empty array if there are existing labels - this causes labels to disappear
    const hasExistingLabels = Object.keys(roomCustomLabels).length > 0;
    const labeledRoomsExist = finalRooms.some(room => {
      const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
      const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
      return hasStableIdLabel || hasIdLabel;
    });
    
    if (hasExistingLabels && labeledRoomsExist) {
      const existingLabelsCount = Object.keys(roomCustomLabels).length;
      const excessRooms = finalRooms.length - existingLabelsCount;
      
      // Apply intelligent filtering: only return labeled rooms when there are excess detected rooms
      if (excessRooms > 1) {
        const labeledRooms = finalRooms.filter(room => {
          const hasStableIdLabel = room.stableId && roomCustomLabels.hasOwnProperty(room.stableId);
          const hasIdLabel = room.id && roomCustomLabels.hasOwnProperty(room.id);
          const hasCustomLabel = room.name && !room.name.startsWith('Room ');
          return hasStableIdLabel || hasIdLabel || hasCustomLabel;
        });
        
        console.log('🔧 ENDPOINT ROOM CREATION: Excess rooms detected, returning labeled rooms only:', {
          totalRoomsDetected: finalRooms.length,
          existingLabelsCount,
          excessRooms,
          labeledRoomsReturned: labeledRooms.length,
          preventingInappropriateMerging: true
        });
        return labeledRooms.length > 0 ? labeledRooms : finalRooms.slice(0, existingLabelsCount);
      }
      
      console.log('🔧 ENDPOINT ROOM CREATION: No excess rooms, returning all rooms to preserve layout:', {
        totalRoomsDetected: finalRooms.length,
        existingLabelsCount,
        excessRooms,
        reasoning: 'No room fragmentation detected, preserving current layout'
      });
      return finalRooms; // Return all rooms when no excess detected
    } else {
      console.log('🔧 ENDPOINT ROOM CREATION: No new rooms and no existing labels, preventing unwanted room consolidation:', {
        totalRoomsDetected: finalRooms.length,
        originalRoomCount: creationContext?.originalRoomCount || 0,
        preventingMerge: true
      });
      return []; // Only return empty when there are truly no labels to preserve
    }
  }
  
  // Final debugging before return
  console.log('🔧 CALCULATE ROOMS FINAL:', {
    context,
    wallCount: walls.length,
    finalRoomsCount: finalRooms.length,
    roomsWithStableIds: finalRooms.filter(r => r.stableId).length,
    roomDetails: finalRooms.map(r => ({
      name: r.name,
      stableId: r.stableId?.substring(0, 50) + '...',
      area: r.area.toFixed(2)
    }))
  });

  return finalRooms;
};

// Helper to project a point onto a line segment
export const projectPointOntoLine = (point: Point, lineStart: Point, lineEnd: Point): Point => {
  const L2 = (lineEnd.x - lineStart.x) ** 2 + (lineEnd.y - lineStart.y) ** 2;
  if (L2 === 0) return { ...lineStart }; // Avoid division by zero if line has zero length

  const t = ((point.x - lineStart.x) * (lineEnd.x - lineStart.x) + (point.y - lineStart.y) * (lineEnd.y - lineStart.y)) / L2;

  const projectedX = lineStart.x + t * (lineEnd.x - lineStart.x);
  const projectedY = lineStart.y + t * (lineEnd.y - lineStart.y);

  return { x: projectedX, y: projectedY };
};

// Add this helper function, e.g., near other geometric helpers
export const orientation = (p: Point, q: Point, r: Point): number => {
  // Using a small tolerance for floating point comparisons
  const tolerance = 1e-7; // Small tolerance value
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);

  if (Math.abs(val) < tolerance) return 0; // Collinear (with tolerance)

  return (val > 0) ? 1 : 2; // Clockwise or Counterclockwise
};

// Helper function to determine the resize direction based on a wall and room bounds
export const getWallResizeDirection = (
  wall: Wall,
  roomBounds: { minX: number; maxX: number; minY: number; maxY: number },
  tolerance: number = 5
): string | null => {
  const { minX, maxX, minY, maxY } = roomBounds;

  // Check if this is an extrusion connector wall
  const isExtrusionConnector = (wall as any).isExtrusionConnector;
  
  if (isExtrusionConnector) {
    // For extrusion connectors, determine direction based on wall orientation
    // These walls connect the main room to the extruded segment
    const wallLengthX = Math.abs(wall.start.x - wall.end.x);
    const wallLengthY = Math.abs(wall.start.y - wall.end.y);
    const wallIsHorizontal = wallLengthX > wallLengthY;
    
    if (wallIsHorizontal) {
      // Horizontal connector should resize in horizontal direction (width)
      return wall.start.x < (minX + maxX) / 2 ? 'w' : 'e';
    } else {
      // Vertical connector should resize in vertical direction (width)  
      return wall.start.y < (minY + maxY) / 2 ? 'n' : 's';
    }
  }

  const isTopWall = Math.abs(wall.start.y - minY) < tolerance && Math.abs(wall.end.y - minY) < tolerance;
  const isBottomWall = Math.abs(wall.start.y - maxY) < tolerance && Math.abs(wall.end.y - maxY) < tolerance;
  const isLeftWall = Math.abs(wall.start.x - minX) < tolerance && Math.abs(wall.end.x - minX) < tolerance;
  const isRightWall = Math.abs(wall.start.x - maxX) < tolerance && Math.abs(wall.end.x - maxX) < tolerance;

  const wallLengthX = Math.abs(wall.start.x - wall.end.x);
  const wallLengthY = Math.abs(wall.start.y - wall.end.y);

  // Prioritize walls that are clearly aligned and longer along the expected axis of the bound
  if (isTopWall && wallLengthX >= wallLengthY * 0.5) return 'n'; // Top-most horizontal-ish wall
  if (isBottomWall && wallLengthX >= wallLengthY * 0.5) return 's'; // Bottom-most horizontal-ish wall
  if (isLeftWall && wallLengthY >= wallLengthX * 0.5) return 'w'; // Left-most vertical-ish wall
  if (isRightWall && wallLengthY >= wallLengthX * 0.5) return 'e'; // Right-most vertical-ish wall

  // Fallback for very short walls or perfectly diagonal corners forming part of a bound
  if (isTopWall) return 'n';
  if (isBottomWall) return 's';
  if (isLeftWall) return 'w';
  if (isRightWall) return 'e';

  return null;
};

export const calculateOverallWallLengthsAndArea = (
  currentWalls: Wall[],
  roomCustomLabels: Record<string, string>,
  context: RoomCalculationContext = 'geometryUpdate', // Add context, default for safety
  currentFloor?: number,
  creationContext?: {
    isFromEndpoint?: boolean;
    lastRoomCount?: number;
    expectedNewRooms?: number;
  }
): { hlpLength: number; pwLength: number; totalArea: number } => {
  console.log('🔧 AREA CALCULATION DEBUG: calculateOverallWallLengthsAndArea called', {
    context,
    currentFloor,
    creationContext,
    wallCount: currentWalls.length,
    roomCustomLabelsCount: Object.keys(roomCustomLabels).length
  });

  const hlpLen = calculateHLPLength(currentWalls); 
  const pwLen = calculatePWLength(currentWalls);

  const allPotentiallyCountableRooms = calculateRooms(currentWalls, roomCustomLabels, context, currentFloor, creationContext);
  
  console.log('🔧 AREA CALCULATION DEBUG: Rooms for area calculation', {
    roomCount: allPotentiallyCountableRooms.length,
    rooms: allPotentiallyCountableRooms.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
  });
  
  // Calculate total area directly from all rooms
  const totalAreaVal = allPotentiallyCountableRooms.reduce((sum: number, room: Room) => sum + room.area, 0);
  
  console.log('🔧 AREA CALCULATION DEBUG: Final area calculation', {
    totalArea: totalAreaVal.toFixed(2),
    individualAreas: allPotentiallyCountableRooms.map(r => r.area.toFixed(2))
  });
  
  return {
    hlpLength: hlpLen,
    pwLength: pwLen,
    totalArea: totalAreaVal
  };
};

export const snapToEndpointsOrGrid = (
  point: Point,
  walls: Wall[], // Expects Wall[], FloorPlanCreator.currentWalls is WallWithTempId[]
  gridSize: number,
  snapThreshold: number,
  isNearPointFn: (p1: Point, p2: Point, threshold: number) => boolean,
  snapToGridFn: (p: Point, gSize: number) => Point
): Point => {
  // First, try to snap to existing wall endpoints
  for (const wall of walls) {
    if (isNearPointFn(point, wall.start, snapThreshold)) return { ...wall.start };
    if (isNearPointFn(point, wall.end, snapThreshold)) return { ...wall.end };
  }
  // If not snapped to an endpoint, snap to the general grid
  return snapToGridFn(point, gridSize);
};

export const migrateRoomLabels = (
  oldWalls: Wall[],
  newWalls: Wall[],
  existingLabels: Record<string, string>,
  currentFloor?: number
): { labels: Record<string, string>; migrationMap: Record<string, string> } => {
  const newLabels: Record<string, string> = {};
  const migrationMap: Record<string, string> = {};
  
  // 🔧 ROOM RESIZE FIX: Filter labels to only process the current floor's rooms
  // This prevents rooms on other floors from losing their labels during resize operations
  let labelsToProcess = existingLabels;
  let labelsToPreserve: Record<string, string> = {};
  
  if (currentFloor !== undefined) {
    const currentFloorPrefix = `floor_${currentFloor}_`;
    
    // Split labels into current floor (to be processed) and other floors (to be preserved)
    labelsToProcess = {};
    labelsToPreserve = {};
    
    Object.entries(existingLabels).forEach(([id, label]) => {
      if (id.startsWith(currentFloorPrefix)) {
        labelsToProcess[id] = label;
      } else {
        labelsToPreserve[id] = label;
      }
    });
    
    console.log('🔧 FLOOR-SPECIFIC ROOM LABEL MIGRATION:', {
      currentFloor,
      totalLabels: Object.keys(existingLabels).length,
      currentFloorLabels: Object.keys(labelsToProcess).length,
      otherFloorLabels: Object.keys(labelsToPreserve).length,
      currentFloorPrefix
    });
  }
  
  // 🔧 EXTRUSION FIX: Enhanced room calculation with better label preservation
  // Use labelPreservation context for both old and new rooms to ensure consistency
  const oldRooms: Room[] = calculateRooms(oldWalls, labelsToProcess, 'labelPreservation', currentFloor);
  const newGeometricRooms: Room[] = calculateRooms(newWalls, labelsToProcess, 'labelPreservation', currentFloor);
  
  // 🔧 EXTRUSION FIX: If no rooms were calculated, preserve existing labels
  if (oldRooms.length === 0 && newGeometricRooms.length === 0 && Object.keys(labelsToProcess).length > 0) {
    console.log('🔧 EXTRUSION FIX: No rooms calculated in migration, preserving existing labels');
    return { labels: { ...labelsToPreserve, ...labelsToProcess }, migrationMap: {} };
  }
  
  // 🛡️ DELETION PROTECTION: Detect if this is a room deletion scenario
  const roomCountDecreased = newGeometricRooms.length < oldRooms.length;
  const deletedRoomCount = Math.max(0, oldRooms.length - newGeometricRooms.length);
  
  console.log('🛡️ ROOM DELETION DETECTION:', {
    oldRoomCount: oldRooms.length,
    newRoomCount: newGeometricRooms.length,
    roomCountDecreased,
    deletedRoomCount,
    context: roomCountDecreased ? 'DELETION_DETECTED' : 'NORMAL_MIGRATION'
  });

  oldRooms.forEach((oldRoom: Room) => {
    const currentOldStableId = oldRoom.stableId;
    if (currentOldStableId && labelsToProcess[currentOldStableId]) {
      const oldLabel = labelsToProcess[currentOldStableId];
      const oldCentroid = getRoomCenter(oldRoom.walls);
      let bestMatchNewRoom: Room | null = null;
      let minDistance = Infinity;

      const oldBounds = getRoomBounds(oldRoom.walls);
      // 🔧 LARGE RESIZE FIX: Much more lenient thresholds for large geometry changes
      // Use the room's largest dimension as base threshold, but allow for dramatic changes
      const roomDiagonal = Math.hypot(oldBounds.maxX - oldBounds.minX, oldBounds.maxY - oldBounds.minY);
      const baseCentroidThreshold = Math.max(oldBounds.maxX - oldBounds.minX, oldBounds.maxY - oldBounds.minY) * 1.5; // Increased from 0.75 to 1.5
      const calculatedCentroidDistanceThreshold = Math.max(baseCentroidThreshold, roomDiagonal, SNAP_THRESHOLD * 100); // Increased significantly

      newGeometricRooms.forEach((newRoom: Room) => {
        const newCentroid = getRoomCenter(newRoom.walls);
        const centroidDistance = Math.hypot(newCentroid.x - oldCentroid.x, newCentroid.y - oldCentroid.y);
        
        const centroidDistanceThreshold = calculatedCentroidDistanceThreshold;
        const areaDifferenceRatio = oldRoom.area > 0.01 ? Math.abs(newRoom.area - oldRoom.area) / oldRoom.area : (newRoom.area > 0.01 ? 1 : 0);
        
        // 🔧 LARGE RESIZE FIX: Extremely lenient area tolerance for large resize operations
        // Large resizes can dramatically change room area (10x or more), so allow up to 1000% change
        const areaSimilarityThreshold = 10.0; // Increased from 5.0 to 10.0 for large resizes

        // 🔧 LARGE RESIZE FIX: Very lenient wall count for complex resize operations
        // Complex resizes can add many segments and split walls
        const maxExpectedWallIncrease = Math.max(10, Math.ceil(oldRoom.walls.length * 2.0)); // Significantly increased
        const wallCountAcceptable = newRoom.walls.length >= oldRoom.walls.length &&
                                    newRoom.walls.length <= oldRoom.walls.length + maxExpectedWallIncrease;
        
        const meetsCentroid = centroidDistance < centroidDistanceThreshold;
        const meetsArea = areaDifferenceRatio < areaSimilarityThreshold;
        const meetsWallCount = wallCountAcceptable;

        // 🔧 ROOM RESIZE FIX: Initialize potential match flag
        let isPotentialMatch = false;

        // 🔧 ROOM RESIZE FIX: Special case for exact stable ID match (highest priority)
        if (newRoom.stableId === oldRoom.stableId) {
            isPotentialMatch = true;
            // For exact ID matches, this is the best possible match
            if (centroidDistance < minDistance) {
                minDistance = 0; // Set to 0 to make this the best match
                bestMatchNewRoom = newRoom;
            }
        }
        
        // 🔧 TOLERANT ROOM MERGING: Allow matching for room identity preservation during resize operations
        // This prevents custom label loss when stableIds change slightly due to coordinate updates
        console.log('🔍 ROOM IDENTITY CHECK: Evaluating room matching criteria', {
            oldRoomId: oldRoom.stableId,
            newRoomId: newRoom.stableId,
            exactMatch: newRoom.stableId === oldRoom.stableId,
            centroidDistance: centroidDistance.toFixed(1),
            areaDifference: (areaDifferenceRatio * 100).toFixed(1) + '%',
            meetsCentroid: meetsCentroid,
            meetsArea: meetsArea,
            meetsWallCount: meetsWallCount
        });
        
        // 🛡️ WALL OVERLAP CHECK: During deletion, require significant wall overlap to prevent cross-room label migration
        let hasWallOverlap = false;
        let wallOverlapCount = 0;
        
        if (roomCountDecreased) {
          // Check if old and new rooms share walls (by comparing coordinates within tolerance)
          const COORDINATE_TOLERANCE = 5; // 5 pixel tolerance for coordinate matching
          
          for (const oldWall of oldRoom.walls) {
            for (const newWall of newRoom.walls) {
              const startMatch = Math.abs(oldWall.start.x - newWall.start.x) < COORDINATE_TOLERANCE &&
                                Math.abs(oldWall.start.y - newWall.start.y) < COORDINATE_TOLERANCE;
              const endMatch = Math.abs(oldWall.end.x - newWall.end.x) < COORDINATE_TOLERANCE &&
                              Math.abs(oldWall.end.y - newWall.end.y) < COORDINATE_TOLERANCE;
              
              // Also check reversed wall (start matches end, end matches start)
              const reversedMatch = Math.abs(oldWall.start.x - newWall.end.x) < COORDINATE_TOLERANCE &&
                                   Math.abs(oldWall.start.y - newWall.end.y) < COORDINATE_TOLERANCE &&
                                   Math.abs(oldWall.end.x - newWall.start.x) < COORDINATE_TOLERANCE &&
                                   Math.abs(oldWall.end.y - newWall.start.y) < COORDINATE_TOLERANCE;
              
              if ((startMatch && endMatch) || reversedMatch) {
                wallOverlapCount++;
                hasWallOverlap = true;
                break;
              }
            }
          }
        }
        
        // 🎯 IDENTITY PRESERVATION: Allow matching for rooms that are clearly the same entity
        // During deletion scenarios, require wall overlap to prevent cross-room label migration
        let isIdentityPreservationMatch = false;
        
        if (roomCountDecreased) {
          // 🛡️ STRICT DELETION MODE: Require wall overlap AND close proximity
          const minWallOverlap = Math.max(2, Math.floor(oldRoom.walls.length * 0.4)); // At least 40% of walls must overlap
          isIdentityPreservationMatch = (
            hasWallOverlap &&
            wallOverlapCount >= minWallOverlap &&
            // Much stricter centroid distance during deletion
            centroidDistance < 20 &&
            // Similar area requirement
            areaDifferenceRatio < 0.5 &&
            // Wall count within reasonable bounds
            wallCountAcceptable
          );
          
          console.log('🛡️ DELETION MODE: Strict matching criteria', {
            oldRoomLabel: oldLabel,
            wallOverlapCount,
            minWallOverlap,
            hasWallOverlap,
            centroidDistance: centroidDistance.toFixed(1),
            centroidThreshold: 20,
            areaDifferenceRatio: (areaDifferenceRatio * 100).toFixed(1) + '%',
            isMatch: isIdentityPreservationMatch
          });
        } else {
          // 🔧 NORMAL MODE: Use existing lenient criteria for resize operations
          isIdentityPreservationMatch = (
            // Very close centroids (same room, different size)
            centroidDistance < 50 && 
            // Similar area (not drastically different)
            areaDifferenceRatio < 0.8 && 
            // Wall count within reasonable bounds (allows for extrusions/splits)
            wallCountAcceptable
          );
        }
        
        console.log('🏷️ IDENTITY PRESERVATION: Room matching decision', {
            exactMatch: newRoom.stableId === oldRoom.stableId,
            identityMatch: isIdentityPreservationMatch,
            mode: roomCountDecreased ? 'DELETION_MODE' : 'NORMAL_MODE',
            finalDecision: newRoom.stableId === oldRoom.stableId || isIdentityPreservationMatch
        });
        
        // Allow merging for exact matches OR identity preservation matches
        if (newRoom.stableId === oldRoom.stableId || isIdentityPreservationMatch) {
            isPotentialMatch = true;
        }
        
        if (isPotentialMatch) {
            if (centroidDistance < minDistance) {
                minDistance = centroidDistance;
                bestMatchNewRoom = newRoom;
            }
        }
      });

      if (bestMatchNewRoom) { 
        const newMatchStableId = (bestMatchNewRoom as Room).stableId; 
        if (newMatchStableId) { 
          migrationMap[currentOldStableId] = newMatchStableId;
          if (!newLabels[newMatchStableId]) {
            newLabels[newMatchStableId] = oldLabel;
          }
        } else {
          if (newGeometricRooms.some((nr: Room) => nr.stableId === currentOldStableId && !newLabels[currentOldStableId])) {
             newLabels[currentOldStableId] = oldLabel;
          }
        }
      } else {
        if (newGeometricRooms.some((nr: Room) => nr.stableId === currentOldStableId && !newLabels[currentOldStableId])) {
             newLabels[currentOldStableId] = oldLabel;
        }
      }
    }
  });

  // 🔧 LABEL PRESERVATION FIX: More aggressive preservation of existing labels
  // First, preserve labels that have exact stableId matches (common case)
  newGeometricRooms.forEach((newRoom: Room) => {
    if (newRoom.stableId && labelsToProcess[newRoom.stableId] && !newLabels[newRoom.stableId]) {
      newLabels[newRoom.stableId] = labelsToProcess[newRoom.stableId];
      console.log('🔧 EXACT ID MATCH: Preserved label for room', {
        roomId: newRoom.stableId,
        label: labelsToProcess[newRoom.stableId]
      });
    }
  });
  
  // 🔧 NEW ROOM CREATION FIX: When creating new rooms, existing rooms should keep their labels
  // If we have more old labels than were successfully migrated, preserve them using fallback logic
  // 🛡️ DELETION PROTECTION: Skip fallback for deletion scenarios to prevent deleted room labels migrating
  const successfullyMigratedCount = Object.keys(newLabels).length;
  const originalLabelsCount = Object.keys(labelsToProcess).length;
  
  if (originalLabelsCount > successfullyMigratedCount && !roomCountDecreased) {
    console.log('🔧 LABEL PRESERVATION: Some labels may have been lost, applying fallback preservation', {
      originalCount: originalLabelsCount,
      migratedCount: successfullyMigratedCount,
      newRoomsCount: newGeometricRooms.length
    });
    
    // Find old labels that weren't preserved
    const unpreservedLabels = Object.entries(labelsToProcess).filter(([oldId, label]) => 
      !newLabels[oldId] && !Object.values(migrationMap).includes(oldId)
    );
    
    // Find new rooms that don't have labels yet
    const unlabeledNewRooms = newGeometricRooms.filter(newRoom => 
      newRoom.stableId && !newLabels[newRoom.stableId]
    );
    
    // Apply smart fallback matching for unpreserved labels
    unpreservedLabels.forEach(([oldId, oldLabel], index) => {
      if (index < unlabeledNewRooms.length) {
        const targetRoom = unlabeledNewRooms[index];
        if (targetRoom.stableId) {
          newLabels[targetRoom.stableId] = oldLabel;
          migrationMap[oldId] = targetRoom.stableId;
          console.log('🔧 FALLBACK PRESERVATION: Preserved label using smart matching', {
            oldId: oldId.substring(0, 50) + '...',
            newId: targetRoom.stableId.substring(0, 50) + '...',
            label: oldLabel
          });
        }
      }
    });
  } else if (roomCountDecreased) {
    console.log('🛡️ DELETION PROTECTION: Skipping fallback preservation during room deletion to prevent label contamination', {
      originalCount: originalLabelsCount,
      migratedCount: successfullyMigratedCount,
      deletedRoomCount
    });
  }
  
  // 🔧 LARGE RESIZE EMERGENCY FALLBACK: When normal migration fails, use aggressive matching
  // This handles cases where room geometry changes so much that normal matching fails
  // 🛡️ DELETION PROTECTION: Don't apply emergency fallback during deletions
  const unmatchedOldLabels = Object.keys(labelsToProcess).filter(oldId => 
    !Object.values(migrationMap).includes(oldId) && 
    !newLabels[oldId] // Not already preserved
  );
  
  if (unmatchedOldLabels.length > 0 && newGeometricRooms.length > 0 && !roomCountDecreased) {
    console.log('🚨 EMERGENCY FALLBACK: Normal migration failed, trying aggressive matching', {
      unmatchedOldLabels,
      unmatchedLabels: unmatchedOldLabels.map(id => labelsToProcess[id]),
      newRoomsCount: newGeometricRooms.length,
      oldRoomsCount: oldRooms.length
    });
    
    // Emergency strategy: if we have same number of rooms, map them by index
    if (unmatchedOldLabels.length === 1 && newGeometricRooms.length === 1) {
      const oldId = unmatchedOldLabels[0];
      const oldLabel = labelsToProcess[oldId];
      const newRoom = newGeometricRooms[0];
      
      if (newRoom.stableId && !newLabels[newRoom.stableId]) {
        console.log('🚨 EMERGENCY FALLBACK: Single-to-single room mapping', {
          oldId,
          oldLabel,
          newId: newRoom.stableId,
          reason: 'single_room_scenario'
        });
        newLabels[newRoom.stableId] = oldLabel;
        migrationMap[oldId] = newRoom.stableId;
      }
    } else if (unmatchedOldLabels.length <= newGeometricRooms.length) {
      // Multiple rooms: try to match by remaining unmatched new rooms
      const unmatchedNewRooms = newGeometricRooms.filter(newRoom => 
        newRoom.stableId && !Object.values(migrationMap).includes(newRoom.stableId)
      );
      
      unmatchedOldLabels.forEach((oldId, index) => {
        if (index < unmatchedNewRooms.length) {
          const oldLabel = labelsToProcess[oldId];
          const newRoom = unmatchedNewRooms[index];
          
          if (newRoom.stableId && !newLabels[newRoom.stableId]) {
            console.log('🚨 EMERGENCY FALLBACK: Index-based room mapping', {
              oldId,
              oldLabel,
              newId: newRoom.stableId,
              index,
              reason: 'index_based_matching'
            });
            newLabels[newRoom.stableId] = oldLabel;
            migrationMap[oldId] = newRoom.stableId;
          }
        }
      });
    }
  }
  
  // 🔧 ROOM RESIZE FIX: Combine processed labels with preserved labels from other floors
  let finalLabels = { ...labelsToPreserve, ...newLabels };
  
  // 🛡️ DELETION VALIDATION: During deletion, verify all labels correspond to existing rooms
  if (roomCountDecreased && newGeometricRooms.length > 0) {
    const validRoomIds = new Set(newGeometricRooms.map(room => room.stableId).filter(Boolean));
    const cleanedLabels: Record<string, string> = {};
    
    Object.entries(finalLabels).forEach(([roomId, label]) => {
      // Keep labels from other floors (they start with floor_X_ where X != currentFloor)
      const isOtherFloor = currentFloor !== undefined && 
                          roomId.startsWith('floor_') && 
                          !roomId.startsWith(`floor_${currentFloor}_`);
      
      // Keep labels that correspond to rooms that still exist
      const roomStillExists = validRoomIds.has(roomId);
      
      if (isOtherFloor || roomStillExists) {
        cleanedLabels[roomId] = label;
      } else {
        console.log('🛡️ DELETION VALIDATION: Removing label for deleted room', {
          roomId: roomId.substring(0, 50) + '...',
          label,
          reason: 'room_no_longer_exists'
        });
      }
    });
    
    finalLabels = cleanedLabels;
  }
  
  // 🔧 ROOM RESIZE FIX: Enhanced debugging for label migration
  const labelsLost = Object.keys(labelsToProcess).filter(id => !newLabels[id]);
  const labelsAdded = Object.keys(newLabels).filter(id => !labelsToProcess[id]);
  
  // Always log migration debug info for resize operations to help troubleshoot
  console.log('🔧 ROOM LABEL MIGRATION DEBUG:', {
    currentFloor,
    oldRoomsCount: oldRooms.length,
    newRoomsCount: newGeometricRooms.length,
    labelsToProcessCount: Object.keys(labelsToProcess).length,
    newLabelsCount: Object.keys(newLabels).length,
    preservedLabelsCount: Object.keys(labelsToPreserve).length,
    finalLabelsCount: Object.keys(finalLabels).length,
    labelsLost: labelsLost.length > 0 ? labelsLost.map(id => ({ id: id.substring(0, 50) + '...', label: labelsToProcess[id] })) : 'none',
    labelsAdded: labelsAdded.length > 0 ? labelsAdded.map(id => ({ id: id.substring(0, 50) + '...', label: newLabels[id] })) : 'none',
    migrationMapEntries: Object.keys(migrationMap).length,
    migrationDetails: Object.entries(migrationMap).map(([old, new_]) => ({ 
      old: old.substring(0, 50) + '...', 
      new: new_.substring(0, 50) + '...',
      oldLabel: labelsToProcess[old],
      newLabel: newLabels[new_]
    }))
  });
  
  return { labels: finalLabels, migrationMap };
};

/**
 * Migrates old room labels (without floor prefix) to new floor-specific format
 * This is needed when loading old saved files that don't have floor-prefixed stable IDs
 */
export const migrateOldRoomLabelsToFloorFormat = (
  oldRoomLabels: Record<string, string>,
  floorPlans: Record<number, Wall[]>,
  currentFloor: number
): Record<string, string> => {
  const migratedLabels: Record<string, string> = {};
  
  // Check if labels are already in new format (have floor_ prefix)
  const hasFloorPrefixes = Object.keys(oldRoomLabels).some(id => id.startsWith('floor_'));
  if (hasFloorPrefixes) {
    // Labels are already migrated, return as-is
    return oldRoomLabels;
  }
  
  // Migrate labels for each floor
  Object.entries(floorPlans).forEach(([floorNumStr, walls]) => {
    const floorNum = parseInt(floorNumStr);
    if (walls && walls.length > 0) {
      // 🔧 FIX: Pass oldRoomLabels to calculateRooms to maintain consistent room IDs
      // This prevents labels from being lost when the file is reloaded
      const roomsWithOldIds = calculateRooms(walls, oldRoomLabels, 'explicitRoomCreation'); // Don't pass floor to get old IDs
      const roomsWithNewIds = calculateRooms(walls, oldRoomLabels, 'explicitRoomCreation', floorNum); // Pass floor to get new IDs
      
      // Create mapping between old and new stable IDs
      roomsWithOldIds.forEach((oldRoom, index) => {
        const newRoom = roomsWithNewIds[index];
        if (oldRoom.stableId && newRoom.stableId && oldRoomLabels[oldRoom.stableId]) {
          migratedLabels[newRoom.stableId] = oldRoomLabels[oldRoom.stableId];
        }
      });
    }
  });
  
  // Preserve any labels that couldn't be migrated (edge case)
  Object.entries(oldRoomLabels).forEach(([oldId, label]) => {
    const alreadyMigrated = Object.values(migratedLabels).includes(label);
    if (!alreadyMigrated && !oldId.startsWith('floor_')) {
      // Try to assign to current floor as fallback
      const fallbackId = `floor_${currentFloor}_${oldId}`;
      if (!migratedLabels[fallbackId]) {
        migratedLabels[fallbackId] = label;
      }
    }
  });
  
  console.log('🔄 Room label migration:', {
    oldLabelsCount: Object.keys(oldRoomLabels).length,
    migratedLabelsCount: Object.keys(migratedLabels).length,
    oldLabels: Object.keys(oldRoomLabels),
    migratedLabels: Object.keys(migratedLabels)
  });
  
  return migratedLabels;
};

export const generateStableWallId = (p1: Point, p2: Point): string => { // Ensure this function exists and is exported
  const p1Str = `${p1.x.toFixed(2)},${p1.y.toFixed(2)}`;
  const p2Str = `${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  // Sort by coordinates to ensure consistent ID regardless of point order
  if (p1Str < p2Str) {
    return `${p1Str};${p2Str}`;
  }
  return `${p2Str};${p1Str}`;
};

// Add this function to handle extrusion width adjustments
export const adjustExtrusionWidth = (
  walls: Wall[],
  mainSegmentId: string,
  draggedConnectorId: string,
  targetPoint: Point
): Wall[] | null => {
  console.log('🔧 ADJUST_EXTRUSION_WIDTH_START', {
    totalWalls: walls.length,
    mainSegmentId,
    draggedConnectorId,
    targetPoint,
    extrusionWalls: walls.filter(w => (w as any).isExtrusionConnector || (w as any).isMainExtrusionSegment).map(w => ({
      tempId: (w as any).tempId,
      isMainExtrusionSegment: (w as any).isMainExtrusionSegment,
      isExtrusionConnector: (w as any).isExtrusionConnector,
      start: w.start,
      end: w.end
    }))
  });

  // Find the main segment
  const mainSegment = walls.find(w => (w as any).tempId === mainSegmentId);
  if (!mainSegment) {
    console.log('🔧 ADJUST_EXTRUSION_WIDTH_ERROR: Main segment not found', {
      searchedId: mainSegmentId,
      availableWalls: walls.map(w => ({ tempId: (w as any).tempId, isMainExtrusionSegment: (w as any).isMainExtrusionSegment }))
    });
    return null;
  }

  // Find all connector walls for this extrusion
  const connectorWalls = walls.filter(w => (w as any).isExtrusionConnector);
  const draggedConnector = connectorWalls.find(w => (w as any).tempId === draggedConnectorId);
  if (!draggedConnector) {
    console.log('🔧 ADJUST_EXTRUSION_WIDTH_ERROR: Dragged connector not found', {
      searchedId: draggedConnectorId,
      connectorWalls: connectorWalls.map(w => ({ tempId: (w as any).tempId, start: w.start, end: w.end }))
    });
    return null;
  }

  // Find which connector is being dragged and which is its partner
  let partnerConnector: Wall | null = null;
  for (const connector of connectorWalls) {
    if ((connector as any).tempId !== draggedConnectorId) {
      // Check if this connector shares an endpoint with the main segment
      const sharesWithMain = 
        pointsAreEqual(connector.start, mainSegment.start) ||
        pointsAreEqual(connector.start, mainSegment.end) ||
        pointsAreEqual(connector.end, mainSegment.start) ||
        pointsAreEqual(connector.end, mainSegment.end);
      
      console.log('🔧 ADJUST_EXTRUSION_WIDTH_CHECKING_PARTNER', {
        connectorId: (connector as any).tempId,
        connectorStart: connector.start,
        connectorEnd: connector.end,
        mainSegmentStart: mainSegment.start,
        mainSegmentEnd: mainSegment.end,
        sharesWithMain
      });
      
      if (sharesWithMain) {
        partnerConnector = connector;
        break;
      }
    }
  }

  if (!partnerConnector) {
    console.log('🔧 ADJUST_EXTRUSION_WIDTH_ERROR: Partner connector not found', {
      draggedConnectorId,
      mainSegment: { tempId: (mainSegment as any).tempId, start: mainSegment.start, end: mainSegment.end },
      connectorWalls: connectorWalls.map(w => ({ 
        tempId: (w as any).tempId, 
        start: w.start, 
        end: w.end,
        sharePointsWithMain: [
          pointsAreEqual(w.start, mainSegment.start),
          pointsAreEqual(w.start, mainSegment.end),
          pointsAreEqual(w.end, mainSegment.start),
          pointsAreEqual(w.end, mainSegment.end)
        ]
      }))
    });
    return null;
  }

  // Determine which end of the main segment each connector connects to
  const draggedConnectsToMainStart = 
    pointsAreEqual(draggedConnector.start, mainSegment.start) ||
    pointsAreEqual(draggedConnector.end, mainSegment.start);
  
  const draggedConnectsToMainEnd = 
    pointsAreEqual(draggedConnector.start, mainSegment.end) ||
    pointsAreEqual(draggedConnector.end, mainSegment.end);

  // Calculate the perpendicular direction from the main segment
  const mainVector = {
    x: mainSegment.end.x - mainSegment.start.x,
    y: mainSegment.end.y - mainSegment.start.y
  };
  
  const mainLength = Math.sqrt(mainVector.x * mainVector.x + mainVector.y * mainVector.y);
  if (mainLength === 0) return null;

  // Normalize the main vector
  const mainDir = {
    x: mainVector.x / mainLength,
    y: mainVector.y / mainLength
  };

  // Get perpendicular direction (rotate 90 degrees)
  const perpDir = {
    x: -mainDir.y,
    y: mainDir.x
  };

  // Project the target point onto the perpendicular line from the main segment
  let projectionBase: Point;
  if (draggedConnectsToMainStart) {
    projectionBase = mainSegment.start;
  } else if (draggedConnectsToMainEnd) {
    projectionBase = mainSegment.end;
  } else {
    // This shouldn't happen, but fallback to start
    projectionBase = mainSegment.start;
  }

  // Calculate the new extrusion depth
  const toTarget = {
    x: targetPoint.x - projectionBase.x,
    y: targetPoint.y - projectionBase.y
  };

  // Project onto perpendicular direction to get the new depth
  const newDepth = toTarget.x * perpDir.x + toTarget.y * perpDir.y;
  
  // Calculate new positions for the extrusion segment endpoints
  const newExtrusionStart = {
    x: mainSegment.start.x + newDepth * perpDir.x,
    y: mainSegment.start.y + newDepth * perpDir.y
  };
  
  const newExtrusionEnd = {
    x: mainSegment.end.x + newDepth * perpDir.x,
    y: mainSegment.end.y + newDepth * perpDir.y
  };

  // 🔗 PHASE 1 FIX: PRESERVE EXTRUSION METADATA DURING RESIZE
  console.log('🔗 PHASE1_METADATA_PRESERVATION: Starting extrusion resize with metadata preservation');
  
  // Store original relationships for validation
  const originalWalls = [...walls];
  
  // Create the updated walls array with metadata preservation
  const updatedWalls = walls.map(wall => {
    const tempId = (wall as any).tempId;

    if (tempId === mainSegmentId) {
      // Update the main segment to the new extruded position with metadata preservation
      return updateWallWithExtrusionMetadata(wall, {
        start: newExtrusionStart,
        end: newExtrusionEnd
      });
    } else if (tempId === draggedConnectorId) {
      // Update the dragged connector with metadata preservation
      const newConnectorWall = draggedConnectsToMainStart ? {
        start: mainSegment.start,
        end: newExtrusionStart
      } : {
        start: mainSegment.end,
        end: newExtrusionEnd
      };
      
      return updateWallWithExtrusionMetadata(wall, newConnectorWall);
    } else if (partnerConnector && tempId === (partnerConnector as any).tempId) {
      // Update the partner connector with metadata preservation
      const partnerConnectsToMainStart = 
        pointsAreEqual(partnerConnector.start, mainSegment.start) ||
        pointsAreEqual(partnerConnector.end, mainSegment.start);
      
      const newPartnerWall = partnerConnectsToMainStart ? {
        start: mainSegment.start,
        end: newExtrusionStart
      } : {
        start: mainSegment.end,
        end: newExtrusionEnd
      };
      
      return updateWallWithExtrusionMetadata(wall, newPartnerWall);
    }

    return wall;
  });

  console.log('🔧 ADJUST_EXTRUSION_WIDTH_SUCCESS', {
    originalWallCount: walls.length,
    updatedWallCount: updatedWalls.length,
    wallCountMatches: walls.length === updatedWalls.length,
    mainSegmentUpdated: updatedWalls.find(w => (w as any).tempId === mainSegmentId) !== undefined,
    draggedConnectorUpdated: updatedWalls.find(w => (w as any).tempId === draggedConnectorId) !== undefined,
    partnerConnectorUpdated: partnerConnector ? updatedWalls.find(w => (w as any).tempId === (partnerConnector as any).tempId) !== undefined : false,
    partnerConnectorFound: partnerConnector !== null,
    partnerConnectorId: partnerConnector ? (partnerConnector as any).tempId : null,
    wallCounts: {
      original: walls.length,
      updated: updatedWalls.length,
      mainSegment: updatedWalls.filter(w => (w as any).isMainExtrusionSegment).length,
      connectors: updatedWalls.filter(w => (w as any).isExtrusionConnector).length,
      regular: updatedWalls.filter(w => !(w as any).isMainExtrusionSegment && !(w as any).isExtrusionConnector).length
    },
    updatedWallIds: updatedWalls.map(w => (w as any).tempId),
    originalWallIds: walls.map(w => (w as any).tempId)
  });

  // 🚨 CRITICAL: Safety check - ensure wall count integrity
  if (updatedWalls.length !== walls.length) {
    console.error('🔧 ADJUST_EXTRUSION_WIDTH_WALL_COUNT_MISMATCH', {
      message: 'Wall count mismatch in adjustExtrusionWidth - returning original walls to prevent room breakage',
      originalCount: walls.length,
      updatedCount: updatedWalls.length,
      missingWalls: walls.length - updatedWalls.length
    });
    // Return original walls to maintain wall count integrity
    return walls;
  }

  // 🔗 PHASE 1 FIX: VALIDATE EXTRUSION INTEGRITY AFTER RESIZE
  const integrityValidation = validateExtrusionIntegrity(originalWalls, updatedWalls);
  
  if (!integrityValidation.isValid) {
    console.warn('🔗 PHASE1_VALIDATION_FAILED: Extrusion integrity compromised, attempting repair', {
      issues: integrityValidation.issues,
      brokenRelationships: integrityValidation.brokenRelationships
    });
    
    // Attempt to repair broken relationships
    const repairedWalls = repairExtrusionRelationships(updatedWalls, integrityValidation.brokenRelationships);
    
    // Validate the repair
    const repairValidation = validateExtrusionIntegrity(originalWalls, repairedWalls);
    
    if (repairValidation.isValid) {
      console.log('✅ PHASE1_REPAIR_SUCCESS: Extrusion relationships restored');
      return repairedWalls;
    } else {
      console.error('🚨 PHASE1_REPAIR_FAILED: Could not restore extrusion integrity, returning original walls', {
        remainingIssues: repairValidation.issues
      });
      return walls; // Fallback to original to prevent corruption
    }
  } else {
    console.log('✅ PHASE1_VALIDATION_SUCCESS: Extrusion integrity preserved during resize');
  }

  return updatedWalls;
};

// Enhanced snapping function specifically for room creation
export const snapRoomPointToNearbyStructures = (
  point: Point,
  walls: Wall[],
  existingRooms: Room[],
  gridSize: number,
  snapThreshold: number,
  scale: number = 1
): { snappedPoint: Point; snapType: 'endpoint' | 'wall-edge' | 'wall-extension' | 'room-corner' | 'grid' | 'none' } => {
  const adjustedSnapThreshold = snapThreshold * 1.5; // Slightly more generous snapping for room creation (4.5px = 4.5cm)
  const wallExtensionThreshold = snapThreshold * 2; // More generous for wall extensions (6px = 6cm)

  // 1. First priority: Snap to existing wall endpoints (corners)
  for (const wall of walls) {
    if (isNearPoint(point, wall.start, adjustedSnapThreshold)) {
      return { snappedPoint: { ...wall.start }, snapType: 'endpoint' };
    }
    if (isNearPoint(point, wall.end, adjustedSnapThreshold)) {
      return { snappedPoint: { ...wall.end }, snapType: 'endpoint' };
    }
  }

  // 2. Second priority: Snap to wall edges (perpendicular to walls)
  for (const wall of walls) {
    // Check if point is close to the wall line
    const distanceToWall = pointToLineDistance(point, wall.start, wall.end);
    if (distanceToWall <= adjustedSnapThreshold) {
      // Find the closest point on the wall
      const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
      const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
      
      if (wallLength > 0) {
        const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
        const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength ** 2);
        
        // Only snap if the projection is within the wall segment (not extended)
        if (projection >= 0 && projection <= 1) {
          const snappedPoint = {
            x: wall.start.x + projection * wallVector.x,
            y: wall.start.y + projection * wallVector.y
          };
          return { snappedPoint, snapType: 'wall-edge' };
        }
      }
    }
  }

  // 3. Third priority: Snap to wall extensions (extending existing walls)
  for (const wall of walls) {
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
    
    if (wallLength > 0) {
      const wallDir = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
      
      // Check extension from start point
      const toPointFromStart = { x: point.x - wall.start.x, y: point.y - wall.start.y };
      const projectionFromStart = toPointFromStart.x * wallDir.x + toPointFromStart.y * wallDir.y;
      
      if (projectionFromStart < 0) { // Point is behind the start
        const extensionPoint = {
          x: wall.start.x + projectionFromStart * wallDir.x,
          y: wall.start.y + projectionFromStart * wallDir.y
        };
        const distanceToExtension = Math.hypot(point.x - extensionPoint.x, point.y - extensionPoint.y);
        
        if (distanceToExtension <= wallExtensionThreshold) {
          return { snappedPoint: extensionPoint, snapType: 'wall-extension' };
        }
      }
      
      // Check extension from end point
      const toPointFromEnd = { x: point.x - wall.end.x, y: point.y - wall.end.y };
      const projectionFromEnd = toPointFromEnd.x * wallDir.x + toPointFromEnd.y * wallDir.y;
      
      if (projectionFromEnd > 0) { // Point is beyond the end
        const extensionPoint = {
          x: wall.end.x + projectionFromEnd * wallDir.x,
          y: wall.end.y + projectionFromEnd * wallDir.y
        };
        const distanceToExtension = Math.hypot(point.x - extensionPoint.x, point.y - extensionPoint.y);
        
        if (distanceToExtension <= wallExtensionThreshold) {
          return { snappedPoint: extensionPoint, snapType: 'wall-extension' };
        }
      }
    }
  }

  // 4. Fourth priority: Snap to room corners (calculated from room bounds)
  for (const room of existingRooms) {
    const bounds = getRoomBounds(room.walls);
    const corners = [
      { x: bounds.minX, y: bounds.minY }, // Top-left
      { x: bounds.maxX, y: bounds.minY }, // Top-right
      { x: bounds.maxX, y: bounds.maxY }, // Bottom-right
      { x: bounds.minX, y: bounds.maxY }  // Bottom-left
    ];
    
    for (const corner of corners) {
      if (isNearPoint(point, corner, adjustedSnapThreshold)) {
        return { snappedPoint: { ...corner }, snapType: 'room-corner' };
      }
    }
  }

  // 5. Final fallback: Snap to grid
  const gridSnappedPoint = snapPointToGrid(point, gridSize);
  return { snappedPoint: gridSnappedPoint, snapType: 'grid' };
};

// Enhanced room creation snapping that considers both start and end points
export const snapRoomForAdjacentCreation = (
  roomStart: Point,
  roomEnd: Point,
  walls: Wall[],
  existingRooms: Room[],
  gridSize: number,
  snapThreshold: number,
  scale: number = 1
): { snappedStart: Point; snappedEnd: Point; isAdjacentToExisting: boolean } => {
  // Snap both start and end points
  const startSnap = snapRoomPointToNearbyStructures(roomStart, walls, existingRooms, gridSize, snapThreshold, scale);
  const endSnap = snapRoomPointToNearbyStructures(roomEnd, walls, existingRooms, gridSize, snapThreshold, scale);
  
  // Determine if this room will be adjacent to existing structures
  const isAdjacentToExisting = 
    startSnap.snapType !== 'grid' && startSnap.snapType !== 'none' ||
    endSnap.snapType !== 'grid' && endSnap.snapType !== 'none';
  
  // If both points snap to meaningful structures, ensure they create a valid rectangle
  if (isAdjacentToExisting) {
    // Make sure we maintain rectangular shape while respecting snapping
    const snappedStart = startSnap.snappedPoint;
    const snappedEnd = endSnap.snappedPoint;
    
    // If one dimension is very small, expand it to maintain minimum room size
    const minRoomSize = gridSize * 2; // Minimum 2 grid units
    let finalStart = { ...snappedStart };
    let finalEnd = { ...snappedEnd };
    
    if (Math.abs(finalEnd.x - finalStart.x) < minRoomSize) {
      const midX = (finalStart.x + finalEnd.x) / 2;
      finalStart.x = midX - minRoomSize / 2;
      finalEnd.x = midX + minRoomSize / 2;
    }
    
    if (Math.abs(finalEnd.y - finalStart.y) < minRoomSize) {
      const midY = (finalStart.y + finalEnd.y) / 2;
      finalStart.y = midY - minRoomSize / 2;
      finalEnd.y = midY + minRoomSize / 2;
    }
    
    return {
      snappedStart: finalStart,
      snappedEnd: finalEnd,
      isAdjacentToExisting: true
    };
  }
  
  return {
    snappedStart: startSnap.snappedPoint,
    snappedEnd: endSnap.snappedPoint,
    isAdjacentToExisting: false
  };
};

export const getDoorResizeHandles = (door: DoorElement): { [key: string]: Point } => {
  const angle = door.rotation * (Math.PI / 180);
  const halfWidth = door.width / 2;

  const dx = Math.cos(angle) * halfWidth;
  const dy = Math.sin(angle) * halfWidth;

  const handle1 = {
    x: door.position.x + dx,
    y: door.position.y + dy,
  };

  const handle2 = {
    x: door.position.x - dx,
    y: door.position.y - dy,
  };

  return { left: handle1, right: handle2 };
};

// 🎯 NEW: Professional CAD-style wall splitting detection
export interface WallClickAnalysis {
  isOnWall: boolean;
  pointOnWall: Point | null;
  clickType: 'endpoint' | 'middle' | 'none';
  shouldAutoSplit: boolean;
  splitPoint: Point | null;
  endpointIndex: number | null; // 0 for start, 1 for end, null for middle
  distanceFromNearestEndpoint: number;
}

/**
 * Analyzes a click on a wall to determine if it should trigger auto-splitting
 * Professional CAD behavior: split when clicking on wall middle, resize when clicking endpoints
 */
export const analyzeWallClick = (
  clickPoint: Point, 
  wall: Wall, 
  tolerance: number = 5,
  endpointTolerance: number = 15 // Larger tolerance for endpoints
): WallClickAnalysis => {
  const pointOnWall = isPointOnWall(clickPoint, wall, tolerance);
  
  if (!pointOnWall) {
    return {
      isOnWall: false,
      pointOnWall: null,
      clickType: 'none',
      shouldAutoSplit: false,
      splitPoint: null,
      endpointIndex: null,
      distanceFromNearestEndpoint: Infinity
    };
  }

  // Calculate distances to wall endpoints
  const distToStart = Math.hypot(pointOnWall.x - wall.start.x, pointOnWall.y - wall.start.y);
  const distToEnd = Math.hypot(pointOnWall.x - wall.end.x, pointOnWall.y - wall.end.y);
  const minEndpointDistance = Math.min(distToStart, distToEnd);

  // Determine click type
  let clickType: 'endpoint' | 'middle' | 'none' = 'none';
  let endpointIndex: number | null = null;
  let shouldAutoSplit = false;
  let splitPoint: Point | null = null;

  if (distToStart <= endpointTolerance) {
    clickType = 'endpoint';
    endpointIndex = 0;
    shouldAutoSplit = false;
  } else if (distToEnd <= endpointTolerance) {
    clickType = 'endpoint';
    endpointIndex = 1;
    shouldAutoSplit = false;
  } else {
    clickType = 'middle';
    endpointIndex = null;
    shouldAutoSplit = true;
    splitPoint = { ...pointOnWall };
  }

  return {
    isOnWall: true,
    pointOnWall,
    clickType,
    shouldAutoSplit,
    splitPoint,
    endpointIndex,
    distanceFromNearestEndpoint: minEndpointDistance
  };
};

/**
 * Enhanced wall detection for professional CAD workflow
 * Returns both the wall and analysis of where the click occurred
 */
export const findWallForProfessionalCAD = (
  clickPoint: Point, 
  walls: Wall[], 
  tolerance: number = 5,
  endpointTolerance: number = 15
): { wall: Wall | null; analysis: WallClickAnalysis | null } => {
  let bestWall: Wall | null = null;
  let bestAnalysis: WallClickAnalysis | null = null;
  let bestDistance = Infinity;

  for (const wall of walls) {
    const analysis = analyzeWallClick(clickPoint, wall, tolerance, endpointTolerance);
    
    if (analysis.isOnWall) {
      const distance = pointToLineDistance(clickPoint, wall.start, wall.end);
      
      if (distance < bestDistance) {
        bestDistance = distance;
        bestWall = wall;
        bestAnalysis = analysis;
      }
    }
  }

  return { wall: bestWall, analysis: bestAnalysis };
};

// 🔧 NEW: Specialized room calculation for endpoint creation
// This prevents multiple room detection when connecting to existing rooms at multiple points
const calculateRoomsForEndpointCreation = (
  walls: Wall[], 
  roomCustomLabels: Record<string, string> = {},
  context: RoomCalculationContext = 'explicitRoomCreation',
  currentFloor?: number,
  creationContext?: {
    isFromEndpoint?: boolean;
    lastRoomCount?: number;
    expectedNewRooms?: number;
    originalRoomCount?: number;
    newWalls?: Wall[]; // Track which walls are newly added
  }
): Room[] => {
  console.log('🔧 ENDPOINT ROOM CREATION: Starting specialized room calculation', {
    totalWalls: walls.length,
    originalRoomCount: creationContext?.originalRoomCount,
    hasNewWalls: !!creationContext?.newWalls,
    newWallsCount: creationContext?.newWalls?.length || 0
  });

  if (!walls || walls.length === 0) {
    return [];
  }

  // If we don't have information about new walls, fall back to regular calculation
  if (!creationContext?.newWalls || creationContext.newWalls.length === 0) {
    console.log('🔧 ENDPOINT ROOM CREATION: No new walls tracked, falling back to regular calculation');
    return calculateRoomsInternal(walls, roomCustomLabels, context, currentFloor, creationContext);
  }

  const newWalls = creationContext.newWalls;
  const existingWalls = walls.filter(wall => !newWalls.some(nw => 
    Math.abs(nw.start.x - wall.start.x) < 0.1 && 
    Math.abs(nw.start.y - wall.start.y) < 0.1 &&
    Math.abs(nw.end.x - wall.end.x) < 0.1 && 
    Math.abs(nw.end.y - wall.end.y) < 0.1
  ));

  console.log('🔧 ENDPOINT ROOM CREATION: Wall classification', {
    totalWalls: walls.length,
    newWalls: newWalls.length,
    existingWalls: existingWalls.length
  });

  // Use cycle detection but filter for cycles that are primarily new walls
  const allRooms = calculateRoomsInternal(walls, roomCustomLabels, context, currentFloor, creationContext);
  
  // Filter rooms to only include those that are primarily formed by new walls
  const newlyCreatedRooms = allRooms.filter(room => {
    const roomWalls = room.walls;
    const newWallCount = roomWalls.filter(roomWall => 
      newWalls.some(nw => 
        Math.abs(nw.start.x - roomWall.start.x) < 0.1 && 
        Math.abs(nw.start.y - roomWall.start.y) < 0.1 &&
        Math.abs(nw.end.x - roomWall.end.x) < 0.1 && 
        Math.abs(nw.end.y - roomWall.end.y) < 0.1
      )
    ).length;
    
    const newWallRatio = newWallCount / roomWalls.length;
    
    // Only include rooms where new walls make up at least 50% of the room
    // This ensures we only get the newly created room, not existing rooms
    const isNewRoom = newWallRatio >= 0.5;
    
    console.log('🔧 ENDPOINT ROOM CREATION: Room evaluation', {
      roomName: room.name,
      totalWallsInRoom: roomWalls.length,
      newWallsInRoom: newWallCount,
      newWallRatio: (newWallRatio * 100).toFixed(1) + '%',
      isNewRoom,
      roomArea: room.area.toFixed(2)
    });
    
    return isNewRoom;
  });

  console.log('🔧 ENDPOINT ROOM CREATION: Final filtering result', {
    allRoomsFound: allRooms.length,
    newlyCreatedRooms: newlyCreatedRooms.length,
    roomsFiltered: allRooms.length - newlyCreatedRooms.length,
    newRooms: newlyCreatedRooms.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
  });

  return newlyCreatedRooms;
};


